import React, { useEffect, useId, useRef, useState, RefObject, useCallback, useLayoutEffect, useMemo } from 'react';
import { AlertCircle, ChevronDown, Info, RotateCcw, X, ArrowUpDown, ChevronRight, ChevronUp, Loader2, ArrowDown, ArrowUp, Check, Pin, Search, SlidersHorizontal, Calendar, Calendar1, ChevronLeft, Filter, Layers, Bookmark, Download, HelpCircle, Link2, Star, LayoutTemplate, Lock, Users, Settings2 } from 'lucide-react';
import { Capabilities, AllocBasis, AnyRow, DataFlag, Grain, isVendorRow, ShipHint, Status, OtbPeriodLine, BenchmarkEntity, BenchmarkMetricId, BenchmarkPayload, BenchmarkPeer, PriceBasis, Compare, Multiplier, OtbOnOrder, ProductSearchField, BenchmarkLevel, ClassReportPayload, ClassRow, type ReportKey, Tier, VendorRowsPayload } from '../../../interfaces/reports/WeeklyPlanning.interface';
import { cn } from '../../../lib/utils';
import { clampReportColumnWidth, REPORT_COLUMN_RESIZE_STEP, REPORT_MAX_COLUMN_WIDTH, REPORT_MIN_COLUMN_WIDTH, ActionCode, CellContext, CellValue, CHECK_DATA_LABEL, checkDataTooltip, classTypeTooltip, columnLabel, columnShortLabel, comparableSales, compareValue, DEADSTOCK_LABEL, DEADSTOCK_TOOLTIP, defersToParent, EMPTY, fixMojibake, fmtCurrency, isRowUrgent, isTrendCell, metricById, MetricDefinition, NEW_LABEL, NO_WEEKLY_PLAN_LABEL, prorate, prorationTitle, resolveTarget, reviewTooltip, rowAction, salesCompareOnBasis, SEE_PARENT_LABEL, STATUS_COLORS, summaryOnBasis, v2HistoryNote, otbPeriodSlot, FlatRow, placeholderLoads, PRODUCT_LOAD_FAILED, type RowTreeSort, type SortDirection, fmtPct, NOT_AT_COST_TOOLTIP, periodFullName, periodNoun, RUNNING_OTB_RULE, STATUS_LABELS, BENCHMARK_METRICS, BENCHMARK_TONE_CSS, benchmarkIndex, benchmarkMonthEnd, benchmarkMonthNote, fmtWeeks, fmtWeeksExact, formatIndex, gatedPeerValue, indexTone, PEER_TEXT_NOT_ENOUGH, RETAIL_BASIS_NOTE, signedPct, WEEKS_CAP, AttentionItem, familyDrivers, trimOtbLines, benchmarkLabel, BenchmarkRowStatus, BenchmarkRowValue, BenchmarkView, guardNoSales, metricLabelIn, ownIn, peerIn, SalesAhead, salesAheadForRows, storeNamesOf, totalRows, ACTION_PRIORITY_SORT, HIERARCHY_BY_KEY, HIERARCHY_OPTIONS, hierarchyOptionsFor, HierarchyView, isProductView, needsVendorRows, ProductGrain, columnsFor, DEADSTOCK_FILTER, formatPeriodLabel, formatWeekLabel, MULTIPLIERS, otbStartLabel, OVER_TARGET_SORT, TREND_METHOD_LABELS, TREND_METHODS, TrendMethod, unitize, FRESHNESS_IDS, LOOKBACK_OPTIONS, LOOKBACK_RANGE, OTB_SPAN_MONTH_OPTIONS, OTB_SPAN_RANGE, OTB_SPAN_UNIT, OTB_SPAN_WEEK_OPTIONS, otbStartFor, otbStartOptions, type ProductSort, ReportViewState, resetColumns, setFreshnessShown, setShowPctOfTotal, sortForProductRank, ViewFilters, benchmarkMonth, daysToCome, epochWeekStart, fmtDate, METRIC_DEFINITIONS, ON_ORDER_TIMING_CAVEAT, otbPeriodLabel, periodContainsSalesDay, TREND_METHODS_HELP, usesPlanWeights, withBenchmarkBasis, withBenchmarkMonth, withContextLabels, withGrainUnits, chainApplies, chainClassCount, chainCodeOf, chainVendorClassCount, isLocationAllocationView, totalsNoun, totalsViewOf, toSavedViewPayload, VIEW_TEMPLATES, SavedViewPayload, exportReportCsv, capabilityAllows, type CapabilityRequirement, allocatedOtbOf, allocatedOtbBadge, allocatedOtbTooltip, clientColumnIds, defaultViewState, reorderExplanation, fmtUnits, bomAbbr, bomInventoryLabel } from './logic';
import { ColumnHeaderMenu } from '../ColumnHeaderMenu.component';
import { COLUMN_RESIZE_HANDLE_CLASS } from '../../../hooks/useResizableColumns';
import { pinColumn, unpinColumn } from '../../../hooks/columnPinning';
import { normalizeColumnOrder, useDraggableColumnOrder } from '../../../hooks/useDraggableColumnOrder';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { type WeeklyPlanningReportModel, useWeeklyPlanningReport } from './useWeeklyPlanningReport';
import { FilterSelection, usePendingFilterSelection } from '../../../hooks/usePendingFilterSelection';
import { createPortal } from 'react-dom';
import AuthProvider from '../../../providers/AuthProvider';
import { SAVED_VIEW_NAME_MAX_LENGTH, SavedViewRecord, SavedViewVisibility } from '../../../services/reports/SavedViews.service';
import { useSavedViews } from '../../../hooks/useSavedViews';

// ===================================================================================================================
// ==== primitives.tsx ================================================================================================
// ===================================================================================================================
// The kit's building blocks, in the Vendor Performance / Top Sellers visual style: popover, tooltip, segmented control,
// switch, pill, the table's loading / empty / error states and the capability gate. `shadow-2xs` from VP is a Tailwind v4
// utility the v3 config does not define, so `shadow-sm` is used throughout; hover states are React state rather than
// `group-hover`, which this project's compiled build silently drops.

// ---- Popover -------------------------------------------------------------------------------------------------------------
// A labelled trigger button and an absolutely-positioned card. Controlled: the parent owns `isOpen`. Closes on an outside
// mousedown, pointerdown or click (scoped by `data-popover-container`) and on Escape. Focus moves into the panel when it
// opens and returns to the trigger on Escape or when the panel closes while focus was inside it.

export const FILTER_BUTTON_CSS =
  'h-[34px] min-w-[120px] bg-white border border-[#D0D5DD] rounded-[9px] px-[12px] text-[14px] font-semibold text-[#344054] text-left leading-[21px] flex items-center justify-between gap-1.5 shadow-sm hover:bg-[#F9FAFB] transition-colors cursor-pointer';

export const POPOVER_CARD_CSS =
  'absolute top-16 z-50 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-4 flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-100';

export interface PopoverProps {
  /** Caption above the trigger and the trigger's accessible name. */
  label: string;
  /** Text shown inside the trigger; defaults to `label`. */
  value?: React.ReactNode;
  icon?: React.ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  align?: 'left' | 'right';
  /** Tailwind width class for the card, e.g. `w-64` (default) or `w-[320px]`. */
  width?: string;
  /** Count shown on the trigger (active filters). Hidden when 0 / undefined. */
  badge?: number;
  /** Hide the caption row (for toolbar buttons that have no label above them). */
  hideCaption?: boolean;
  disabled?: boolean;
  /** The trigger's tooltip (e.g. the Filters summary in full). */
  title?: string;
  children: React.ReactNode;
}

export const Popover: React.FC<PopoverProps> = ({ label, value, icon, isOpen, onOpenChange, align = 'left', width = 'w-64', badge, hideCaption = false, disabled = false, title, children }) => {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const focusInside = useRef(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (isOpen) {
      wasOpen.current = true;
      const card = panel.current;
      if (card && !card.contains(document.activeElement)) {
        const first = card.querySelector<HTMLElement>('input, button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])');
        (first ?? card).focus();
      }
      focusInside.current = !!card && card.contains(document.activeElement);
      return undefined;
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      // The unmounting panel would drop focus on <body>, so it goes back to the trigger.
      if (focusInside.current && (!document.activeElement || document.activeElement === document.body)) trigger.current?.focus();
      focusInside.current = false;
    }
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutside = (e: Event) => {
      const target = e.target as HTMLElement | null;
      // A click's target can already be gone (a control inside re-rendered it away): that is not an outside click.
      if (!target || target.isConnected === false) return;
      if (!target.closest(`[data-popover-container="${id}"]`)) onOpenChange(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      trigger.current?.focus();
      onOpenChange(false);
    };
    const handleFocusIn = (e: FocusEvent) => {
      focusInside.current = !!panel.current && panel.current.contains(e.target as Node);
    };
    const outsideEvents = ['mousedown', 'pointerdown', 'click'] as const;
    outsideEvents.forEach((name) => document.addEventListener(name, handleOutside));
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      outsideEvents.forEach((name) => document.removeEventListener(name, handleOutside));
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [isOpen, id, onOpenChange]);

  return (
    <div className="flex flex-col gap-1 relative" data-popover-container={id}>
      {!hideCaption && <span className="text-[12px] font-medium text-[#71717A]">{label}</span>}
      <button
        ref={trigger}
        type="button"
        aria-label={label}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={title}
        disabled={disabled}
        onClick={() => onOpenChange(!isOpen)}
        className={cn(FILTER_BUTTON_CSS, isOpen && 'bg-[#F9FAFB]', disabled && 'opacity-60 cursor-not-allowed')}
      >
        <span className="flex items-center gap-2 min-w-0 truncate">
          {icon}
          <span className="truncate">{value ?? label}</span>
          {badge ? (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4EBFF] text-[#6941C6] text-[11px] font-bold leading-none">{badge}</span>
          ) : null}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-[#667085] shrink-0" />
      </button>
      {isOpen && (
        <div ref={panel} role="dialog" tabIndex={-1} aria-label={label} className={cn(POPOVER_CARD_CSS, 'focus:outline-none', width, align === 'right' ? 'right-0' : 'left-0')}>
          {children}
        </div>
      )}
    </div>
  );
};

// ---- InfoTooltip ---------------------------------------------------------------------------------------------------------
// A hidden bubble is also `display: none`, so an unseen bubble near the page's right edge never widens the page.

export const TOOLTIP_BUBBLE_CSS =
  'pointer-events-none z-50 rounded-lg bg-[#101828] px-2.5 py-2 text-[11px] font-medium normal-case leading-snug text-white shadow-lg transition-opacity whitespace-normal break-words';

export interface InfoTooltipProps {
  text: string;
  widthClass?: string;
  positionClass?: string;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, widthClass = 'w-48', positionClass = 'bottom-full left-1/2 -translate-x-1/2 mb-1.5' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const bubbleId = useId();
  return (
    <span className="relative inline-flex items-center cursor-pointer" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)}>
      <span
        tabIndex={0}
        aria-label="More information"
        aria-describedby={bubbleId}
        className="inline-flex items-center rounded focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
      >
        <Info className="w-3.5 h-3.5 text-[#98A2B3] hover:text-[#667085]" />
      </span>
      <span id={bubbleId} role="tooltip" aria-hidden={!isVisible} className={cn(TOOLTIP_BUBBLE_CSS, 'absolute', positionClass, widthClass, isVisible ? 'opacity-100' : 'opacity-0 hidden')}>
        {text}
      </span>
    </span>
  );
};

// ---- SegmentedControl ----------------------------------------------------------------------------------------------------

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'md' | 'sm';
  ariaLabel?: string;
}

export function SegmentedControl<T extends string>({ options, value, onChange, size = 'md', ariaLabel }: SegmentedControlProps<T>): JSX.Element {
  return (
    <div role="group" aria-label={ariaLabel} className={cn('inline-flex items-center bg-[#F2F4F7] p-[3px] rounded-[8px] border border-[#E4E4E7]/60', size === 'sm' ? 'h-[30px]' : 'h-[34px]')}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'px-3 py-1 font-semibold rounded-[6px] transition-all cursor-pointer flex items-center justify-center whitespace-nowrap',
              size === 'sm' ? 'text-[12px] h-6 px-2.5' : 'text-[13px] h-7',
              selected ? 'bg-white text-[#101828] shadow-sm font-bold' : 'text-[#667085] hover:text-[#101828]'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

// ---- ToggleSwitch --------------------------------------------------------------------------------------------------------
// A 36x20 pill with a 16px knob, exposed as role="switch"; `size="sm"` is a 28x16 pill with a 12px knob and a 12px label.

export interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
  /** Optional element rendered between the label and the switch (e.g. an InfoTooltip). */
  hint?: React.ReactNode;
  size?: 'md' | 'sm';
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, label, disabled = false, hint, size = 'md' }) => {
  const switchId = useId();
  const sm = size === 'sm';
  return (
    <div className={cn('flex items-center', sm ? 'gap-1.5 h-6 whitespace-nowrap' : 'gap-2')}>
      <label htmlFor={switchId} className={cn(sm ? 'text-[12px] leading-[18px]' : 'text-[13px] leading-[20px]', 'font-semibold text-[#344054]', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
        {label}
      </label>
      {hint}
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(sm ? 'w-7 h-4' : 'w-9 h-5', 'flex items-center rounded-full p-0.5 transition-colors cursor-pointer shrink-0', checked ? 'bg-[#7C3AED]' : 'bg-[#E4E4E7]', disabled && 'opacity-50 cursor-not-allowed')}
      >
        <span className={cn('block bg-white rounded-full shadow-md transform transition-transform', sm ? 'w-3 h-3' : 'w-4 h-4', checked ? (sm ? 'translate-x-3' : 'translate-x-4') : 'translate-x-0')} />
      </button>
    </div>
  );
};

// ---- Pill ----------------------------------------------------------------------------------------------------------------
// Chips and badges: active-filter chips (with a remove button) and status badges (colour from STATUS_COLORS).

export type PillTone = 'neutral' | 'brand' | 'status';

export interface PillProps {
  children: React.ReactNode;
  onRemove?: () => void;
  tone?: PillTone;
  /** Explicit colours (status badges); overrides the tone classes. */
  color?: { fg: string; bg: string };
  /** Accessible name for the remove button when children is not a plain string. */
  removeLabel?: string;
  className?: string;
  /** Hover text (a status badge's explanation). */
  title?: string;
}

const TONE_CSS: Record<PillTone, string> = {
  neutral: 'bg-[#F1F5F9] text-[#64748B]',
  brand: 'bg-[#F4EBFF] text-[#6941C6]',
  status: 'bg-[#F2F4F7] text-[#475467]',
};

export const Pill: React.FC<PillProps> = ({ children, onRemove, tone = 'neutral', color, removeLabel, className, title }) => {
  const name = removeLabel ?? (typeof children === 'string' || typeof children === 'number' ? String(children) : 'item');
  return (
    <span
      className={cn('inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold leading-[18px] whitespace-nowrap', !color && TONE_CSS[tone], className)}
      style={color ? { color: color.fg, backgroundColor: color.bg } : undefined}
      title={title}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${name}`}
          onClick={(e) => {
            // The chip may sit inside a clickable row; removing it must not also activate the row.
            e.stopPropagation();
            onRemove();
          }}
          className="inline-flex items-center justify-center rounded-full -mr-1 p-0.5 opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
};

// ---- TableStates ---------------------------------------------------------------------------------------------------------
// Loading / empty / error for the report table: a pulsing skeleton with one cell per column, a quiet empty message, or an
// alert with "Try again". Rendered in place of the table body so the header and control bar stay put.

export type TableState = 'loading' | 'empty' | 'error';

export interface TableStatesProps {
  state: TableState;
  message?: string;
  onRetry?: () => void;
  /** Number of skeleton cells per row (the visible column count). */
  columns: number;
}

export const SKELETON_ROWS = 6;

export const TableStates: React.FC<TableStatesProps> = ({ state, message, onRetry, columns }) => {
  const cols = Math.max(1, columns);
  if (state === 'loading') {
    return (
      <div role="status" aria-label="Loading" className="flex flex-col gap-2 p-4">
        {Array.from({ length: SKELETON_ROWS }, (_, r) => (
          <div key={r} data-skeleton-row className="grid gap-3" style={{ gridTemplateColumns: cols > 1 ? `minmax(160px, 2fr) repeat(${cols - 1}, 1fr)` : 'minmax(160px, 2fr)' }}>
            {Array.from({ length: cols }, (_, c) => (
              <span key={c} className="h-4 rounded bg-[#F2F4F7] animate-pulse" style={{ opacity: 1 - r * 0.1 }} />
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (state === 'empty') {
    return (
      <div className="flex flex-col items-center justify-center gap-1 py-14 text-center">
        <span className="text-[14px] font-semibold text-[#344054]">{message ?? 'No rows match the current filters.'}</span>
        <span className="text-[12px] text-[#98A2B3]">Try widening the period or clearing a filter.</span>
      </div>
    );
  }
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-3 py-14 text-center">
      <span className="w-9 h-9 rounded-full bg-[#FEF3F2] border border-[#FECDCA] flex items-center justify-center">
        <AlertCircle className="w-5 h-5 text-[#D92D20]" />
      </span>
      <span className="text-[14px] font-semibold text-[#344054] max-w-md">{message ?? 'Something went wrong loading this report.'}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="h-9 px-3.5 bg-white border border-[#E4E4E7] rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F4F4F5] shadow-sm flex items-center gap-1.5 transition-colors leading-[21px]"
        >
          <RotateCcw className="w-4 h-4 text-[#667085]" /> Try again
        </button>
      )}
    </div>
  );
};

// ---- CapabilityGate ------------------------------------------------------------------------------------------------------
// Tier gating: renders its children only when the client's capabilities allow the feature. `vendor` (the Vendor hierarchy and
// SKU-level columns) needs SKU-level data on a Tier A/B client; Tier C never sees vendor rows even if a flag is mis-set. The
// kit never reads the client context itself: the pages fetch `capabilities` and pass it down.

export { capabilityAllows, type CapabilityRequirement } from './logic';

export interface CapabilityGateProps {
  capabilities: Capabilities | null | undefined;
  requires: CapabilityRequirement;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const CapabilityGate: React.FC<CapabilityGateProps> = ({ capabilities, requires, children, fallback = null }) => (
  <>{capabilityAllows(capabilities, requires) ? children : fallback}</>
);

/** The report header's toolbar buttons (How it works, Save view, Share, Export). */
export const HEADER_BUTTON_CSS =
  'h-9 px-3.5 bg-white border border-[#E4E4E7] rounded-[9px] text-[14px] font-semibold text-[#344054] hover:bg-[#F4F4F5] shadow-sm flex items-center gap-1.5 transition-colors leading-[21px] disabled:opacity-50 disabled:cursor-not-allowed';

// ===================================================================================================================
// ==== Table.tsx =====================================================================================================
// ===================================================================================================================
/**
 * The element's clientWidth, kept current with a ResizeObserver. (item 9): the observer callback only schedules
 * a measurement for the next animation frame (several notifications in one frame make one), and the state is set only when the width
 * changed. Setting state straight inside the callback re-lays-out the observed element within the same frame, which the browser reports as
 * "ResizeObserver loop completed with undelivered notifications" (a red overlay in development builds). Without ResizeObserver the window
 * resize event is the trigger. The first measure is synchronous (the first paint has the width).
 */
export const useElementWidth = (ref: RefObject<HTMLElement>): number => {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame: number | null = null;
    const apply = () => {
      frame = null;
      const next = el.clientWidth;
      setWidth((prev) => (prev === next ? prev : next));
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(apply);
    };
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', schedule);
      return () => {
        window.removeEventListener('resize', schedule);
        if (frame !== null) window.cancelAnimationFrame(frame);
      };
    }
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [ref]);
  return width;
};

// The weekly planning tables' column resizer: the app's `hooks/useResizableColumns` (same handle, keys and double-click reset) with a
export function useReportColumnResize(defaultWidths: Readonly<Record<string, number>>, fallbackWidth: number) {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const widthsRef = useRef(columnWidths);
  widthsRef.current = columnWidths;
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cleanupRef.current?.(), []);

  const widthOf = (key: string): number => columnWidths[key] ?? defaultWidths[key] ?? fallbackWidth;

  const setColumnWidth = (key: string, width: number) => setColumnWidths({ ...widthsRef.current, [key]: clampReportColumnWidth(width) });

  const resetColumnWidth = (key: string) => {
    const { [key]: _removed, ...rest } = widthsRef.current; // eslint-disable-line @typescript-eslint/no-unused-vars
    setColumnWidths(rest);
  };

  const resetColumnWidths = () => setColumnWidths({});

  const startResize = (key: string, startX: number) => {
    const startWidth = widthOf(key);
    setResizingKey(key);
    const onMove = (e: MouseEvent) => setColumnWidths({ ...widthsRef.current, [key]: clampReportColumnWidth(startWidth + e.clientX - startX) });
    const cleanup = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', cleanup);
      cleanupRef.current = null;
      setResizingKey(null);
    };
    cleanupRef.current?.();
    cleanupRef.current = cleanup;
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', cleanup);
  };

  const getResizeHandleProps = (key: string, label: string) => ({
    role: 'separator' as const,
    'aria-orientation': 'vertical' as const,
    'aria-label': `Resize ${label} column`,
    'aria-valuenow': widthOf(key),
    'aria-valuemin': REPORT_MIN_COLUMN_WIDTH,
    'aria-valuemax': REPORT_MAX_COLUMN_WIDTH,
    tabIndex: 0,
    title: 'Drag to resize, double-click to reset',
    draggable: false,
    onMouseDown: (e: React.MouseEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      startResize(key, e.clientX);
    },
    onClick: (e: React.MouseEvent<HTMLElement>) => e.stopPropagation(),
    onDoubleClick: (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      resetColumnWidth(key);
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      e.stopPropagation();
      setColumnWidth(key, widthOf(key) + (e.key === 'ArrowRight' ? REPORT_COLUMN_RESIZE_STEP : -REPORT_COLUMN_RESIZE_STEP));
    },
  });

  return { columnWidths, widthOf, isResizing: resizingKey !== null, getResizeHandleProps, resetColumnWidths };
}

// Child rows loaded lazily per parent. One request per parent per `resetKey`
// (client + base params): a new resetKey drops what was loaded and re-requests every parent still
// loading under the new params, so a mounted "Loading products…" placeholder is never stranded (the
// SKU loader's rule in useWeeklyPlanningReport). Rows and payloads are kept per parent key exactly as
// requested (`all`, `class:<id>`, `classes:<ids>`, `category:<id|none>`, `vendor:<key>[:classes:<ids>]`):
// the same `product:<pid>` arrives under several parents with different numbers, so nothing is merged.
// Responses for an older resetKey are ignored. A failure adds the caller's notice for that parent
// (`noticesByParent`, so the caller shows only the parents still in its tree); a retryable failure (the
// default) leaves the parent unloaded and `failed`, so the next `load` (a placeholder remounting on the
// next expand) asks again; a final one (`retryable` false, e.g. a 414) stores []. `fetch`,
// `failureNotice`, `retryable` and `params` are read through refs, so inline functions re-run nothing.

// One request per chain expand: `loadMany(keys)` asks for several parents at once through `fetchMany` (parents
// that `canBatch` accepts, at most BATCH_SIZE per request; the rest, and a lone parent, go one by one through `fetch`). Each parent
// is then held exactly as if it had been requested alone: its own payload, notice and failure (a failed batch fails each of its
// parents with that parent's own notice; a parent the answer lacks fails alone).

// One metric <td> for the report table (Plan 3 ). The value and its flags come from the
// column's `cell(row, ctx)` so the table, the CSV and the sort agree; this file only decides how a
// flag shows. Cell classes copy Vendor Performance's `metricCellCss` / `lastYearCellCss` (VP
// L1082/L1090) and its group-header bold variant (VP L1289).
//
// Decisions recorded here:
// - Markers use a native `title` tooltip (the cell text stays selectable and the table has no
// hover state per cell); the header's own tooltip is the state-driven bubble from VP.
// - Plan value columns (PLAN_COLUMNS; `markdownsPlan` is the Plan & OTB matrix's Markdowns Plan line): Weekly shows no
// monthly plan (D4), so a class without a weekly plan shows a muted "No weekly plan" (metricDefinitions' planCell).
// - A child judged at its planned parent shows a muted "see parent" in Health and
// Action, titled "Plan is set on <parent>", and no urgent dot; its cover cell is the value alone.
// - Status-kind columns (health / action) show only the pill; the estimated / allocated flags
// of the underlying cell are shown on the measure cells, not repeated on the pill.
// - The urgent dot sits next to the health pill and the action pill; it follows `isRowUrgent`
// (re-derived under a WOS override, else `summary.urgent`), the rule `needsAttention` uses.
// - The cover cell stacks a muted "own avg" under the value when the target in force is the row's
// own trailing average (the RESOLVED basis, so a WOS override, which replaces the target, drops
// it); its tooltip names the lookback ("last 3 months"). Short and on its own line so the column
// can still be resized narrow. An empty cover cell (unknown WOS) shows no suffix.
// - Plan cells on a vendor-class row that has a PLAN target (basis 'plan') but no allocated cell in the target period
// show a muted "no share" instead of the dash: the class plan exists, this vendor just has no
// share of it. A value, if present, is still shown.
// - This period sales gets a second muted line "vs $X" (the Compare basis value: plan, LY or the
// lookback average) with a ▲ / ▼ arrow. It is left out when the compare value is missing, and at cost (sales and their plans
// are retail only: `basis.salesCompareOnBasis`). The arrow and the figure beside it always describe the same number.
// "Sales WTD / MTD" leads with the ACTUAL sales to date (the cell's `text`, which is
// also what the CSV and the sort read), its line compares them with the plan (LY, average) PRORATED by the days elapsed
// ("vs $X", titled WTD / MTD); the Trended sales column shows the trended figure with the same ▲ / ▼ line against the FULL
// plan. Outside a trend period the figure is the sales and the arrow compares them with the plan.
// QA fix 8: a Tier C cell (basis 'projected', ReportDataSource) holds the month's projection as its sales and has no sales
// to date, so it never claims one: the figure keeps its PROJECTED tag and its line compares the projection with the full plan.
// - A CellValue with a `title` (the Sales Trend / Trend % columns: the basis in words) renders its
// text in a span carrying that title.
// - The estimated marker (italic + ESTIMATED_TOOLTIP) follows the cell's `flags.estimated`, which
// metricDefinitions' `flagsOf` sets only on inventory-derived columns (the period's INVENTORY was
// back-calculated; sales, inflow, plans and the trend columns are not). A cell that is both
// estimated and titled keeps both: "<title> · estimated from sales and inflows".

export const ESTIMATED_TOOLTIP = 'estimated from sales and inflows';
export const PROJECTED_TOOLTIP = 'projected from the weeks so far';
/** the projected tag's title in the table's grain ("the week so far" / "the month so far"). */
export const projectedTooltip = (grain: string | null | undefined): string => (grain === 'monthly' ? 'projected from the month so far' : grain === 'weekly' ? 'projected from the week so far' : PROJECTED_TOOLTIP);
export const PARTIAL_HISTORY_TOOLTIP = "last year's history does not cover this whole period";
export const URGENT_TOOLTIP = 'Urgent: on order within the span will not bring stock back to target';

export const metricCellCss = 'text-[#344054] text-[13px] font-semibold leading-[19.5px]';
export const lastYearCellCss = 'text-[#64748B] text-[13px] font-medium leading-[19.5px]';
export const groupCellCss = 'font-bold text-[#101828] text-[13px] leading-[19.5px]';
export const mutedCellCss = 'text-[#98A2B3] text-[13px] font-medium leading-[19.5px]';
export const MUTED_SUFFIX_CSS = 'text-[11px] font-medium text-[#98A2B3] whitespace-nowrap';
/**
 * / a caption stacked under a cell's value (own avg, vs plan, WTD, the push / cancel hint). It
 * WRAPS inside the column and contributes no width to it, so a long caption can never set the column width (it made OTB + Dev 268px
 * instead of ~136px). `w-0 min-w-full` was not enough live (a percentage min-width in a table cell's intrinsic sizing), so the caption
 * uses size containment (`contain: inline-size`: its intrinsic width is that of an empty box) and stretches to the width the value
 * gives the column. The stack is a block-level flex column, so the caption has the cell to wrap in.
 */
export const CELL_CAPTION_CSS = 'self-stretch [contain:inline-size] whitespace-normal break-words text-right text-[11px] font-medium text-[#98A2B3]';
/** A text caption (the push / cancel hint, own avg): at most two lines, then an ellipsis; its `title` carries the full text. */
export const CELL_CAPTION_TEXT_CSS = `${CELL_CAPTION_CSS} line-clamp-2`;
export const CELL_CAPTION_STACK_CSS = 'flex flex-col items-end leading-tight';
export const NO_SHARE_TOOLTIP = 'this vendor has no allocated share of the class plan in this period';
export const OWN_AVG_TOOLTIP = 'no plan target: judged against its own trailing average';
export const MARKER_TAG_CSS = 'inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-[#F2F4F7] text-[10px] font-semibold uppercase tracking-wide text-[#475467] leading-[14px] whitespace-nowrap cursor-help';

const ALLOC_BASIS_LABEL: Record<AllocBasis, string> = { net_sales: 'net sales', gross_margin: 'gross margin', cash_margin: 'cash margin', inventory: 'BOM inventory' };
const PLAN_COLUMNS = new Set(['salesPlan', 'inventoryPlan', 'inflowPlan', 'markdownsPlan', 'inventoryVsPlan']);
/** red for a reorder, amber for one with no OTB, purple to sell down / push, deep amber to sell through, grey otherwise. */
const ACTION_COLOR: Record<ActionCode, { fg: string; bg: string }> = {
  reorder: STATUS_COLORS.understocked,
  reorder_no_otb: STATUS_COLORS.watch_low,
  on_order_covers: STATUS_COLORS.no_plan,
  out_of_season: STATUS_COLORS.no_plan,
  class_type: STATUS_COLORS.no_plan,
  sell_down: STATUS_COLORS.overstocked,
  push_orders: STATUS_COLORS.overstocked,
  sell_through: STATUS_COLORS.watch_high,
  hold: STATUS_COLORS.no_plan,
  review: STATUS_COLORS.review,
  review_clear: STATUS_COLORS.no_recent_sales,
};
/** The suffix on units suggested from the prior year: the rule is 52 weeks in Weekly and 12 months in Monthly. */
export const reorderBasisSuffix = (grain: Grain | null | undefined): string => (grain === 'monthly' ? '12 mo' : '52 wk');

export const allocationTooltip = (ctx: CellContext): string => {
  const a = ctx.allocation;
  if (!a) return 'allocated from the class';
  const unit = a.grain === 'monthly' ? 'month' : 'week';
  // Inventory % of total shares by the BOM inventory at the target period: no window.
  if (a.basis === 'inventory') return `allocated by share of ${bomInventoryLabel(a.grain)}`;
  return `allocated by ${ALLOC_BASIS_LABEL[a.basis]} over ${a.window} ${unit}${a.window === 1 ? '' : 's'}`;
};

const ALIGN: Record<MetricDefinition['align'], string> = { left: 'text-left', right: 'text-right', center: 'text-center' };

/** The <td> classes for a column on a row kind; numbers are right-aligned tabular figures. */
export const metricCellClass = (column: MetricDefinition, kind: FlatRow['kind'] = 'leaf'): string => {
  const text = kind === 'group' || kind === 'subtotal' ? groupCellCss : kind === 'unallocated' || kind === 'unclassified' ? mutedCellCss : column.group === 'lastYear' ? lastYearCellCss : metricCellCss;
  const pad = kind === 'group' || kind === 'subtotal' ? 'py-2.5' : 'py-3';
  return cn('px-[10px] whitespace-nowrap', pad, ALIGN[column.align], column.kind !== 'text' && 'tabular-nums', text);
};

const Tag: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <span className={MARKER_TAG_CSS} title={title}>{children}</span>
);

const UrgentDot: React.FC = () => (
  <span role="img" aria-label="Urgent" title={URGENT_TOOLTIP} className="inline-block w-1.5 h-1.5 rounded-full bg-[#D92D20] shrink-0" />
);

const statusContent = (column: MetricDefinition, value: CellValue, urgent: boolean, grain: Grain | null | undefined): React.ReactNode => {
  if (value.value === null) return <span className="text-[#98A2B3]" title={value.title}>{EMPTY}</span>;
  const color = column.kind === 'action' ? ACTION_COLOR[value.value as ActionCode] : STATUS_COLORS[value.value as Status];
  // the Review pill (health 'review' / action 'review') explains itself on hover; actions carry their own title.
  const review = value.value === 'review';
  return (
    <span className="inline-flex items-center justify-center gap-1.5">
      <Pill color={color} title={review ? reviewTooltip(grain) : value.title}>{value.text}</Pill>
      {urgent && <UrgentDot />}
    </span>
  );
};

const OWN_AVG_SUFFIX = 'own avg';

/** The own-avg tooltip: OWN_AVG_TOOLTIP plus the lookback ("over the last 3 months"). */
const ownAvgTitle = (ctx: CellContext): string => {
  const n = ctx.lookback.length;
  const unit = ctx.grain === 'monthly' ? 'month' : 'week';
  return `${OWN_AVG_TOOLTIP} over the last ${n === 1 ? unit : `${n} ${unit}s`}`;
};

/** A vendor-class row with a plan target whose target-period cell carries no allocation. */
const hasNoShare = (row: AnyRow, ctx: CellContext): boolean => {
  if (!isVendorRow(row) || row.level !== 'vendorClass') return false;
  // Vendor-class rows inherit the class's target and basis: only a PLAN target means a class plan
  // exists for this vendor to have a share of (an own-average target means there is no plan).
  const t = row.summary?.targetWos;
  if (t === null || t === undefined || row.summary?.targetBasis !== 'plan') return false;
  return !row.periods[String(ctx.targetPeriod)]?.isAllocated;
};

/** The second line under This period sales: "vs $X" with a trend arrow, or null. */
const compareLine = (row: AnyRow, ctx: CellContext): React.ReactNode => {
  const c = row.periods[String(ctx.targetPeriod)];
  if (!c) return null;
  const full = compareValue(c, 'sales', ctx.compare, { periods: row.periods, lookback: ctx.lookback });
  const onBasis = salesCompareOnBasis(typeof full === 'number' && !Number.isNaN(full) ? full : null, ctx.basis);
  if (!onBasis.available || onBasis.value === null) return null; // at cost the plan is retail: no line
  const base = onBasis.value;
  const sales = comparableSales(c, ctx.trendMethod);
  const trended = isTrendCell(c) && sales !== null;
  const arrow = sales === null || sales === base ? null : sales > base ? <span role="img" className="text-[#027A48]" aria-label="up">▲</span> : <span role="img" className="text-[#B42318]" aria-label="down">▼</span>;
  return (
    <span className={cn(CELL_CAPTION_CSS, 'flex flex-wrap items-center justify-end gap-x-1')} title={trended ? `Trended sales ${fmtCurrency(sales)} vs ${fmtCurrency(base)}` : undefined}>
      {arrow}
      <span>{`vs ${fmtCurrency(base)}`}</span>
    </span>
  );
};

const COMPARE_WORD = { plan: 'plan', ly: 'last-year value', avg: 'average' } as const;

/**
 * The line under Sales WTD / MTD in a trend period (QA fix 5): the sales to date against the plan (LY, average) prorated by the days
 * elapsed, "vs $X" titled WTD / MTD. Null at cost, without a compare value, or where proration is impossible (no sales day).
 */
const proratedCompareLine = (row: AnyRow, ctx: CellContext): React.ReactNode => {
  const c = row.periods[String(ctx.targetPeriod)];
  if (!c || typeof c.sales !== 'number') return null;
  const full = compareValue(c, 'sales', ctx.compare, { periods: row.periods, lookback: ctx.lookback });
  const onBasis = salesCompareOnBasis(typeof full === 'number' && !Number.isNaN(full) ? full : null, ctx.basis);
  if (!onBasis.available || onBasis.value === null) return null;
  const p = prorate(onBasis.value, c, ctx.targetPeriod, ctx.grain, ctx.salesThrough);
  if (p.value === null) return null;
  const arrow = c.sales === p.value ? null : c.sales > p.value ? <span role="img" className="text-[#027A48]" aria-label="up">▲</span> : <span role="img" className="text-[#B42318]" aria-label="down">▼</span>;
  return (
    <span className={cn(CELL_CAPTION_CSS, 'flex flex-wrap items-center justify-end gap-x-1')} title={prorationTitle(p, COMPARE_WORD[ctx.compare])}>
      {arrow}
      <span>{`vs ${fmtCurrency(p.value)}`}</span>
    </span>
  );
};


/** The value with its flag markers (estimated italic, allocated / projected / partial history tags). */
const renderValueWithTags = (value: CellValue, empty: boolean, ctx: CellContext): React.ReactNode => {
  const { flags } = value;
  const title = [value.title, flags.estimated ? ESTIMATED_TOOLTIP : null].filter(Boolean).join(' · ');
  // An empty cell keeps its own title.
  const text = empty ? <span className="text-[#98A2B3]" title={value.title || undefined}>{EMPTY}</span> : title ? <span className={flags.estimated ? 'italic' : undefined} title={title}>{value.text}</span> : value.text;
  const tags: React.ReactNode[] = [];
  if (!empty && flags.allocated) tags.push(<Tag key="allocated" title={allocationTooltip(ctx)}>allocated</Tag>);
  if (!empty && flags.projected) tags.push(<Tag key="projected" title={projectedTooltip(ctx.grain)}>projected</Tag>);
  // D6 (Monthly): a plan taken from the V2 plan as it stood in the month, before the class's first V3 month.
  if (!empty && flags.v2History && ctx.grain === 'monthly') tags.push(<Tag key="v2History" title={v2HistoryNote(ctx.targetPeriod)}>V2 plan</Tag>);
  if (flags.lyPartial) tags.push(<Tag key="lyPartial" title={PARTIAL_HISTORY_TOOLTIP}>partial history</Tag>);
  if (!empty && value.tag) tags.push(<Tag key="prorated" title={value.title ?? value.tag}>{value.tag}</Tag>);
  if (tags.length === 0) return text;
  return (
    <span className="inline-flex items-center justify-end gap-1.5">
      {tags}
      {text}
    </span>
  );
};

/** The contents of a metric cell: the value with its markers, or a tag that replaces it. */
export const renderCellContent = (column: MetricDefinition, flatRow: FlatRow, ctx: CellContext): React.ReactNode => {
  if (flatRow.kind === 'skuPlaceholder') return null;
  const value = column.cell(flatRow.row, { ...ctx, pctOfParent: flatRow.pctOfParent, distributedOtb: flatRow.distributedOtb ?? null });
  if ((column.kind === 'status' || column.kind === 'action') && defersToParent(flatRow.row.summary)) {
    // The parent's name is in the title for hover and in sr-only text for screen readers.
    return (
      <span className="inline-flex items-center justify-center">
        <span className={MUTED_SUFFIX_CSS} title={value.title}>{SEE_PARENT_LABEL}</span>
        {value.title && <span className="sr-only">{` (${value.title})`}</span>}
      </span>
    );
  }
  if (column.kind === 'status' || column.kind === 'action') return statusContent(column, value, isRowUrgent(flatRow.row, ctx), ctx.grain);
  // D4: a class without a weekly plan: a muted "No weekly plan" in its plan and OTB cells.
  if (value.value === null && value.text === NO_WEEKLY_PLAN_LABEL) return <span className={MUTED_SUFFIX_CSS} title={value.title}>{NO_WEEKLY_PLAN_LABEL}</span>;
  // activity against no baseline: a muted "new" (its title says which baseline is missing).
  if (value.value === null && value.text === NEW_LABEL) return <span className={MUTED_SUFFIX_CSS} title={value.title}>{NEW_LABEL}</span>;
  const empty = value.value === null || value.text === EMPTY;
  if (empty && PLAN_COLUMNS.has(column.id) && hasNoShare(flatRow.row, ctx)) {
    return <span className={MUTED_SUFFIX_CSS} title={NO_SHARE_TOOLTIP}>no share</span>;
  }
  const main = renderValueWithTags(value, empty, ctx);
  // Wendi (2026-10-22), item 3: an allocated OTB (a vendor's share of the class plan, or a distributed one) says which basis split it.
  if (!empty && (column.id === 'otb' || column.id === 'otbDev')) {
    const a = allocatedOtbOf(flatRow.row, { ...ctx, distributedOtb: flatRow.distributedOtb ?? null });
    if (a) {
      return (
        <span className={CELL_CAPTION_STACK_CSS}>
          {main}
          <span data-testid="allocated-otb" className={CELL_CAPTION_TEXT_CSS} title={allocatedOtbTooltip(a, ctx.grain)}>{allocatedOtbBadge(a)}</span>
        </span>
      );
    }
  }
  if (!empty && pushCancelHint(column, value, flatRow, ctx)) {
    return (
      <span className={CELL_CAPTION_STACK_CSS}>
        {main}
        <span className={CELL_CAPTION_TEXT_CSS} title={PUSH_CANCEL_HINT}>{PUSH_CANCEL_HINT}</span>
      </span>
    );
  }
  if (column.id === 'cover' && value.value !== null && resolveTarget(flatRow.row.summary, ctx.wosOverride).basis === 'own_avg') {
    return (
      <span className={CELL_CAPTION_STACK_CSS}>
        {main}
        <span className={CELL_CAPTION_TEXT_CSS} title={ownAvgTitle(ctx)}>{OWN_AVG_SUFFIX}</span>
      </span>
    );
  }
  // units suggested from the last 52 weeks (or, on a product, partly so) carry a muted "52 wk" suffix; the
  // title is the basis in words (metricDefinitions sets it only for last52 / mixed).
  if (column.id === 'suggestedReorder' && value.value !== null && value.title) {
    const basis = flatRow.row.summary?.reorderBasis;
    if (basis === 'last52' || basis === 'mixed') {
      return (
        <span className="inline-flex items-center justify-end gap-1.5">
          <span>{value.text}</span>
          <span className={MUTED_SUFFIX_CSS} title={value.title}>{reorderBasisSuffix(ctx.grain)}</span>
        </span>
      );
    }
  }
  if (column.id === 'thisPeriodSales' || (column.id === 'salesTrend' && !empty)) {
    // Sales WTD / MTD: the sales to date, against the prorated plan in a trend period (a Tier C projection has no sales to date: it
    // keeps its tag and is compared with the full plan); Trended sales: the trended figure against the full plan.
    const c = flatRow.row.periods[String(ctx.targetPeriod)];
    const toDate = column.id === 'thisPeriodSales' && !empty && isTrendCell(c) && c?.trendBasis !== 'projected';
    const line = toDate ? proratedCompareLine(flatRow.row, ctx) : empty ? null : compareLine(flatRow.row, ctx);
    if (line) {
      return (
        <span className={CELL_CAPTION_STACK_CSS}>
          {main}
          {line}
        </span>
      );
    }
  }
  return main;
};

/** Round 2: a muted note under a negative OTB + Dev on a Hold row that still has on order (display only: no status or action change). */
export const PUSH_CANCEL_HINT = 'on order exceeds plan: consider push/cancel';
const pushCancelHint = (column: MetricDefinition, value: CellValue, flatRow: FlatRow, ctx: CellContext): boolean => {
  if (column.id !== 'otbDev' || typeof value.value !== 'number' || value.value >= 0) return false;
  if (rowAction(flatRow.row, ctx) !== 'hold') return false;
  const onOrder = summaryOnBasis(flatRow.row, 'onOrder', ctx.basis).value;
  return typeof onOrder === 'number' && onOrder > 0;
};

export interface MetricCellProps { column: MetricDefinition; flatRow: FlatRow; ctx: CellContext; style?: React.CSSProperties }

export const MetricCell: React.FC<MetricCellProps> = ({ column, flatRow, ctx, style }) => (
  <td className={metricCellClass(column, flatRow.kind)} style={style}>{renderCellContent(column, flatRow, ctx)}</td>
);

// One <tr> of the report table (Plan 3 ). Row classes copy Vendor Performance: group rows
// `bg-[#F8F9FA]` bold with the chevron (VP L3990 location / class group rows), plain rows white
// with the sticky first cell `sticky left-0 z-10 bg-white border-r shadow-[1px_0_0_0_#EAECF0]`
// (VP L3876), the Top Sellers expand-to-children chevron (TS L3780-3800). Hover is React state,
// not `group-hover` (VP L1501). Depth indents the label by 20px a level.
// The label cell is a row header (`<th scope="row">`, as the Plan & OTB matrix) so screen readers
// announce the row name with each cell; LABEL_CSS sets its weight and `text-left` its alignment.
//
// A class or product row whose summary carries the M1 §6 `deadstock` flag shows a small "Deadstock" tag after its label.
//
// A row flagged `filteredChildren` (filters / search hid some of its children while its own totals
// stay the payload's) shows a small "filtered" tag after its label.
//
// An optional detail toggle (`detail`, from ReportTable's `renderRowDetail`) sits after the label,
// separate from the group chevron, so opening a row's detail never collapses / expands the group.
//
// The SKU placeholder row (`kind 'skuPlaceholder'`) renders "Loading SKUs…" (or "Loading
// products…" for a `<parent>:products` placeholder of a product view) and asks the caller
// for the rows once per MOUNT (`onExpandSkus(parentKey)`; the page passes `loadChildRows`, which
// loads product rows in a product view and SKU rows otherwise); the caller swaps it for real rows by
// passing them to buildRowTree's `skuRowsByParent` / `productRowsByParent`. Collapsing and re-expanding
// the parent unmounts and remounts the placeholder, which fires `onExpandSkus` again: the caller
// must de-duplicate in-flight keys. A `<parent>:note` row is a static message (no request); a
// placeholder with a `className` (a failed product load) shows that text and still asks on mount, so
// collapsing and re-expanding retries. On a failed SKU load the caller replaces the placeholder (an
// error row or an empty list in `skuRowsByParent`) rather than leave it to re-request forever.

export const FILTERED_TOOLTIP = 'Some rows under this one are hidden by the filters or search; its totals are the full row.';
export const STORE_MATCH_TOOLTIP = 'The Status filter matches these stores only. The totals are the whole chain; expand the row to see the matching stores\' rows.';
const FILTERED_TAG_CSS = 'inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-[#F4EBFF] text-[10px] font-semibold uppercase tracking-wide text-[#6D28D9] leading-[14px] whitespace-nowrap cursor-help shrink-0';

const DEADSTOCK_TAG_CSS = 'inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-[#EAECF0] text-[10px] font-semibold uppercase tracking-wide text-[#344054] leading-[14px] whitespace-nowrap cursor-help shrink-0';

export const STICKY_LABEL_CELL_CSS = 'sticky left-0 z-10 px-[12px] border-r border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] whitespace-nowrap';

const ROW_CSS: Record<FlatRow['kind'], string> = {
  group: 'bg-[#F8F9FA] border-y border-[#E4E4E7]',
  subtotal: 'bg-[#F9FAFB] border-y border-[#E4E4E7]',
  leaf: 'bg-white',
  sku: 'bg-white',
  unallocated: 'bg-white',
  unclassified: 'bg-white',
  skuPlaceholder: 'bg-white',
};
const HOVER_CSS: Partial<Record<FlatRow['kind'], string>> = { group: 'bg-[#F1F3F5]', subtotal: 'bg-[#F2F4F7]' };
const LABEL_CSS: Record<FlatRow['kind'], string> = {
  group: 'text-[14px] font-bold text-[#101828]',
  subtotal: 'text-[14px] font-bold text-[#101828]',
  leaf: 'text-[13px] font-semibold text-[#101828]',
  sku: 'text-[13px] font-medium text-[#344054]',
  unallocated: 'text-[13px] font-medium italic text-[#667085]',
  unclassified: 'text-[13px] font-medium text-[#667085]',
  skuPlaceholder: 'text-[13px] font-medium text-[#71717A]',
};

export const rowLabelText = (flatRow: FlatRow): string => metricById('label').cell(flatRow.row, { grain: 'weekly', targetPeriod: 0, lookback: [], compare: 'plan', basis: 'retail', multiplier: 1.5, wosOverride: null }).text;

const DeadstockTag: React.FC = () => <span className={DEADSTOCK_TAG_CSS} title={DEADSTOCK_TOOLTIP}>{DEADSTOCK_LABEL}</span>;
/** Round-2 D9: suspect data is flagged (summary.dataFlags), never excluded: a small amber tag whose title says why. */
const CHECK_DATA_TAG_CSS = 'inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-[#FEF0C7] text-[10px] font-semibold uppercase tracking-wide text-[#93370D] leading-[14px] whitespace-nowrap cursor-help shrink-0';
const CheckDataTag: React.FC<{ flags: readonly DataFlag[]; grain?: Grain | null }> = ({ flags, grain }) => <span className={CHECK_DATA_TAG_CSS} title={checkDataTooltip(flags, grain)}>{CHECK_DATA_LABEL}</span>;

/** W2: the marker on a non-selling location's row under its class (Class › Location). */
export const NON_SELLING_TAG = 'Non-selling';
/** W2 item 12: Hold / Take beside a vendor × class row with on order due this period (a display hint, never a status). */
const SHIP_TAG_CSS: Record<'hold' | 'take', string> = {
  hold: 'inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-[#FEF0C7] text-[10px] font-semibold uppercase tracking-wide text-[#93370D] leading-[14px] whitespace-nowrap cursor-help shrink-0',
  take: 'inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-[#D1FADF] text-[10px] font-semibold uppercase tracking-wide text-[#05603A] leading-[14px] whitespace-nowrap cursor-help shrink-0',
};
/** the need here is before on order (target cover × plan sales − BOM), less the other vendors' due this period. */
export const shipHintTitle = (h: ShipHint): string => {
  const others = (h.otherDue ?? 0) > 0 ? `, of which other vendors have ${fmtCurrency(h.otherDue ?? 0)} due` : '';
  return h.hint === 'hold'
    ? `Hold: ${fmtCurrency(h.due)} on order is due this period against a need of ${fmtCurrency(h.need)} for the class here before on order${others}. Ask the vendor to hold what is beyond the need.`
    : `Take: ${fmtCurrency(h.due)} on order is due this period, within the need of ${fmtCurrency(h.need)} for the class here before on order${others}.`;
};
const ShipHintTag: React.FC<{ hint: ShipHint }> = ({ hint }) => (
  // the tag names the PO ("Hold PO" / "Take PO"); the title keeps its "Hold:" / "Take:" lead.
  <span className={SHIP_TAG_CSS[hint.hint]} title={shipHintTitle(hint)}>{hint.hint === 'hold' ? 'Hold PO' : 'Take PO'}</span>
);

/** Default label: code · name; SKU rows product title with colour / size muted; counts on groups. */
export const DefaultRowLabel: React.FC<{ flatRow: FlatRow; grain?: Grain | null }> = ({ flatRow, grain }) => {
  const { row, kind, childCount } = flatRow;
  if (kind === 'skuPlaceholder' && row.className && (row.key.endsWith(':note') || row.className === PRODUCT_LOAD_FAILED)) {
    // A note row (`<parent>:note`) or a failed product load: the text, no spinner.
    return <span className="italic">{row.className}</span>;
  }
  if (kind === 'skuPlaceholder') {
    return (
      <span className="inline-flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#7F56D9]" aria-hidden="true" />
        {row.key.endsWith(':products') ? 'Loading products…' : 'Loading SKUs…'}
      </span>
    );
  }
  if (kind === 'sku' && isVendorRow(row)) {
    const variant = [row.color, row.size].filter(Boolean).join(' / ');
    return (
      <span className="inline-flex items-baseline gap-2 min-w-0">
        <span className="truncate">{fixMojibake(row.productTitle) || row.skuId || row.variantId || row.key}</span>
        {variant && <span className="text-[12px] text-[#667085] font-normal">{variant}</span>}
        {row.level === 'product' && row.summary?.deadstock && <DeadstockTag />}
      </span>
    );
  }
  // No code chip on a row labelled by its vendor (Class by Vendor): its label is the vendor, not code · name.
  const byVendor = isVendorRow(row) && row.labelAs === 'vendor';
  // W2: a location row of Class › Location is labelled by its location (no class code chip either).
  const byLocation = !isVendorRow(row) && row.labelAs === 'location';
  // Product rows carry their class's code and name (stamped by the backend); their label is the product.
  const isProduct = isVendorRow(row) && row.level === 'product';
  const code = kind !== 'unallocated' && kind !== 'unclassified' && !byVendor && !byLocation && !isProduct && row.classCode && row.classCode !== row.className ? row.classCode : null;
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      {code && <span className="px-1.5 py-0.5 rounded-[5px] bg-[#F2F4F7] text-[11px] font-mono font-semibold text-[#475467] shrink-0">{code}</span>}
      <span className="truncate">{code ? row.className : rowLabelText(flatRow)}</span>
      {(kind === 'group' || kind === 'subtotal') && childCount > 0 && (
        <span className="font-normal text-[12px] text-[#667085]">{childCount} {childCount === 1 ? 'row' : 'rows'}</span>
      )}
      {flatRow.storeMatch ? (
        <span className={FILTERED_TAG_CSS} title={STORE_MATCH_TOOLTIP}>{flatRow.storeMatch.matched} of {flatRow.storeMatch.total} stores match</span>
      ) : flatRow.filteredChildren && (
        <span className={FILTERED_TAG_CSS} title={FILTERED_TOOLTIP}>filtered</span>
      )}
      {/* M1 §6 Deadstock: a flag beside the status, on class and product rows only (never a subtotal, a vendor or a SKU row). */}
      {row.summary?.deadstock && (kind === 'leaf' || kind === 'group') && (!isVendorRow(row) || isProduct) && <DeadstockTag />}
      {!!row.summary?.dataFlags?.length && <CheckDataTag flags={row.summary.dataFlags} grain={grain} />}
      {byLocation && row.summary?.nonSelling && <span className={DEADSTOCK_TAG_CSS} title={classTypeTooltip(null, true)}>{NON_SELLING_TAG}</span>}
      {isVendorRow(row) && row.shipHint && <ShipHintTag hint={row.shipHint} />}
    </span>
  );
};

export interface ReportTableRowProps {
  flatRow: FlatRow;
  columns: MetricDefinition[];
  ctx: CellContext;
  widthOf: (id: string) => number;
  /** A user-sized column: its cells are held to the width, overflow cut with an ellipsis. */
  isCapped?: (id: string) => boolean;
  onToggleGroup: (key: string) => void;
  onExpandSkus: (parentKey: string) => void;
  renderRowLabel?: (flatRow: FlatRow) => React.ReactNode;
  /** The row-detail toggle (ReportTable's `renderRowDetail`); omitted → no toggle. */
  detail?: { open: boolean; onToggle: () => void; /** id of the detail row this toggle controls */ controlsId?: string };
}

export const ReportTableRow: React.FC<ReportTableRowProps> = ({ flatRow, columns, ctx, widthOf, isCapped = () => false, onToggleGroup, onExpandSkus, renderRowLabel, detail }) => {
  const { row, kind, depth, expanded, childCount, parentKey } = flatRow;
  const [hovered, setHovered] = useState(false);
  const requested = useRef(false);
  useEffect(() => {
    // Both placeholders ask the caller (the page passes `loadChildRows`: product rows in a product view, SKU rows otherwise).
    if (placeholderLoads(flatRow) && parentKey && !requested.current) {
      requested.current = true;
      onExpandSkus(parentKey);
    }
  }, [kind, parentKey, onExpandSkus]); // eslint-disable-line react-hooks/exhaustive-deps -- flatRow is read for placeholderLoads; the request is once per mount (the ref), keyed by kind and parent

  const expandable = childCount > 0 && kind !== 'skuPlaceholder';
  const name = rowLabelText(flatRow);
  const bg = hovered ? HOVER_CSS[kind] ?? 'bg-[#FAFAFC]' : ROW_CSS[kind];
  const labelWidth = widthOf('label');
  const label = renderRowLabel ? renderRowLabel(flatRow) : <DefaultRowLabel flatRow={flatRow} grain={ctx.grain} />;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <tr
      data-row-kind={kind}
      data-row-key={row.key}
      data-row-id={flatRow.id}
      aria-label={name}
      className={cn('transition-colors', bg, kind === 'group' || kind === 'subtotal' ? 'border-y border-[#E4E4E7]' : 'border-b border-[#EAECF0]')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <th
        scope="row"
        className={cn(STICKY_LABEL_CELL_CSS, bg, 'py-2.5 text-left', LABEL_CSS[kind])}
        style={{ width: labelWidth, minWidth: labelWidth, maxWidth: labelWidth, paddingLeft: 16 + depth * 20 }}
      >
        {expandable ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${name}`}
            onClick={() => onToggleGroup(flatRow.expandKey ?? row.key)}
            className="inline-flex items-center gap-1.5 max-w-full text-left cursor-pointer select-none"
          >
            <Chevron className="w-4 h-4 text-[#475467] shrink-0" aria-hidden="true" />
            {label}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 max-w-full pl-[22px]">{label}</span>
        )}
        {detail && (
          <button
            type="button"
            aria-expanded={detail.open}
            aria-controls={detail.controlsId}
            aria-label={`${detail.open ? 'Hide' : 'Show'} details for ${name}`}
            title={detail.open ? 'Hide details' : 'Show details'}
            onClick={detail.onToggle}
            className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-[5px] align-middle text-[#667085] hover:text-[#09090B] hover:bg-[#F2F4F7] transition-colors"
          >
            <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', detail.open && 'rotate-180')} aria-hidden="true" />
          </button>
        )}
      </th>
      {columns.map((column) => (
        <MetricCell key={column.id} column={column} flatRow={flatRow} ctx={ctx} style={isCapped(column.id) ? { width: widthOf(column.id), minWidth: widthOf(column.id), maxWidth: widthOf(column.id), overflow: 'hidden', textOverflow: 'ellipsis' } : { width: widthOf(column.id), minWidth: widthOf(column.id) }} />
      ))}
    </tr>
  );
};

// The sticky header row of the report table (Plan 3 ): the `SortableHeader` cell from
// Vendor Performance (VP L1395-1460: info icon with a state-driven tooltip, label button that
// opens the ColumnHeaderMenu, sort icon), the sticky first column (VP L3738), the drag / resize
// wiring (VP L3765-3800). Column state is the caller's; this only renders and raises events.

export const HEADER_ROW_CSS = 'bg-[#F9F9FB] border-b border-[#E4E4E7] text-[#64748B] text-[12px] font-semibold select-none';
const JUSTIFY: Record<MetricDefinition['align'], string> = { left: 'justify-start', right: 'justify-end', center: 'justify-center' };
const TEXT_ALIGN: Record<MetricDefinition['align'], string> = { left: 'text-left', right: 'text-right', center: 'text-center' };

interface HeaderCellProps {
  column: MetricDefinition;
  grain?: Grain;
  sort: RowTreeSort | null;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  menu: React.ReactNode;
  isLast: boolean;
  /** A user-sized column: the label truncates instead of holding the column open. */
  capped?: boolean;
}

const HeaderCell: React.FC<HeaderCellProps> = ({ column, grain, sort, isMenuOpen, onToggleMenu, menu, isLast, capped = false }) => {
  // a column shows its short label (wrapping onto two lines at most); the full name is the first line of its tooltip when it differs.
  const fullLabel = columnLabel(column, grain) !== columnShortLabel(column, grain) ? columnLabel(column, grain) : null;
  const [hovered, setHovered] = useState(false);
  const active = sort?.column === column.id;
  const sortIcon = active
    ? (sort!.direction === 'asc' ? <ChevronUp className="w-3 h-3 shrink-0" /> : <ChevronDown className="w-3 h-3 shrink-0" />)
    : <ArrowUpDown className="w-3 h-3 text-[#94A3B8] shrink-0" />;
  return (
    <div className={cn('relative flex items-center gap-1.5 w-full hover:z-50', JUSTIFY[column.align])} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} data-column-menu>
      {column.description && !capped && ( // a user-narrowed column gives the info icon's room to its label (the header menu keeps the description)
        <div className="relative inline-flex items-center shrink-0">
          <Info className="w-3 h-3 text-[#94A3B8] shrink-0 cursor-help" aria-hidden="true" />
          {hovered && !isMenuOpen && (
            <div role="tooltip" className={cn(TOOLTIP_BUBBLE_CSS, 'absolute top-full mt-1.5 w-56 text-left opacity-100 z-50', isLast ? 'right-0' : 'left-0')}>
              {fullLabel && <span className="block font-semibold mb-0.5">{fullLabel}</span>}
              {column.description}
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onToggleMenu}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        className={cn('flex items-center gap-1 hover:text-[#09090B] transition-colors cursor-pointer', capped && 'min-w-0', JUSTIFY[column.align], TEXT_ALIGN[column.align], (active || isMenuOpen) && 'text-[#09090B]')}
      >
        <span className={cn(capped ? 'truncate min-w-0' : 'whitespace-normal break-words line-clamp-2', 'leading-tight', TEXT_ALIGN[column.align])} title={capped ? columnLabel(column, grain) : undefined}>{columnShortLabel(column, grain)}</span>
        {sortIcon}
      </button>
      {isMenuOpen && menu}
    </div>
  );
};

export interface ReportTableHeaderProps {
  labelColumn: MetricDefinition;
  columns: MetricDefinition[];
  sort: RowTreeSort | null;
  onSort: (sort: RowTreeSort) => void;
  activeMenu: string | null;
  setActiveMenu: (id: string | null) => void;
  widthOf: (id: string) => number;
  /** A user-sized column: held to its width (max-width), the label truncated. */
  isCapped?: (id: string) => boolean;
  isResizing: boolean;
  getResizeHandleProps: (id: string, label: string) => React.HTMLAttributes<HTMLElement> & Record<string, unknown>;
  getDragProps: (id: string) => React.HTMLAttributes<HTMLElement> & { draggable: boolean };
  dragClass: (id: string) => string;
  isPinned: (id: string) => boolean;
  onTogglePin: (id: string) => void;
  onHide: (id: string) => void;
  /** The grain in force: the cover column reads WOS (Weekly) or MOS (Monthly). */
  grain?: Grain;
}

export const ReportTableHeader: React.FC<ReportTableHeaderProps> = ({
  labelColumn, columns, sort, onSort, activeMenu, setActiveMenu, widthOf, isCapped = () => false, isResizing, getResizeHandleProps, getDragProps, dragClass, isPinned, onTogglePin, onHide, grain,
}) => {
  const close = () => setActiveMenu(null);
  const toggle = (id: string) => setActiveMenu(activeMenu === id ? null : id);
  // aria-sort on the sortable column header: the direction when it is the sorted column, else 'none'.
  const ariaSortFor = (id: string): 'ascending' | 'descending' | 'none' => (sort?.column === id ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none');
  const sortFor = (id: string) => (direction: SortDirection) => onSort({ column: id, direction });
  const labelWidth = widthOf(labelColumn.id);
  return (
    <thead className="sticky top-0 z-20 bg-[#F9F9FB]">
      <tr className={HEADER_ROW_CSS}>
        <th
          scope="col"
          aria-sort={ariaSortFor(labelColumn.id)}
          style={{ width: labelWidth, minWidth: labelWidth }}
          className={cn('sticky left-0 top-0 bg-[#F9F9FB] hover:z-50 px-[12px] py-2.5 border-r border-[#E4E4E7] shadow-[1px_0_0_0_#EAECF0] text-left', activeMenu === labelColumn.id ? 'z-50' : 'z-30')}
        >
          <HeaderCell
            column={labelColumn}
            sort={sort}
            isMenuOpen={activeMenu === labelColumn.id}
            onToggleMenu={() => toggle(labelColumn.id)}
            isLast={false}
            menu={<ColumnHeaderMenu title={labelColumn.label} description={labelColumn.description} sortType="text" activeDirection={sort?.column === labelColumn.id ? sort.direction : null} onSort={sortFor(labelColumn.id)} onClose={close} />}
          />
          <span {...getResizeHandleProps(labelColumn.id, labelColumn.label)} className={COLUMN_RESIZE_HANDLE_CLASS} />
        </th>
        {columns.map((column, idx) => {
          const isMenuOpen = activeMenu === column.id;
          const isLast = idx >= columns.length - 2;
          const pinned = isPinned(column.id);
          const width = widthOf(column.id);
          const capped = isCapped(column.id);
          return (
            <th
              key={column.id}
              scope="col"
              aria-sort={ariaSortFor(column.id)}
              {...getDragProps(column.id)}
              draggable={!isResizing && !pinned && !isMenuOpen}
              style={capped ? { width, minWidth: width, maxWidth: width } : { width, minWidth: width }}
              className={cn('px-[10px] py-2.5 sticky top-0 bg-[#F9F9FB] hover:z-50', isMenuOpen ? 'z-50' : 'z-20', TEXT_ALIGN[column.align], !pinned && 'cursor-grab', dragClass(column.id))}
            >
              <HeaderCell
                column={column}
                grain={grain}
                sort={sort}
                isMenuOpen={isMenuOpen}
                onToggleMenu={() => toggle(column.id)}
                isLast={isLast}
                capped={capped}
                menu={
                  <ColumnHeaderMenu
                    title={columnLabel(column, grain)}
                    description={column.description}
                    sortType={column.kind === 'text' ? 'text' : 'numeric'}
                    activeDirection={sort?.column === column.id ? sort.direction : null}
                    onSort={sortFor(column.id)}
                    pin={{ isPinned: pinned, onToggle: () => onTogglePin(column.id) }}
                    onHide={() => onHide(column.id)}
                    onClose={close}
                    align={isLast ? 'right' : 'left'}
                  />
                }
              />
              <span {...getResizeHandleProps(column.id, columnShortLabel(column, grain))} className={COLUMN_RESIZE_HANDLE_CLASS} />
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

// a column's DEFAULT width follows its data, not its header. The default is the widest formatted cell value among the
// rows shown (the first SAMPLE_ROWS of them), clamped to MIN_AUTO_WIDTH..MAX_AUTO_WIDTH. Headers wrap onto two or three lines, so the
// header only sets a floor: its longest single word (+ the info and sort icons), never the whole label. "OTB + Dev" is three short
// words, so it no longer holds a wide column open. A width the user resized and saved always wins over this (ReportTable overlays
// `columnState.widths` on top), and a column with no data to sample keeps the catalogue's own default.
//
// The figures are estimates (no layout is measured, so this is pure and testable): the 13px tabular figures run about 7.4px each, the
// 12px semibold header words about 7px each; cells are `whitespace-nowrap`, so an under-estimate only means the browser grows the
// column to fit, never that text is cut.

export const MIN_AUTO_WIDTH = 56;
export const MAX_AUTO_WIDTH = 260;
export const SAMPLE_ROWS = 60;
const CELL_CHAR_PX = 7.4;
const BOLD_FACTOR = 1.07; // group / subtotal rows are semibold
const CELL_PAD_PX = 20; // px-[10px] each side
const MARKER_PX = 20; // a status pill's padding, the "est." / WTD tags' gap
const HEADER_CHAR_PX = 7;
const HEADER_ICONS_PX = 36; // the info icon and the sort icon with their gaps

const cellText = (column: MetricDefinition, flatRow: FlatRow, ctx: CellContext): { text: string; extra: number } | null => {
  if (flatRow.kind === 'skuPlaceholder') return null;
  try {
    const v = column.cell(flatRow.row, { ...ctx, pctOfParent: flatRow.pctOfParent });
    const tag = v.tag ? v.tag.length * 6 + 6 : 0;
    const pill = column.kind === 'status' || column.kind === 'action' ? MARKER_PX : 0;
    return { text: v.text ?? '', extra: tag + pill };
  } catch {
    return null;
  }
};

/** The header's floor: its longest word (headers wrap) plus the icons and padding. */
export const headerFloorWidth = (column: MetricDefinition, grain: CellContext['grain']): number => {
  const words = columnShortLabel(column, grain).split(/\s+/).filter(Boolean);
  const longest = words.reduce((m, w) => Math.max(m, w.length), 0);
  return Math.ceil(longest * HEADER_CHAR_PX + HEADER_ICONS_PX + CELL_PAD_PX);
};

/** The column's default width from the rows shown, or null when there is nothing to sample (the caller keeps the catalogue default). */
export const dataColumnWidth = (column: MetricDefinition, flatRows: readonly FlatRow[], ctx: CellContext): number | null => {
  let widest = 0;
  let sampled = 0;
  for (const flatRow of flatRows) {
    if (sampled >= SAMPLE_ROWS) break;
    const c = cellText(column, flatRow, ctx);
    if (!c) continue;
    sampled += 1;
    const bold = flatRow.kind === 'group' || flatRow.kind === 'subtotal' ? BOLD_FACTOR : 1;
    widest = Math.max(widest, c.text.length * CELL_CHAR_PX * bold + c.extra);
  }
  if (sampled === 0) return null;
  const width = Math.max(widest + CELL_PAD_PX, headerFloorWidth(column, ctx.grain));
  return Math.round(Math.min(MAX_AUTO_WIDTH, Math.max(MIN_AUTO_WIDTH, width)));
};

/** Default widths for the columns (id → px) from the rows shown; a column with no data to sample is left out. */
export const dataColumnWidths = (columns: readonly MetricDefinition[], flatRows: readonly FlatRow[], ctx: CellContext): Record<string, number> => {
  const out: Record<string, number> = {};
  columns.forEach((c) => {
    if (c.id === 'label') return;
    const w = dataColumnWidth(c, flatRows, ctx);
    if (w !== null) out[c.id] = w;
  });
  return out;
};

// The grouped report table (Plan 3 spec 6 "Table"): sticky header and first column, a
// header menu per column (sort / pin / hide through ColumnHeaderMenu), resize and drag-reorder
// through the shared hooks, and the row kinds / markers from ReportTableRow and cellRenderers.
// Shell classes copy Vendor Performance (VP L3732-3736).
//
// Column state is CONTROLLED: `columnState` is the truth and every change goes out through
// `onColumnState` (persists it in the view state). The shared hooks therefore get a `null`
// storage key (they never touch localStorage) and are used for the interaction only:
// - useResizableColumns: `defaultWidths` = catalogue defaults overlaid with `columnState.widths`,
// so the hook's own state only ever holds the width of an interaction in progress. When one
// ends (never mid-drag) the merged widths go out through `onColumnState` and the hook is reset
// at once, so it retains nothing: a caller reset (`widths: {}`) or a loaded saved view simply
// shows. A double-click reset drops the key from the caller's widths.
// - useDraggableColumnOrder: the hook's default order is `columnState.order` normalised against
// the catalogue (ids missing from the caller's order appended after their catalogue
// predecessor, so every column is draggable), memoised on contents; a drop is reported as that
// full order when the hook's order diverges from it. The header renders from the normalised
// caller order, never the hook's, so a pin / unpin has no stale frame.
// - Pinning uses pinColumn / unpinColumn directly on the normalised order; usePinnedColumns owns
// its own state and would fight a controlled `pinned` list.
// The `label` column is always first and sticky; `order` lists every column (hidden ones too).
// Row detail (`renderRowDetail`) is the one piece of table-local state: which rows' details are
// open, keyed by FlatRow.id; only leaf and group rows get a toggle (DETAIL_KINDS).

export interface ReportColumnState {
  order: string[];
  /** Missing ids count as visible. */
  visible: Record<string, boolean>;
  widths: Record<string, number>;
  pinned: string[];
}

export interface ReportTableProps {
  flatRows: FlatRow[];
  columns: MetricDefinition[];
  columnState: ReportColumnState;
  onColumnState: (next: ReportColumnState) => void;
  sort: RowTreeSort | null;
  onSort: (sort: RowTreeSort) => void;
  ctx: CellContext;
  onToggleGroup: (key: string) => void;
  onExpandSkus: (parentKey: string) => void;
  renderRowLabel?: (flatRow: FlatRow) => React.ReactNode;
  stickyLabelWidth?: number;
  footer?: React.ReactNode;
  /** Height class for the scroll container; VP's `max-h-[calc(100vh-220px)]` by default. */
  maxHeightClass?: string;
  /**
   * Expanded-row detail (spec 7.1 / 7.3). When given, leaf and group rows get a detail toggle after
   * the label; an open row renders one full-width row below it with the returned node (null → none).
   * Open details are held here, keyed by `flatRow.id`.
   */
  renderRowDetail?: (flatRow: FlatRow) => React.ReactNode | null;
}

export const LABEL_COLUMN_ID = 'label';
export const FALLBACK_COLUMN_WIDTH = 140;
export const DEFAULT_LABEL_WIDTH = 280;
const FALLBACK_LABEL: MetricDefinition = {
  id: LABEL_COLUMN_ID, label: 'Class', shortLabel: 'Class', description: 'Class, vendor or SKU the row describes.', kind: 'text', align: 'left', defaultWidth: DEFAULT_LABEL_WIDTH,
  tiers: ['A', 'B', 'C'], reports: ['inventory_health', 'plan_otb', 'weekly_action'], defaultVisible: true, group: 'identity', cell: () => ({ value: null, text: '', flags: { estimated: false, allocated: false, projected: false, lyPartial: false } }),
};

/** Row kinds that can show a detail row (placeholders, Unallocated, Unclassified and subtotals cannot). */
export const DETAIL_KINDS: readonly FlatRow['kind'][] = ['leaf', 'group'];

const sameWidths = (a: Record<string, number>, b: Record<string, number>): boolean => {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
};

/** Visible metric columns in display order: pinned first (in pin order), then `order`; `label` excluded. */
export const orderedVisibleColumns = (columns: MetricDefinition[], state: ReportColumnState): MetricDefinition[] => {
  const byId = new Map(columns.map((c) => [c.id, c]));
  const seen = new Set<string>();
  const ids = [...state.pinned, ...state.order, ...columns.map((c) => c.id)].filter((id) => {
    if (seen.has(id) || id === LABEL_COLUMN_ID || !byId.has(id) || state.visible[id] === false) return false;
    seen.add(id);
    return true;
  });
  return ids.map((id) => byId.get(id) as MetricDefinition);
};

export const ReportTable: React.FC<ReportTableProps> = ({
  flatRows, columns, columnState, onColumnState, sort, onSort, ctx, onToggleGroup, onExpandSkus, renderRowLabel, stickyLabelWidth, footer, maxHeightClass = 'max-h-[calc(100vh-220px)]', renderRowDetail,
}) => {
  const labelColumn = columns.find((c) => c.id === LABEL_COLUMN_ID) ?? FALLBACK_LABEL;
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [openDetails, setOpenDetails] = useState<Record<string, boolean>>({});
  const toggleDetail = useCallback((id: string) => setOpenDetails((prev) => ({ ...prev, [id]: !prev[id] })), []);
  const detailIdBase = useId();
  // Open details only for rows still in the table: a row that leaves (filtered, collapsed, re-queried)
  // comes back closed, and the map never grows past the rows shown.
  useEffect(() => {
    setOpenDetails((prev) => {
      const present = new Set(flatRows.map((f) => f.id));
      const kept = Object.keys(prev).filter((id) => prev[id] && present.has(id));
      if (kept.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(kept.map((id) => [id, true]));
    });
  }, [flatRows]);
  // The scroll container's width, so a detail block pinned to the left edge never runs past it.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollWidth = useElementWidth(scrollRef);
  const stateRef = useRef(columnState);
  stateRef.current = columnState;

  // ---- widths --------------------------------------------------------------------------------
  const defaultWidths = useMemo(() => {
    const out: Record<string, number> = {};
    // a column's default follows its data (the widest formatted cell shown, clamped), the catalogue's own default only when there is nothing to sample.
    const fromData = dataColumnWidths(columns, flatRows, ctx);
    columns.forEach((c) => { out[c.id] = fromData[c.id] ?? c.defaultWidth; });
    // The catalogue's own defaults (the Class column's is 280); a caller's stickyLabelWidth beats the catalogue, and a width the user saved wins over both.
    out[LABEL_COLUMN_ID] = stickyLabelWidth ?? labelColumn.defaultWidth;
    return { ...out, ...columnState.widths };
  }, [columns, flatRows, ctx, columnState.widths, stickyLabelWidth, labelColumn]);
  const resize = useReportColumnResize(defaultWidths, FALLBACK_COLUMN_WIDTH);
  // column review: a column the user sized (saved in the view, or being dragged) is held to that width, its text cut with an
  // ellipsis, so it can go narrower than its content. Default widths are estimates and keep growing to fit, so no default ever cuts text.
  const isCapped = (id: string): boolean => id in columnState.widths || id in resize.columnWidths;
  const resetWidths = useRef(resize.resetColumnWidths);
  resetWidths.current = resize.resetColumnWidths;
  const getResizeHandleProps = (id: string, label: string) => {
    const props = resize.getResizeHandleProps(id, label);
    return {
      ...props,
      onDoubleClick: (e: React.MouseEvent<HTMLElement>) => {
        props.onDoubleClick(e);
        const { [id]: _dropped, ...rest } = stateRef.current.widths;
        if (id in stateRef.current.widths) onColumnState({ ...stateRef.current, widths: rest });
      },
    };
  };
  useEffect(() => {
    if (resize.isResizing || Object.keys(resize.columnWidths).length === 0) return;
    const merged = { ...stateRef.current.widths, ...resize.columnWidths };
    if (!sameWidths(merged, stateRef.current.widths)) onColumnState({ ...stateRef.current, widths: merged });
    resetWidths.current();
  }, [resize.columnWidths, resize.isResizing, onColumnState]);

  // ---- order -----------------------------------------------------------------------------------
  const catalogueIds = useMemo(() => columns.map((c) => c.id), [columns]);
  const orderKey = columnState.order.join('\u0000');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- orderKey is the stable string form of columnState.order (the array's identity changes on every state write)
  const fullOrder = useMemo(() => normalizeColumnOrder(columnState.order, catalogueIds), [orderKey, catalogueIds]);
  const drag = useDraggableColumnOrder(fullOrder, null);
  // Report the hook's order only after a drop. When the caller changes the order itself (pin,
  // Columns menu, reset, a saved view, a different column list) the hook re-seeds from `fullOrder`
  // one render later; emitting its stale order in between would swap the two forever.
  const seededWith = useRef(fullOrder);
  useEffect(() => {
    if (seededWith.current !== fullOrder) {
      seededWith.current = fullOrder;
      return;
    }
    if (drag.columnOrder.join('\u0000') !== fullOrder.join('\u0000')) onColumnState({ ...stateRef.current, order: drag.columnOrder });
  }, [drag.columnOrder, fullOrder, onColumnState]);

  // ---- pin / hide (the label column counts as always pinned, so pins land right after it) ------
  const isPinned = (id: string) => columnState.pinned.includes(id);
  const onTogglePin = (id: string) => {
    const s = stateRef.current;
    const order = normalizeColumnOrder(s.order, catalogueIds);
    if (isPinned(id)) {
      const pinned = s.pinned.filter((k) => k !== id);
      onColumnState({ ...s, pinned, order: unpinColumn(order, id, [LABEL_COLUMN_ID, ...pinned], catalogueIds) });
    } else {
      onColumnState({ ...s, pinned: [...s.pinned, id], order: pinColumn(order, id, [LABEL_COLUMN_ID, ...s.pinned]) });
    }
  };
  const onHide = (id: string) => onColumnState({ ...stateRef.current, visible: { ...stateRef.current.visible, [id]: false } });

  // Outside mousedown closes the open header menu (VP L1917-1934).
  useEffect(() => {
    if (!activeMenu) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement | null)?.closest('[data-column-menu]')) setActiveMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [activeMenu]);

  const visibleColumns = useMemo(() => orderedVisibleColumns(columns, { ...columnState, order: fullOrder }), [columns, columnState, fullOrder]);

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-2xl shadow-sm overflow-hidden relative">
      <div ref={scrollRef} className={`overflow-x-auto overflow-y-auto ${maxHeightClass}`}>
        <table className="w-full text-left text-[13px] border-collapse">
          <ReportTableHeader
            labelColumn={labelColumn}
            columns={visibleColumns}
            sort={sort}
            onSort={onSort}
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
            widthOf={resize.widthOf} isCapped={isCapped}
            isResizing={resize.isResizing}
            getResizeHandleProps={getResizeHandleProps}
            getDragProps={(id) => drag.getDragProps(id, 'x')}
            dragClass={(id) => drag.dragClass(id, 'x')}
            isPinned={isPinned}
            onTogglePin={onTogglePin}
            onHide={onHide}
            grain={ctx.grain}
          />
          <tbody>
            {flatRows.map((flatRow) => {
              const hasDetail = !!renderRowDetail && DETAIL_KINDS.includes(flatRow.kind);
              const open = hasDetail && !!openDetails[flatRow.id];
              const detail = open ? renderRowDetail!(flatRow) : null;
              const detailId = `${detailIdBase}-detail-${flatRow.id}`;
              return (
                <React.Fragment key={flatRow.id}>
                  <ReportTableRow
                    flatRow={flatRow}
                    columns={visibleColumns}
                    ctx={ctx}
                    widthOf={resize.widthOf} isCapped={isCapped}
                    onToggleGroup={onToggleGroup}
                    onExpandSkus={onExpandSkus}
                    renderRowLabel={renderRowLabel}
                    detail={hasDetail ? { open, controlsId: detailId, onToggle: () => toggleDetail(flatRow.id) } : undefined}
                  />
                  {detail != null && detail !== false && (
                    <tr id={detailId} data-row-detail={flatRow.id} className="border-t border-t-[#D0D5DD] border-b border-b-[#D0D5DD]">
                      {/* Tinted with a left accent, so the detail reads as belonging to the row above it, not the header below. */}
                      <td colSpan={visibleColumns.length + 1} className="bg-[#F5F3FF] border-l-4 border-l-[#7F56D9] px-6 py-4">
                        {/* Pinned to the left edge and no wider than the visible area, so it stays in view when the table is scrolled right. */}
                        <div className="sticky left-0" style={scrollWidth > 48 ? { maxWidth: scrollWidth - 48 } : undefined}>{detail}</div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {footer && <tfoot className="border-t-2 border-[#D0D5DD] bg-[#F8F9FA] font-bold">{footer}</tfoot>}
        </table>
      </div>
    </div>
  );
};

// The "VISIBLE COLUMNS" popover from Vendor Performance (VP L3584-3668): one row per column with
// a checkbox, name and description, plus ↑ / ↓ reorder buttons, and Reset. the list is grouped into sections (Sales,
// Inventory & cover, Plan, Inflows, OTB, Peers, % of Total), each with its rarely used columns folded under a collapsed "More columns"
// (it opens by itself when one of them is on). Which columns are shown by default is the catalogue's, not this menu's. Controlled: `visible` / `order` are the caller's (keeps them
// in the view state); drag-reorder stays on the table header. The `label` column is always shown
// and is not listed. Pinned columns are listed first and locked (pin icon, arrows disabled), as the
// header shows them; unpinning is the header menu's job. column review: a search box filters the list by name or description
// (sections and "More columns" open to show every match), and each description is clamped to two lines; a longer one shows "More", and
// clicking it (or the text) expands it without toggling the column.

export interface ColumnsMenuProps {
  columns: MetricDefinition[];
  /** Missing ids count as visible. */
  visible: Record<string, boolean>;
  order: string[];
  onChange: (next: { visible: Record<string, boolean>; order: string[] }) => void;
  onReset: () => void;
  /** Columns pinned to the front (ReportColumnState.pinned); listed first and locked. */
  pinned?: string[];
  /** The grain in force, so the cover column reads WOS / MOS as in the table header. */
  grain?: Grain;
}

const MENU_LABEL_COLUMN_ID = 'label';

/** Swaps `id` with its neighbour among `sectionIds` inside the full `order`. */
export const moveWithinSection = (order: string[], sectionIds: string[], id: string, direction: 'up' | 'down'): string[] => {
  const at = sectionIds.indexOf(id);
  const neighbour = sectionIds[direction === 'up' ? at - 1 : at + 1];
  if (at < 0 || neighbour === undefined) return order;
  const a = order.indexOf(id);
  const b = order.indexOf(neighbour);
  if (a < 0 || b < 0) return order;
  const next = order.slice();
  next[a] = neighbour;
  next[b] = id;
  return next;
};

/** The menu's sections, in display order. */
export type ColumnSection = 'sales' | 'inventory' | 'plan' | 'inflows' | 'otb' | 'peers' | 'pct' | 'other';
export const COLUMN_SECTIONS: readonly { key: ColumnSection; label: string }[] = [
  { key: 'sales', label: 'Sales' },
  { key: 'inventory', label: 'Inventory & cover' },
  { key: 'plan', label: 'Plan' },
  { key: 'inflows', label: 'Inflows' },
  { key: 'otb', label: 'OTB' },
  { key: 'peers', label: 'Peers' },
  { key: 'pct', label: '% of Total' },
  { key: 'other', label: 'Other' },
];

const PLAN_IDS = new Set(['salesPlan', 'inventoryPlan', 'markdownsPlan']);
const INFLOW_IDS = new Set(['inflow', 'inflowPlan', 'inflowVsCompare']);
const INVENTORY_IDS = new Set(['health', 'action', 'cover', 'suggestedReorder']);
/** Columns most buyers never turn on: folded under "More columns" in their section. */
const MORE_IDS = new Set([
  'trendPct', 'salesLy', 'inventoryLy', 'inventoryVsPlan', 'freshPctSku', 'aging4w', 'aging8w', 'aging12w', 'inflowVsCompare', 'pctOfInflow', 'pctOfOnOrder',
]);

/** The section a column is listed in. */
export const sectionOf = (c: MetricDefinition): ColumnSection => {
  if (c.group === 'pctOfTotal') return 'pct';
  if (c.group === 'benchmark') return 'peers';
  if (PLAN_IDS.has(c.id)) return 'plan';
  if (INFLOW_IDS.has(c.id) || c.group === 'inflow') return 'inflows';
  if (INVENTORY_IDS.has(c.id) || c.group === 'health' || c.group === 'inventory' || c.group === 'freshness') return 'inventory';
  if (c.group === 'otb') return 'otb';
  if (c.group === 'sales' || c.group === 'lastYear') return 'sales';
  return 'other';
};

/** Rarely used: the per-period OTB slots, the peer index columns, and the curated ids above. */
export const isMoreColumn = (c: MetricDefinition): boolean =>
  MORE_IDS.has(c.id) || otbPeriodSlot(c.id) !== null || (c.group === 'benchmark' && (c.id.endsWith('_index') || c.id === 'bm_salesChangeLy_diff'));

/** A column matches the search when its name or description contains the text (case-insensitive). */
export const columnMatches = (c: MetricDefinition, name: string, query: string): boolean => {
  const q = query.trim().toLowerCase();
  return q === '' || name.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q);
};

/** A column's description, two lines at most; "More" / "Less" when it runs longer (measured, so a short one has no button). */
const ColumnDescription: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !expanded) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);
  const expandable = overflows || expanded;
  const flip = (e: React.MouseEvent) => {
    if (!expandable) return; // a short description still toggles the column, like the name
    e.stopPropagation();
    setExpanded((x) => !x);
  };
  return (
    <span className="flex flex-col items-start mt-0.5">
      <span ref={ref} onClick={flip} title={expandable && !expanded ? 'Click to expand' : undefined} className={cn('text-[11px] text-[#667085] leading-snug', !expanded && 'line-clamp-2', expandable && 'cursor-pointer')}>
        {text}
      </span>
      {expandable && (
        <button type="button" aria-expanded={expanded} onClick={flip} className="text-[11px] font-semibold text-[#7F56D9] hover:text-[#6941C6]">
          {expanded ? 'Less' : 'More'}
        </button>
      )}
    </span>
  );
};

export const ColumnsMenu: React.FC<ColumnsMenuProps> = ({ columns, visible, order, onChange, onReset, pinned = [], grain }) => {
  const [isOpen, setIsOpen] = useState(false);
  const byId = new Map(columns.map((c) => [c.id, c]));
  const ordered = [...order, ...columns.map((c) => c.id)].filter((id, i, all) => id !== MENU_LABEL_COLUMN_ID && byId.has(id) && all.indexOf(id) === i).map((id) => byId.get(id) as MetricDefinition);
  const isLocked = (id: string) => pinned.includes(id);
  const unpinned = ordered.filter((c) => !isLocked(c.id));
  const locked = pinned.map((id) => byId.get(id)).filter((c): c is MetricDefinition => !!c && c.id !== MENU_LABEL_COLUMN_ID);
  const [moreOpen, setMoreOpen] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');
  const searching = query.trim() !== '';
  const matches = (c: MetricDefinition) => columnMatches(c, columnLabel(c, grain), query);
  const isVisible = (id: string) => visible[id] !== false;
  const toggle = (id: string) => onChange({ visible: { ...visible, [id]: !isVisible(id) }, order });
  const move = (section: MetricDefinition[], id: string, direction: 'up' | 'down') => onChange({ visible, order: moveWithinSection(order, section.map((c) => c.id), id, direction) });
  const shown = ordered.filter((c) => isVisible(c.id)).length;

  const item = (section: MetricDefinition[], c: MetricDefinition) => {
    const idx = section.indexOf(c);
    const checked = isVisible(c.id);
    const lock = isLocked(c.id);
    const name = columnLabel(c, grain);
    return (
      <div key={c.id} className="flex items-start gap-2 rounded-[6px]">
        {lock && <Pin className="w-3.5 h-3.5 mt-0.5 text-[#7C3AED] shrink-0" aria-label="Pinned" />}
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          aria-label={name}
          onClick={() => toggle(c.id)}
          className={cn('w-4 h-4 rounded mt-0.5 flex items-center justify-center transition-colors shrink-0', checked ? 'bg-[#7F56D9] text-white' : 'bg-white border border-[#D0D5DD] hover:border-[#7F56D9]')}
        >
          {checked && <Check className="w-3 h-3 stroke-[3]" aria-hidden="true" />}
        </button>
        <div className="flex flex-col min-w-0 flex-1 cursor-pointer" onClick={() => toggle(c.id)}>
          <span className="text-[13px] font-bold text-[#101828] leading-tight">{name}</span>
          {c.description && <ColumnDescription text={c.description} />}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button type="button" aria-label={`Move ${name} up`} disabled={lock || idx === 0} onClick={() => move(section, c.id, 'up')} className="p-1 rounded text-[#667085] hover:bg-[#F2F4F7] disabled:opacity-30 disabled:hover:bg-transparent">
            <ArrowUp className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <button type="button" aria-label={`Move ${name} down`} disabled={lock || idx === section.length - 1} onClick={() => move(section, c.id, 'down')} className="p-1 rounded text-[#667085] hover:bg-[#F2F4F7] disabled:opacity-30 disabled:hover:bg-transparent">
            <ArrowDown className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <Popover label="Columns" icon={<SlidersHorizontal className="w-4 h-4 text-[#667085]" />} isOpen={isOpen} onOpenChange={setIsOpen} align="right" width="w-80" hideCaption badge={shown}>
      <div className="text-xs font-bold uppercase tracking-wider text-[#101828] border-b border-[#EAECF0] pb-2 flex items-center justify-between">
        <span>Visible columns</span>
        <button type="button" aria-label="Close" onClick={() => setIsOpen(false)} className="text-xs text-[#667085] hover:text-[#101828]">✕</button>
      </div>
      <label className="relative flex items-center">
        <Search className="absolute left-2.5 w-3.5 h-3.5 text-[#98A2B3]" aria-hidden="true" />
        <input
          type="search"
          aria-label="Search columns"
          placeholder="Search columns"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full h-8 pl-8 pr-2 rounded-[8px] border border-[#D0D5DD] text-[13px] text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[#7F56D9]/30 focus:border-[#7F56D9]"
        />
      </label>
      <div className="flex flex-col gap-3.5 max-h-[380px] overflow-y-auto pr-1">
        {locked.filter(matches).map((c) => item(locked, c))}
        {searching && !ordered.some(matches) && <span className="text-[12px] text-[#667085]">No columns match "{query.trim()}".</span>}
        {COLUMN_SECTIONS.map(({ key, label }, n) => {
          const inSection = unpinned.filter((c) => sectionOf(c) === key);
          if (inSection.length === 0) return null;
          const main = inSection.filter((c) => !isMoreColumn(c));
          const more = inSection.filter(isMoreColumn);
          const open = searching || (moreOpen[key] ?? more.some((c) => isVisible(c.id)));
          const mainShown = main.filter(matches);
          const moreShown = more.filter(matches);
          if (searching && mainShown.length + moreShown.length === 0) return null;
          return (
            <div key={key} role="group" aria-label={label} className={cn('flex flex-col gap-3.5', (n > 0 || locked.length > 0) && 'border-t border-[#EAECF0] pt-3')}>
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#667085]">{label}</span>
              {key === 'peers' && !searching && <span className="text-[11px] text-[#667085] leading-snug">Peer values and indices; class rows vs peers in the same master class, location rows vs the vertical.</span>}
              {mainShown.map((c) => item(main, c))}
              {searching ? moreShown.map((c) => item(more, c)) : more.length > 0 && (
                <>
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setMoreOpen((m) => ({ ...m, [key]: !open }))}
                    className="self-start inline-flex items-center gap-1 text-[12px] font-semibold text-[#667085] hover:text-[#101828]"
                  >
                    {open ? <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" /> : <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />}
                    More columns ({more.length})
                  </button>
                  {open && more.map((c) => item(more, c))}
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="border-t border-[#EAECF0] pt-2 text-[11px] text-[#98A2B3] flex items-start justify-between gap-3">
        <span>Use the arrows or drag column headers to reorder. Drag a header's right edge to resize. Saved with the view.</span>
        <button type="button" onClick={onReset} className="shrink-0 font-semibold text-[#7F56D9] hover:text-[#6941C6]">Reset columns</button>
      </div>
    </Popover>
  );
};

// ===================================================================================================================
// ==== Summary.tsx ===================================================================================================
// ===================================================================================================================
// The KPI cards under the filter chips (spec 7.1–7.3 "KPIs"): a responsive row of white cards in the
// Vendor Performance card style. The value is always strong and dark; `tone` colours the sub line
// (the "+4.2% vs plan" / "classes" caption) so a red number never competes with a red caption.
// a KPI that maps to a benchmark metric carries a `benchmark` line under the sub line ("peers 20.0 ·
// you 14.8 in Sep 2026 (index 0.74)", toned by the index; the tooltip names the peer count and pool, through the
// same keyboard-reachable InfoTooltip as the label's). The
// report config builds it from `ctx.benchmarks.client` (never from SKU / product rows), so it never changes
// as those rows load; when the benchmark is unpublishable the line says "not enough peers" instead.
// the row NEVER wraps. Every card is one line per field (label, value, sub, benchmark truncate, the full text in a native
// title), the row measures its width and shows as many cards as fit at MIN_CARD_PX; the rest sit behind a "More (n)" button that opens
// them as a stacked list. (A sideways-scrolling row would clip the cards' tooltips, so overflow goes to a popover instead.) Before the row
// has a width (first paint, tests) every card is shown.

export type KpiTone = 'neutral' | 'good' | 'bad' | 'warn';

export interface KpiBenchmarkLine {
  text: string;
  tooltip?: string;
  tone?: KpiTone;
}

export interface KpiItem {
  label: string;
  value: string;
  sub?: string;
  tone?: KpiTone;
  tooltip?: string;
  /** the peer benchmark line under the sub line; absent = no line. */
  benchmark?: KpiBenchmarkLine;
  /** A KPI-level caveat (e.g. "Weekly plan covers through Nov 29–Dec 5; Monthly OTB uses the monthly plan"). it lives in the label's tooltip, never as wrapped text under the card; absent = none. */
  note?: string;
  /** A small switch at the right of the label (e.g. Weekly Action's OTB + Dev "Dev" include / exclude); absent = none. */
  toggle?: KpiToggle;
}

export interface KpiToggle {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export interface KpiRowProps {
  items: KpiItem[];
}

export const KPI_CARD_CSS = 'rounded-2xl border border-[#E4E4E7] bg-white p-4 flex flex-col gap-1 min-w-0';
const KPI_TONE_CSS: Record<KpiTone, string> = {
  neutral: 'text-[#71717A]',
  good: 'text-[#027A48]',
  bad: 'text-[#B42318]',
  warn: 'text-[#B54708]',
};

/** The narrowest a card may get before it moves behind "More", the gap between cards and the width reserved for the "More" button. */
export const KPI_MIN_CARD_PX = 176;
const GAP_PX = 12;
const MORE_PX = 96;

/** How many of `count` cards fit in `width`: all when they do, else as many as leave room for the More button (at least one). 0 width = not measured: all. */
export const kpiCapacity = (width: number, count: number): number => {
  if (width <= 0 || count <= 0) return count;
  const fitAll = Math.floor((width + GAP_PX) / (KPI_MIN_CARD_PX + GAP_PX));
  if (count <= fitAll) return count;
  return Math.max(1, Math.min(count - 1, Math.floor((width - MORE_PX) / (KPI_MIN_CARD_PX + GAP_PX))));
};

const KpiCard: React.FC<{ item: KpiItem }> = ({ item }) => {
  const tip = [item.tooltip, item.note].filter(Boolean).join(' ');
  return (
    <div data-testid="kpi-card" className={cn(KPI_CARD_CSS, 'flex-1')}>
      <div className="flex items-center gap-1.5 text-[12px] font-medium text-[#71717A] min-w-0">
        <span className="truncate" title={item.label}>{item.label}</span>
        {tip && <InfoTooltip text={tip} />}
        {item.toggle && (
          <span className="ml-auto shrink-0">
            <ToggleSwitch size="sm" label={item.toggle.label} checked={item.toggle.checked} onChange={item.toggle.onChange} />
          </span>
        )}
      </div>
      <div className="text-[22px] font-semibold text-[#101828] tabular-nums leading-7 truncate" title={item.value}>{item.value}</div>
      {item.sub && <div className={cn('text-[12px] font-medium truncate', KPI_TONE_CSS[item.tone ?? 'neutral'])} title={item.sub}>{item.sub}</div>}
      {item.benchmark && (
        <div data-testid="kpi-benchmark" className={cn('flex items-center gap-1.5 text-[11px] font-medium min-w-0', KPI_TONE_CSS[item.benchmark.tone ?? 'neutral'])}>
          <span className="truncate" title={item.benchmark.text}>{item.benchmark.text}</span>
          {item.benchmark.tooltip && <InfoTooltip text={item.benchmark.tooltip} />}
        </div>
      )}
    </div>
  );
};

const MoreCards: React.FC<{ items: KpiItem[] }> = ({ items }) => {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e: Event) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div ref={box} className="relative shrink-0" style={{ width: MORE_PX }}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        className="h-full min-h-[72px] w-full rounded-2xl border border-[#E4E4E7] bg-white px-3 text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
      >
        More ({items.length})
      </button>
      {open && (
        <div role="dialog" aria-label="More figures" className="absolute right-0 top-full z-50 mt-2 w-[280px] max-w-[90vw] flex flex-col gap-2 rounded-2xl border border-[#E4E4E7] bg-[#F9FAFB] p-2 shadow-xl">
          {items.map((item, i) => <KpiCard key={`${item.label}-${i}`} item={item} />)}
        </div>
      )}
    </div>
  );
};

export const KpiRow: React.FC<KpiRowProps> = ({ items }) => {
  const row = useRef<HTMLDivElement>(null);
  const width = useElementWidth(row);
  if (items.length === 0) return null;
  const shown = kpiCapacity(width, items.length);
  return (
    <div ref={row} data-testid="kpi-row" className="flex flex-nowrap items-stretch gap-3">
      {items.slice(0, shown).map((item, i) => (
        <div key={`${item.label}-${i}`} className="flex-1 min-w-0 flex flex-col">
          <KpiCard item={item} />
        </div>
      ))}
      {shown < items.length && <MoreCards items={items.slice(shown)} />}
    </div>
  );
};

//: the compact building blocks of the expanded-row panels (Weekly Action,
// Inventory Health), so a panel is about half its old height and scans in one pass:
// - `StatStrip`: one wrapping line of stats, a small label above each value; any long explanation (the OTB-through sentence, the
// trend basis, "plan target") sits in the stat's info tooltip, never in the strip itself. A short `sub` may follow the value.
// - `DetailCard`: a small bordered card with a heading and an optional info tooltip beside it (outside the heading, so the heading
// stays the table's accessible name).
// - `OtbByPeriodTable`: OTB by week / month in dense rows up to the plan end plus one "Later" row (kit/logic/rowDetail.ts); the
// running-OTB rule is the heading's tooltip.
// - `ShowAllRows`: a long table body shows its first rows and an expander for the rest.

export interface Stat { id: string; label: string; value: string; info?: string | null; sub?: string | null }

export const StatStrip: React.FC<{ stats: Stat[]; label: string }> = ({ stats, label }) => (
  <ul aria-label={label} className="flex flex-wrap items-start gap-x-6 gap-y-2">
    {stats.map((s) => (
      <li key={s.id} data-stat={s.id} className="min-w-0">
        <dl className="flex flex-col gap-0.5">
          <dt className="flex items-center gap-1 text-[11px] font-medium leading-4 text-[#71717A] whitespace-nowrap">
            {s.label}
            {s.info && <InfoTooltip text={s.info} widthClass="w-64" />}
          </dt>
          <dd className="text-[13px] font-semibold leading-5 text-[#101828] tabular-nums whitespace-nowrap">
            {s.value}
            {s.sub && <span className="ml-1.5 text-[11px] font-medium text-[#667085]">{s.sub}</span>}
          </dd>
        </dl>
      </li>
    ))}
  </ul>
);

export const DETAIL_CARD_CSS = 'rounded-xl border border-[#EAECF0] bg-white p-3 flex flex-col gap-1.5 min-w-0';

export const DetailCard: React.FC<{ title: React.ReactNode; headingId?: string; info?: string | null; className?: string; children: React.ReactNode; ariaLabel?: string }> = ({ title, headingId, info, className, children, ariaLabel }) => (
  <section aria-label={ariaLabel} className={cn(DETAIL_CARD_CSS, className)}>
    <div className="flex items-center gap-1">
      <h4 id={headingId} className="text-[12px] font-semibold leading-4 text-[#101828]">{title}</h4>
      {info && <InfoTooltip text={info} widthClass="w-64" />}
    </div>
    {children}
  </section>
);

/** Dense table classes shared by the detail tables. */
export const DENSE_TABLE_CSS = 'text-[11px] leading-4 tabular-nums';
export const DENSE_TH_CSS = 'font-medium text-[#71717A] pb-0.5';
export const DENSE_TD_CSS = 'py-px text-[#344054]';

const SHOW_ALL_LIMIT = 6;

/** The first `limit` rows of a long list and a "Show all N" / "Show fewer" row; a short list renders every row and no button. */
export function ShowAllRows<T>({ items, render, limit = SHOW_ALL_LIMIT, colSpan }: { items: T[]; render: (item: T, index: number) => React.ReactNode; limit?: number; colSpan: number }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const long = items.length > limit;
  const shown = long && !open ? items.slice(0, limit) : items;
  return (
    <>
      {shown.map(render)}
      {long && (
        <tr>
          <td colSpan={colSpan} className="pt-1">
            <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="text-[11px] font-semibold text-[#6941C6] hover:text-[#53389E]">
              {open ? 'Show fewer' : `Show all ${items.length}`}
            </button>
          </td>
        </tr>
      )}
    </>
  );
}

export const OtbByPeriodTable: React.FC<{ id: string; lines: OtbPeriodLine[]; grain: Grain; planEnd: number | null }> = ({ id, lines, grain, planEnd }) => {
  const noun = periodNoun(grain);
  const { shown, later } = trimOtbLines(lines, planEnd);
  const headingId = `otb-by-period-${id}`;
  const th = (text: string, left = false) => <th className={cn(DENSE_TH_CSS, left ? 'text-left pr-3' : 'text-right pl-3')}>{text}</th>;
  return (
    <DetailCard title={`OTB by ${noun}`} headingId={headingId} info={RUNNING_OTB_RULE}>
      <table aria-labelledby={headingId} className={DENSE_TABLE_CSS}>
        <thead>
          <tr>
            {th(noun === 'week' ? 'Week' : 'Month', true)}
            {th('Planned inflows')}
            {th('Received')}
            {th('On order')}
            {th('OTB')}
            {th('Running OTB')}
          </tr>
        </thead>
        <tbody>
          {shown.map((l) => (
            <tr key={l.period}>
              <td className={cn(DENSE_TD_CSS, 'text-left pr-3 whitespace-nowrap')}>{periodFullName(l.period, grain)}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtCurrency(l.inflowPlan)}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtCurrency(l.received)}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtCurrency(l.onOrderDue)}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtCurrency(l.otb)}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3 font-semibold text-[#101828]')}>{fmtCurrency(l.running)}</td>
            </tr>
          ))}
          {later && (
            <tr className="border-t border-[#EAECF0]">
              <td className={cn(DENSE_TD_CSS, 'text-left pr-3 whitespace-nowrap text-[#667085]')}>{`Later (${later.count} ${noun}${later.count === 1 ? '' : 's'})`}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtCurrency(later.inflowPlan)}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtCurrency(later.received)}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtCurrency(later.onOrderDue)}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtCurrency(later.otb)}</td>
              <td className={cn(DENSE_TD_CSS, 'text-right pl-3 font-semibold text-[#101828]')}>{fmtCurrency(later.running)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </DetailCard>
  );
};

// (M1 §8 families): the parent row detail names the child classes behind the family's figures — the top 3 by
// inventory with their share of the parent's inventory and their cover (the table's cover cell: a child judged at its
// parent shows the value alone). Renders nothing for a row with no child classes.


/**
 * Wendi (2026-10-22), item 7: the figures behind a product / SKU row's Suggested reorder (kit `reorderExplanation`): average units, the
 * target, the need, on hand, on order, available (where the row has an allocation), the units suggested and which rule sized them.
 * Nothing for a row that carries no suggestion (class, vendor rows).
 */
export const ReorderDetail: React.FC<{ row: AnyRow; ctx: CellContext; id: string }> = ({ row, ctx, id }) => {
  const e = reorderExplanation(row, ctx);
  if (!e) return null;
  const unit = ctx.grain === 'monthly' ? 'month' : 'week';
  const n = (v: number | null, digits = 0): string => (isNum(v) ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : EMPTY);
  const rows: [string, string][] = [
    [`Avg ${unit}ly units`, n(e.avgUnits, 1)],
    [`Target ${unit}s of stock`, n(e.target, 1)],
    ['Need (avg × target)', n(e.need)],
    [`On hand (${bomAbbr(ctx.grain)} units)`, n(e.onHand)],
    ['On order (units)', n(e.onOrder)],
    ...(e.available !== null ? [['Available at non-selling locations', fmtCurrency(e.available)] as [string, string]] : []),
    ['Suggested reorder', fmtUnits(e.suggested)],
  ];
  return (
    <DetailCard title="Suggested reorder" headingId={`reorder-${id}`} info="Units: need − on hand − on order, floored at 0, where need = average period units × the target weeks (or months) of stock.">
      <table aria-labelledby={`reorder-${id}`} className={DENSE_TABLE_CSS}>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}><td className={cn(DENSE_TD_CSS, 'text-left pr-3 whitespace-nowrap')}>{label}</td><td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{value}</td></tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] font-medium text-[#475467]" data-testid="reorder-rule">Rule: {e.ruleText}</p>
    </DetailCard>
  );
};

export interface FamilyDriversProps { row: AnyRow; rows: readonly AnyRow[]; ctx: CellContext; id: string }

export const FamilyDrivers: React.FC<FamilyDriversProps> = ({ row, rows, ctx, id }) => {
  const drivers = familyDrivers(row, rows, ctx);
  if (drivers.length === 0) return null;
  const unit = ctx.grain === 'weekly' ? 'WOS' : 'MOS';
  const headingId = `child-drivers-${id}`;
  const th = (text: string, left = false) => <th scope="col" className={cn(DENSE_TH_CSS, left ? 'text-left pr-3' : 'text-right pl-3')}>{text}</th>;
  return (
    <DetailCard title="Child drivers" headingId={headingId}>
      <table aria-labelledby={headingId} className={DENSE_TABLE_CSS}>
        <thead>
          <tr>{th('Class', true)}{th('Inventory')}{th('Share')}{th(unit)}</tr>
        </thead>
        <tbody>
          <ShowAllRows
            items={drivers}
            colSpan={4}
            render={(d) => (
              <tr key={d.row.key}>
                <td className={cn(DENSE_TD_CSS, 'text-left pr-3')}>{metricById('label').cell(d.row, ctx).text}</td>
                <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtCurrency(d.inventory)}</td>
                <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{fmtPct(d.share)}</td>
                <td className={cn(DENSE_TD_CSS, 'text-right pl-3')}>{metricById('cover').cell(d.row, ctx).text}</td>
              </tr>
            )}
          />
        </tbody>
      </table>
    </DetailCard>
  );
};

// The Needs Attention list on the dashboards: one row per AttentionItem with its status badge, label,
// reason line and the dollars at stake. Rows are buttons when `onSelect` is given (the page scrolls to
// or filters the table row), plain list items otherwise.

export interface NeedsAttentionListProps {
  items: AttentionItem[];
  onSelect?: (item: AttentionItem) => void;
  emptyMessage?: string;
  /** The report's grain: the Review title says 52 weeks in Weekly and 12 months in Monthly. */
  grain?: Grain | null;
}

const ATTENTION_ROW_CSS = 'w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left';

export const NeedsAttentionList: React.FC<NeedsAttentionListProps> = ({ items, onSelect, emptyMessage = 'Nothing needs attention.', grain }) => {
  if (items.length === 0) {
    return <div className="py-6 text-center text-[13px] font-medium text-[#71717A]">{emptyMessage}</div>;
  }
  return (
    <ul className="flex flex-col divide-y divide-[#F2F4F7]">
      {items.map((item) => {
        const content = (
          <>
            <Pill color={STATUS_COLORS[item.status]} className="shrink-0" title={item.status === 'review' ? reviewTooltip(grain) : undefined}>{STATUS_LABELS[item.status]}</Pill>
            <span className="min-w-0 flex-1 flex flex-col">
              <span className="text-[13px] font-semibold text-[#101828] truncate">{item.label}</span>
              <span className="text-[12px] font-medium text-[#71717A] truncate">{item.reason}</span>
            </span>
            {/* null = no cost figure on the cost basis. */}
            <span className="shrink-0 text-[13px] font-semibold text-[#344054] tabular-nums" title={item.dollars === null ? NOT_AT_COST_TOOLTIP : undefined}>{fmtCurrency(item.dollars)}</span>
          </>
        );
        return (
          <li key={item.key} data-severity={item.severity}>
            {onSelect ? (
              <button type="button" onClick={() => onSelect(item)} className={cn(ATTENTION_ROW_CSS, 'hover:bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#7F56D9]')}>
                {content}
              </button>
            ) : (
              <div className={ATTENTION_ROW_CSS}>{content}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

// The dashboard layout under the KPI row (spec 7.1–7.3 "Dashboard"): the report's chart cards in a
// two-column grid, with the Needs Attention card always in the last cell.

export interface DashboardViewProps {
  cards: React.ReactNode[];
  attention: AttentionItem[];
  onSelect?: (item: AttentionItem) => void;
  grain?: Grain | null;
}

export const DashboardView: React.FC<DashboardViewProps> = ({ cards, attention, onSelect, grain }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    {cards.map((card, i) => (
      <div key={i} className="min-w-0">{card}</div>
    ))}
    <div className="min-w-0 rounded-2xl border border-[#E4E4E7] bg-white p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h3 className="text-[14px] font-semibold text-[#101828]">Needs attention</h3>
        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#F2F4F7] text-[11px] font-semibold text-[#475467] tabular-nums">{attention.length}</span>
      </div>
      <NeedsAttentionList items={attention} onSelect={onSelect} grain={grain} />
    </div>
  </div>
);

// A dashboard chart in the report card style (spec 6 "Summary: charts have sort, hover detail and a
// bold plan line"): bars and lines on one recharts ComposedChart, a custom tooltip that formats every
// value with the caller's formatter, and a "Sort: Value / Name" control when `sortable`.
//
// recharts is imported directly (not through tailwindComponents/ui/chart.tsx) so the kit stays
// self-contained. The chart fills its card through ResponsiveContainer; passing `width` renders it at
// a fixed size instead, which is how the tests get an SVG under jsdom (no layout, no ResizeObserver).
//
// a `marker` series is the peers' value per class (G3), drawn as a diamond on the classes that
// have one and nothing (no line, no dot) where the datum is null — an unpublishable grain has no marker.
// Its legend entry is a diamond; the tooltip adds the datum's `peerNote` ("8 peers · Tops · Sep 2026").

export type ChartSeriesKind = 'bar' | 'line' | 'marker';
export type ChartSort = 'value' | 'name';

export interface ChartSeries {
  key: string;
  label: string;
  kind: ChartSeriesKind;
  color: string;
  /** The plan line: drawn 3px wide. */
  bold?: boolean;
}

export type ChartDatum = Record<string, string | number | null | undefined>;

export interface ChartCardProps {
  title: string;
  data: ChartDatum[];
  series: ChartSeries[];
  xKey: string;
  sortable?: boolean;
  onSort?: (sort: ChartSort) => void;
  formatValue: (value: number) => string;
  height?: number;
  /** Fixed pixel width; omit to fill the card (ResponsiveContainer). */
  width?: number;
  /** Shown under the title, e.g. "Target period · retail". */
  caption?: string;
  /** A visible note under the chart that wraps (why a series is empty, e.g. no class-level peers); absent = none. */
  note?: string;
  /**
   * Upper limit of the value axis (cover: WEEKS_CAP). Every series is clamped to it and the top tick reads
   * "<cap>+", once. Defaults to WEEKS_CAP when `formatValue` is `fmtWeeks`, so cover charts need no extra prop.
   */
  cap?: number;
  /** the small card of an expanded row (less padding, a 12px title, a one-line caption). */
  dense?: boolean;
}

export const CHART_CARD_CSS = 'rounded-2xl border border-[#E4E4E7] bg-white p-4 flex flex-col gap-3 min-w-0';
const CHART_CARD_DENSE_CSS = 'rounded-xl border border-[#EAECF0] bg-white p-3 flex flex-col gap-1.5 min-w-0';
const AXIS_TICK = { fontSize: 11, fill: '#71717A' };
const SORT_OPTIONS: { value: ChartSort; label: string }[] = [
  { value: 'value', label: 'Value' },
  { value: 'name', label: 'Name' },
];

const isNum = (v: unknown): v is number => typeof v === 'number' && !Number.isNaN(v);

/** By the first series' value desc (nulls last) or by the x key asc, locale-aware. */
export const sortChartData = (data: ChartDatum[], series: ChartSeries[], xKey: string, sort: ChartSort): ChartDatum[] => {
  const out = [...data];
  if (sort === 'name') return out.sort((a, b) => String(a[xKey] ?? '').localeCompare(String(b[xKey] ?? ''), undefined, { numeric: true, sensitivity: 'base' }));
  const key = series[0]?.key;
  if (!key) return out;
  return out.sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (!isNum(av) || !isNum(bv)) return isNum(av) ? -1 : isNum(bv) ? 1 : 0;
    return bv - av;
  });
};

/** Every numeric value in `data` clamped at `cap`, so a plan line cannot be drawn above the capped bars. */
export const capChartData = (data: ChartDatum[], cap: number): ChartDatum[] =>
  data.map((d) => Object.fromEntries(Object.entries(d).map(([k, v]) => [k, isNum(v) ? Math.min(v, cap) : v])));

/** Y ticks from 0 to `top` (at most `cap`): five evenly spaced whole numbers, each once. */
export const capAxisTicks = (top: number, cap: number): number[] => {
  const max = Math.min(Math.ceil(top), cap);
  return Array.from(new Set([0, 1, 2, 3, 4].map((i) => Math.round((max * i) / 4)).concat(max)));
};

export interface ChartTooltipProps {
  active?: boolean;
  label?: string | number;
  /** recharts hands each series entry its datum as `payload`; a `peerNote` string on it is shown as a note line. */
  payload?: { name?: string | number; dataKey?: string | number; value?: number | string | null; color?: string; payload?: Record<string, unknown> }[];
  formatValue: (value: number) => string;
}

/** The datum's own value for a series when the chart was drawn clamped (`__raw` holds the unclamped datum), else the plotted one. */
const tooltipValue = (p: NonNullable<ChartTooltipProps['payload']>[number], formatValue: (value: number) => string): string => {
  const raw = p.payload?.__raw as ChartDatum | undefined;
  const real = raw && p.dataKey !== undefined ? raw[p.dataKey] : undefined;
  const v = isNum(real) ? real : p.value;
  // fmtWeeks reads "52+" above the cap: the tooltip is where the real figure shows.
  return isNum(v) ? (formatValue === fmtWeeks ? fmtWeeksExact : formatValue)(v) : '—';
};

/** The hover detail: the x label, then one line per series with its colour swatch and formatted value, then the datum note. */
export const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, label, payload, formatValue }) => {
  if (!active || !payload || payload.length === 0) return null;
  // `peerNote` (the peers' basis) or `planNote` (D6: "Plan as it stood in Sep 2026 (V2)"), whichever the datum carries.
  const note = payload[0]?.payload?.peerNote || payload[0]?.payload?.planNote;
  return (
    <div className="rounded-lg bg-[#101828] px-2.5 py-2 text-[11px] font-medium text-white shadow-lg">
      <div className="mb-1 text-[#D0D5DD]">{label}</div>
      {payload.map((p, i) => (
        <div key={`${p.name}-${i}`} className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="tabular-nums">{tooltipValue(p, formatValue)}</span>
        </div>
      ))}
      {typeof note === 'string' && note !== '' && <div className="mt-1 text-[#D0D5DD]">{note}</div>}
    </div>
  );
};

/** A marker series' dot: a diamond where the datum has a value, nothing where it is null (recharts still calls `dot` there). */
const MarkerDot = (color: string) => (props: { cx?: number; cy?: number; value?: number | null; index?: number }): React.ReactElement<SVGElement> =>
  isNum(props.value) && isNum(props.cx) && isNum(props.cy) ? (
    <path key={`m-${props.index}`} data-peer-marker d={`M${props.cx},${props.cy - 5}L${props.cx + 5},${props.cy}L${props.cx},${props.cy + 5}L${props.cx - 5},${props.cy}Z`} fill={color} stroke="#fff" strokeWidth={1} />
  ) : (
    <g key={`m-${props.index}`} />
  );

export const ChartCard: React.FC<ChartCardProps> = ({ title, data, series, xKey, sortable = false, onSort, formatValue, height = 260, width, caption, note, cap: capProp, dense = false }) => {
  const [sort, setSort] = useState<ChartSort>('value');
  const cap = capProp ?? (formatValue === fmtWeeks ? WEEKS_CAP : undefined);
  const rows = useMemo(() => {
    const ordered = sortable ? sortChartData(data, series, xKey, sort) : data;
    // `__raw` keeps each unclamped datum for the tooltip; the axis and bars use the clamped values.
    return cap === undefined ? ordered : capChartData(ordered, cap).map((d, i) => ({ ...d, __raw: ordered[i] } as unknown as ChartDatum));
  }, [data, series, xKey, sort, sortable, cap]);
  const capTicks = useMemo(() => {
    if (cap === undefined) return undefined;
    const top = Math.max(1, ...rows.flatMap((r) => series.filter((s) => s.kind !== 'marker').map((s) => r[s.key]).filter(isNum)));
    return capAxisTicks(top, cap);
  }, [rows, series, cap]);
  // A marker series with no value anywhere (every grain unpublishable) is dropped, so the legend never promises
  // markers the chart does not draw.
  const drawn = useMemo(() => series.filter((s) => s.kind !== 'marker' || data.some((d) => isNum(d[s.key]))), [series, data]);

  const chart = (
    <ComposedChart data={rows} width={width} height={width !== undefined ? height : undefined} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
      <CartesianGrid vertical={false} stroke="#EAECF0" />
      <XAxis dataKey={xKey} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: '#EAECF0' }} interval="preserveStartEnd" />
      <YAxis
        tick={AXIS_TICK}
        tickLine={false}
        axisLine={false}
        width={56}
        {...(capTicks ? { ticks: capTicks, domain: [0, capTicks[capTicks.length - 1]] as [number, number], allowDataOverflow: true } : {})}
        tickFormatter={(v: number) => (cap !== undefined && v >= cap ? `${cap}+` : formatValue(v))}
      />
      <Tooltip content={<ChartTooltip formatValue={formatValue} />} cursor={{ fill: '#F4F4F5' }} />
      {drawn.length > 1 && <Legend iconType="square" iconSize={8} wrapperStyle={{ fontSize: 11, color: '#71717A' }} />}
      {drawn.map((s) =>
        s.kind === 'bar' ? (
          <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false} />
        ) : s.kind === 'marker' ? (
          <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke="none" legendType="diamond" connectNulls={false} isAnimationActive={false} activeDot={false} dot={MarkerDot(s.color)} />
        ) : (
          <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={s.bold ? 3 : 1.5} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
        )
      )}
    </ComposedChart>
  );

  return (
    <div className={dense ? CHART_CARD_DENSE_CSS : CHART_CARD_CSS}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className={dense ? 'text-[12px] font-semibold leading-4 text-[#101828] truncate' : 'text-[14px] font-semibold text-[#101828] truncate'}>{title}</h3>
          {caption && <div className={dense ? 'text-[11px] font-medium leading-4 text-[#71717A] truncate' : 'text-[12px] font-medium text-[#71717A] truncate'}>{caption}</div>}
        </div>
        {sortable && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[12px] font-medium text-[#71717A]">Sort:</span>
            <SegmentedControl<ChartSort>
              size="sm"
              ariaLabel="Sort"
              options={SORT_OPTIONS}
              value={sort}
              onChange={(next) => {
                setSort(next);
                onSort?.(next);
              }}
            />
          </div>
        )}
      </div>
      {width !== undefined ? (
        chart
      ) : (
        <div style={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height="100%">{chart}</ResponsiveContainer>
        </div>
      )}
      {note && <div data-testid="chart-note" className="text-[11px] font-medium text-[#71717A] leading-snug">{note}</div>}
    </div>
  );
};

// "Sales ahead: plan vs LY" (kit/logic/salesAhead.ts): plan sales as bars and LY sales for the same periods as a line,
// from the target period forward over the OTB span. A missing plan or LY is a gap (no bar / a break in the line) and the note
// names those periods; the tooltip line under the values is the period's plan vs LY % (or why it has none). Retail on every basis.

export const SALES_AHEAD_TITLE = 'Sales ahead: plan vs LY';
const SERIES: ChartSeries[] = [
  { key: 'plan', label: 'Plan sales', kind: 'bar', color: '#9E77ED' },
  { key: 'ly', label: 'LY sales', kind: 'line', color: '#F79009', bold: true },
];

/** "Plan $X vs LY $Y (+n%) · retail" over the periods with both, or "retail" when there are none. */
export const salesAheadCaption = (ahead: SalesAhead): string => {
  const t = ahead.totals;
  return t.plan === null || t.ly === null ? 'retail' : `Plan ${fmtCurrency(t.plan)} vs LY ${fmtCurrency(t.ly)} (${signedPct(t.diffPct)}) · retail`;
};

export interface SalesAheadChartProps { ahead: SalesAhead; grain: Grain; width?: number; height?: number; dense?: boolean }

export const SalesAheadChart: React.FC<SalesAheadChartProps> = ({ ahead, width, height, dense }) => (
  <ChartCard
    title={SALES_AHEAD_TITLE}
    caption={salesAheadCaption(ahead)}
    data={ahead.data.map((d) => ({ name: d.name, plan: d.plan, ly: d.ly, planNote: d.planNote }))}
    series={SERIES}
    xKey="name"
    formatValue={fmtCurrency}
    width={width}
    height={height}
    note={ahead.note ?? undefined}
    dense={dense}
  />
);

// the dashboard's "Sales ahead: plan vs LY" over the visible classes, summed with the totals rule (`totalRows` of the
// expandAll tree), so it follows the filters and search but never row expansion or SKU loads. Null until the payload is in.
export const salesAheadDashboardCard = (m: WeeklyPlanningReportModel): React.ReactNode | null => {
  const { ctx, payload } = m;
  if (!ctx || !payload) return null;
  const ahead = salesAheadForRows(totalRows(m.totalsRows), ctx.targetPeriod, payload.otbPeriods ?? payload.spanPeriods, ctx.grain);
  return <SalesAheadChart key="salesAhead" ahead={ahead} grain={ctx.grain} />;
};

// the "You vs peers" dashboard card. "All stores" reads the client-level value the hook resolved
// (`ctx.benchmarks.client`: the backend's client entity, or the passing classes re-summed under a scope filter,
// which the header then says with " · filtered"); a store reads the payload's whole-store entity (never filtered)
// against the vertical's G4 pool, and at class level adds a "By master class" table of the store's master
// classes against their G3 pools. Peer cells go through `peerText` (never a number for an unpublishable grain,
// the inflow gate for inflow metrics) and the index exists only where the peer value shows. Nothing is drawn
// without a payload or client value, or at level 'none'; the card reads no SKU / product rows, so it never
// changes as those load.

export interface BenchmarkCardProps {
  payload: BenchmarkPayload | null | undefined;
  /** The client-level value (clientBenchmark / ctx.benchmarks.client); null = nothing to show. */
  client: BenchmarkRowValue | null | undefined;
  /** Which metrics the main table lists, in order; the "By master class" table shows the first. */
  metrics: BenchmarkMetricId[];
  /** Store names by client location id (the payload's `locations` keys); the id itself when unnamed. */
  storeNames: Record<string, string>;
  /** The report's grain: Monthly shows weeks of stock as months of supply (MOS). Absent = weeks. */
  grain?: Grain;
  /** The report's cost basis: at cost the metric labels say "(retail)" (benchmarks are retail-based). */
  basis?: PriceBasis;
}

const ALL = 'all';
const TH = 'text-[11px] font-medium text-[#71717A] pb-1';

interface BenchmarkLineProps { label: string; metric: BenchmarkMetricId; own: number | null | undefined; peer: BenchmarkPeer | null | undefined; view: BenchmarkView }
/** One table row: label | you | peers | index (toned). Cover is shown exact (never "52+") so the index matches the figures beside it. */
const BenchmarkLine: React.FC<BenchmarkLineProps> = ({ label, metric, own, peer, view }) => {
  const idx = benchmarkIndex(metric, own, gatedPeerValue(peer, metric));
  return (
    <tr>
      <th scope="row" className="text-left font-medium text-[#344054] pr-4 py-0.5 whitespace-nowrap">{label}</th>
      <td className="text-right pr-4 py-0.5 text-[#101828]">{ownIn(metric, own, view)}</td>
      <td className="text-right pr-4 py-0.5 text-[#344054] whitespace-nowrap">{peerIn(peer, metric, view)}</td>
      <td className={cn('text-right py-0.5 font-semibold', BENCHMARK_TONE_CSS[indexTone(metric, idx)])}>{formatIndex(metric, idx)}</td>
    </tr>
  );
};

const Head: React.FC<{ first: string }> = ({ first }) => (
  <thead>
    <tr>
      <th scope="col" className={cn(TH, 'text-left pr-4')}>{first}</th>
      <th scope="col" className={cn(TH, 'text-right pr-4')}>You</th>
      <th scope="col" className={cn(TH, 'text-right pr-4')}>Peers</th>
      <th scope="col" className={cn(TH, 'text-right')}>Index</th>
    </tr>
  </thead>
);

export const BenchmarkCard: React.FC<BenchmarkCardProps> = ({ payload, client, metrics, storeNames, grain, basis }) => {
  const [scope, setScope] = useState<string>(ALL);
  // PHP's [] for an empty map: Object.entries treats it as empty (interface docblock).
  const stores = useMemo(() => (payload ? Object.entries(payload.locations as Record<string, BenchmarkEntity & { byMasterClass: Record<string, BenchmarkEntity> }>) : []), [payload]);
  if (!payload || !client || payload.level === 'none') return null;

  const label = benchmarkLabel(payload) ?? '';
  // A store picked for one client is not one of the next client's: it falls back to All stores (the select follows).
  const effective = stores.some(([id]) => id === scope) ? scope : ALL;
  const store = effective === ALL ? undefined : stores.find(([id]) => id === effective)?.[1];
  const g4 = payload.peers.vertical;
  const vertical = payload.vertical ?? 'No vertical';
  // No peer counts on screen: only whether there are enough peers to publish.
  const peersText = g4 && g4.publishable ? null : PEER_TEXT_NOT_ENOUGH;
  const header = [vertical, label, peersText, !store && client.filtered ? 'filtered' : null].filter(Boolean).join(' · ');
  const view: BenchmarkView = { grain, weeksInMonth: payload.weeksInMonth, basis };
  // A scope with no sales (a Receiving location) has no sales-based figure: every metric reads "—", not 0.00 against the peers.
  const own = store ? guardNoSales(store.metrics, store.sums) : client.own;
  const byMasterClass = store && payload.level === 'class' ? Object.entries(store.byMasterClass as Record<string, BenchmarkEntity>) : [];
  const first = metrics[0];

  return (
    <div className={CHART_CARD_CSS}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[14px] font-semibold text-[#101828] truncate">You vs peers</h3>
          <div className="text-[12px] font-medium text-[#71717A] truncate">{header}</div>
        </div>
        <label className="flex items-center gap-2 shrink-0 text-[12px] font-medium text-[#71717A]">
          <span>Scope</span>
          <select aria-label="Scope" value={effective} onChange={(e) => setScope(e.target.value)} className="rounded-lg border border-[#E4E4E7] bg-white px-2 py-1 text-[12px] font-medium text-[#101828]">
            <option value={ALL}>All stores</option>
            {stores.map(([id]) => (
              <option key={id} value={id}>{storeNames[id] ?? id}</option>
            ))}
          </select>
        </label>
      </div>
      <table className="text-[12px] tabular-nums">
        <Head first="Metric" />
        <tbody>
          {metrics.map((m) => (
            <BenchmarkLine key={m} label={metricLabelIn(m, view)} metric={m} own={own[m]} peer={g4} view={view} />
          ))}
        </tbody>
      </table>
      {basis === 'cost' && <div data-testid="benchmark-basis-note" className="text-[11px] font-medium text-[#71717A]">{RETAIL_BASIS_NOTE}</div>}
      {byMasterClass.length > 0 && first && (
        <div className="flex flex-col gap-1">
          <h4 className="text-[12px] font-semibold text-[#101828]">By master class</h4>
          <div className="text-[11px] font-medium text-[#71717A]">{metricLabelIn(first, view)} vs the master class peers</div>
          <table className="text-[12px] tabular-nums">
            <Head first="Master class" />
            <tbody>
              {byMasterClass.map(([mc, entity]) => (
                <BenchmarkLine key={mc} label={mc} metric={first} own={guardNoSales(entity.metrics, entity.sums)[first]} peer={payload.peers.masterClass[mc] ?? null} view={view} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// the row-detail section for a class row — the class's own benchmark metrics against its master
// class's G3 pool for the benchmark month, read from `ctx.benchmarks.byRowKey[row.key]` (the same entry the
// benchmark columns show). Nothing for vendor / product / SKU rows, for a class without an entry, or below
// class level (the location level trims the classes away). A non-ok entry explains itself in one sentence.
// a small card of dense rows (metric · you · peers · index) that sits beside the detail's charts; the
// at-cost note is the heading's tooltip.

export interface BenchmarkDetailProps {
  row: AnyRow;
  ctx: CellContext;
  /** Metrics to list, in order; default: every benchmark metric. */
  metrics?: BenchmarkMetricId[];
}

const statusSentence = (status: BenchmarkRowStatus, masterClass: string | null | undefined, month: string): string => {
  switch (status) {
    case 'notEnoughPeers': return `Not enough peers in ${masterClass ?? 'this master class'} for ${month}`;
    case 'mixedMasterClasses': return `No single peer group: this row spans mixed master classes (${month})`;
    case 'noMasterClass': return `No peer group: this class has no master class (${month})`;
    default: return `No peer group for ${month}`;
  }
};

export const BenchmarkDetail: React.FC<BenchmarkDetailProps> = ({ row, ctx, metrics }) => {
  const b = ctx.benchmarks;
  if (!b || b.level !== 'class' || isVendorRow(row)) return null;
  const v: BenchmarkRowValue | undefined = b.byRowKey[row.key];
  if (!v) return null;
  const month = benchmarkMonthNote(b);
  const view: BenchmarkView = { grain: ctx.grain, weeksInMonth: b.weeksInMonth, basis: ctx.basis };
  const ids = metrics ?? BENCHMARK_METRICS.map((d) => d.id);

  if (v.status !== 'ok') {
    return (
      <DetailCard ariaLabel="Benchmark" title="Benchmark" className="max-w-[280px]">
        <p className="text-[11px] font-medium text-[#71717A]">{statusSentence(v.status, v.masterClass, month)}</p>
      </DetailCard>
    );
  }

  return (
    <DetailCard
      ariaLabel="Benchmark"
      title={<>Benchmark <span className="font-medium text-[#71717A]">{`· ${v.masterClass ?? 'no master class'} · ${benchmarkMonthEnd(b)}`}</span></>}
      info={ctx.basis === 'cost' ? RETAIL_BASIS_NOTE : null}
    >
      <table className={DENSE_TABLE_CSS}>
        <tbody>
          {ids.map((m) => {
            const peer = v.peer;
            const idx = benchmarkIndex(m, v.own[m], gatedPeerValue(peer, m));
            return (
              <tr key={m} data-metric={m} data-testid="benchmark-detail-metric">
                <td className={cn(DENSE_TD_CSS, 'text-left pr-3 font-medium text-[#71717A] whitespace-nowrap')}>{metricLabelIn(m, view)}</td>
                <td className={cn(DENSE_TD_CSS, 'text-right pl-3 text-[#101828] whitespace-nowrap')}>you {ownIn(m, v.own[m], view)}</td>
                <td className={cn(DENSE_TD_CSS, 'text-right pl-3 whitespace-nowrap')}>peers {peerIn(peer, m, view)}</td>
                <td className={cn(DENSE_TD_CSS, 'text-right pl-3 font-semibold', BENCHMARK_TONE_CSS[indexTone(m, idx)])}>{formatIndex(m, idx)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </DetailCard>
  );
};

// Tasks 11-13: the "You vs peers" dashboard card as the three report configs append it. Present only with a
// benchmark payload AND a client-level value (`ctx.benchmarks`, null at entitlement 'none' or while loading / failed);
// it reads the benchmark payload and the class rows, never SKU / product rows, so it never moves as those load.
export const benchmarkDashboardCard = (m: WeeklyPlanningReportModel, metrics: BenchmarkMetricId[]): React.ReactNode | null =>
  m.benchmarks && m.ctx?.benchmarks?.client ? (
    <BenchmarkCard key="benchmarks" payload={m.benchmarks} client={m.ctx.benchmarks.client} metrics={metrics} storeNames={storeNamesOf(m.payload?.rows)} grain={m.ctx.grain} basis={m.ctx.basis} />
  ) : null;

// ===================================================================================================================
// ==== Toolbar.tsx ===================================================================================================
// ===================================================================================================================
// The small captioned controls of the weekly planning control card. All are display-only about tier coercions: a control
// shows a permitted option when the stored value is not allowed but never emits onChange for it; `applyTierFallback`
// (viewState) repairs the stored ReportViewState and tells the user.

export const CAPTION_CSS = 'text-[12px] font-medium text-[#71717A]';
export const SELECT_CSS =
  'h-[34px] w-full appearance-none bg-white border border-[#D0D5DD] rounded-[9px] pl-[14px] pr-8 text-[14px] font-semibold text-[#344054] leading-[21px] shadow-sm hover:bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] transition-colors cursor-pointer';
const HINT_POS = 'top-full left-0 mt-1.5 z-[110]';

const Caption: React.FC<{ label: string; hint?: string; htmlFor?: string }> = ({ label, hint, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    {htmlFor ? <label htmlFor={htmlFor} className={CAPTION_CSS}>{label}</label> : <span className={CAPTION_CSS}>{label}</span>}
    {hint && <InfoTooltip text={hint} widthClass="w-56" positionClass={HINT_POS} />}
  </div>
);

// ---------------------------------------------------------------------------------------------------------------------
// CaptionedSelect: a native <select> dressed as the Vendor Performance filter button under the 12px muted caption.

export interface CaptionedSelectOption<T extends string | number> {
  value: T;
  label: string;
}

export interface CaptionedSelectProps<T extends string | number> {
  label: string;
  value: T;
  options: CaptionedSelectOption<T>[];
  onChange: (value: T) => void;
  hint?: string;
  /** Hide the caption row (the label stays as the accessible name). */
  hideCaption?: boolean;
  widthClass?: string;
}

export function CaptionedSelect<T extends string | number>({ label, value, options, onChange, hint, hideCaption = false, widthClass = 'min-w-[120px]' }: CaptionedSelectProps<T>): JSX.Element {
  const id = useId();
  const numeric = typeof value === 'number';
  return (
    <div className="flex flex-col gap-1">
      {!hideCaption && <Caption label={label} hint={hint} htmlFor={id} />}
      <div className={cn('relative', widthClass)}>
        <select
          id={id}
          aria-label={hideCaption ? label : undefined}
          value={String(value)}
          onChange={(e) => onChange((numeric ? Number(e.target.value) : e.target.value) as T)}
          className={SELECT_CSS}
        >
          {options.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#667085]" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// TypedNumberField: a number you type (the lookback, the OTB span) with optional preset chips. The draft is local: typing
// "13" never commits "1" on the way; the value commits on blur / Enter (clamped into min..max, a non-number reverts) and a
// preset commits at once.

/** Clamp a typed value into min..max as a whole number; null when it is not a number at all. */
export const clampTyped = (raw: string | number, min: number, max: number): number | null => {
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (String(raw).trim() === '' || !Number.isFinite(n)) return null;
  return Math.min(max, Math.max(min, Math.round(n)));
};

export interface TypedNumberFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  /** Quick values shown as chips; the one equal to `value` is highlighted. */
  presets?: readonly number[];
  /** The unit shown after the input ("weeks", "months"). */
  unit: string;
  hint?: string;
  hideCaption?: boolean;
  /** The input's accessible name (defaults to the label). */
  inputLabel?: string;
}

export const TypedNumberField: React.FC<TypedNumberFieldProps> = ({ label, value, min, max, onChange, presets = [], unit, hint, hideCaption = false, inputLabel }) => {
  const id = useId();
  const [draft, setDraft] = useState<string>(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = () => {
    const next = clampTyped(draft, min, max);
    if (next === null) return setDraft(String(value));
    setDraft(String(next));
    if (next !== value) onChange(next);
  };

  return (
    <div className="flex flex-col gap-1">
      {!hideCaption && <Caption label={label} hint={hint} htmlFor={id} />}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative inline-flex items-center">
          <input
            id={id}
            type="text"
            inputMode="numeric"
            aria-label={inputLabel ?? (hideCaption ? label : undefined)}
            title={`${min}–${max} ${unit}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
            className="h-[34px] w-[88px] bg-white border border-[#D0D5DD] rounded-[9px] pl-3 pr-14 text-[14px] font-semibold text-[#344054] tabular-nums shadow-sm focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
          />
          <span className="pointer-events-none absolute right-3 text-[12px] font-medium text-[#71717A]">{value === 1 ? unit.replace(/s$/, '') : unit}</span>
        </div>
        {presets.length > 0 && (
          <div role="group" aria-label={`${label} presets`} className="flex items-center gap-1">
            {presets.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={p === value}
                onClick={() => { setDraft(String(p)); if (p !== value) onChange(p); }}
                className={cn(
                  'h-[26px] min-w-[28px] px-1.5 rounded-md border text-[12px] font-semibold tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-[#7F56D9]',
                  p === value ? 'border-[#7F56D9] bg-[#F4EBFF] text-[#6941C6]' : 'border-[#D0D5DD] bg-white text-[#344054] hover:bg-[#F9FAFB]'
                )}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------------------------------------------------
// LookbackSelect: the sales-rate lookback as a typed number, Weekly 1–52 weeks, Monthly 1–12 months. LOOKBACK_OPTIONS are
// the lists VendorAllocationService accepts for the allocation window and the reports' quick values.


export const lookbackLabel = (grain: Grain, n: number): string => `${n} ${grain === 'weekly' ? 'week' : 'month'}${n === 1 ? '' : 's'}`;

export interface LookbackSelectProps {
  grain: Grain;
  value: number;
  onChange: (value: number) => void;
  label?: string;
}

export const LookbackSelect: React.FC<LookbackSelectProps> = ({ grain, value, onChange, label = 'Lookback' }) => {
  const { min, max } = LOOKBACK_RANGE[grain];
  return (
    <TypedNumberField
      label={label}
      value={value}
      min={min}
      max={max}
      unit={grain === 'weekly' ? 'weeks' : 'months'}
      onChange={onChange}
      hint={unitize(`Periods of sales averaged into the sales rate behind weeks of supply and the trend. Type ${min}–${max}.`, grain)}
    />
  );
};

// ---------------------------------------------------------------------------------------------------------------------
// CompareSelect: Plan / LY / Avg. LY is dropped when the client lacks last year's history, so a view never compares
// against nothing.

export const COMPARE_LABELS: Record<Compare, string> = { plan: 'Plan', ly: 'LY', avg: 'Avg' };

export interface CompareSelectProps {
  value: Compare;
  onChange: (compare: Compare) => void;
  lyAvailable: boolean;
}

export const CompareSelect: React.FC<CompareSelectProps> = ({ value, onChange, lyAvailable }) => {
  const options = (Object.keys(COMPARE_LABELS) as Compare[]).filter((c) => lyAvailable || c !== 'ly').map((c) => ({ value: c, label: COMPARE_LABELS[c] }));
  return (
    <div className="flex flex-col gap-1">
      <Caption label="Compare" hint={lyAvailable ? undefined : 'Last year is unavailable: this client has less than a year of history.'} />
      <SegmentedControl<Compare> ariaLabel="Compare" options={options} value={!lyAvailable && value === 'ly' ? 'plan' : value} onChange={onChange} />
    </div>
  );
};

// ---------------------------------------------------------------------------------------------------------------------
// TrendMethodSelect: Auto · By plan · By last year · Days elapsed. Every method is already in the payload (`trendAlt`), so
// this only sets `viewState.trendMethod`: never a request. "By plan" is omitted (not disabled) without V3 plans; a stored
// 'plan' then shows as Auto without emitting.

export const TREND_METHOD_HINT =
  "Auto: the plan's daily shape, else last year, else days elapsed. By plan: the plan's daily shape. By last year: last year's sales on the same weekdays. Days elapsed: a straight line.";

export interface TrendMethodSelectProps {
  value: TrendMethod;
  onChange: (method: TrendMethod) => void;
  /** V3 plans exist (Tier A): offer "By plan". */
  planAvailable: boolean;
}

export const TrendMethodSelect: React.FC<TrendMethodSelectProps> = ({ value, onChange, planAvailable }) => {
  const methods = TREND_METHODS.filter((m) => planAvailable || m !== 'plan');
  return (
    <CaptionedSelect<TrendMethod>
      label="Trend"
      value={methods.includes(value) ? value : 'auto'}
      options={methods.map((m) => ({ value: m, label: TREND_METHOD_LABELS[m] }))}
      onChange={onChange}
      hint={TREND_METHOD_HINT}
      widthClass="min-w-[136px]"
    />
  );
};

// ---------------------------------------------------------------------------------------------------------------------
// SortSelect (Weekly Action): "Action priority" (the default) ranks every row by its action, most pressing first (rowTree
// ACTION_PRIORITY_ORDER), ties by WOS ÷ target; "Most overstocked first" ranks by WOS ÷ target descending (no sales or no
// target last). A sort picked from a column header shows as "Column sort" (not pickable here); "Table order" clears it.
// Pure view state: never a refetch.

export type SortChoice = 'actionPriority' | 'overTarget' | 'column' | 'none';
export const SORT_CHOICE_LABELS: Record<SortChoice, string> = { actionPriority: 'Action priority', overTarget: 'Most overstocked first', column: 'Column sort', none: 'Table order' };
export const SORT_HINT = 'Action priority: urgent reorders, reorders, reorders with no OTB, sell-downs, then the rest. Most overstocked first: weeks of stock ÷ target, highest first. Rows with no sales or no target come last. Click a column header to sort by that column instead.';

export const sortChoiceOf = (sort: ReportViewState['sort']): SortChoice =>
  sort === null ? 'none' : sort.column === ACTION_PRIORITY_SORT ? 'actionPriority' : sort.column === OVER_TARGET_SORT ? 'overTarget' : 'column';

export interface SortSelectProps {
  value: ReportViewState['sort'];
  onChange: (sort: ReportViewState['sort']) => void;
  /** The report's grain: the hint reads weeks of stock in Weekly, months of stock in Monthly. */
  grain?: Grain;
}

export const SortSelect: React.FC<SortSelectProps> = ({ value, onChange, grain }) => {
  const choice = sortChoiceOf(value);
  const options: SortChoice[] = choice === 'column' ? ['actionPriority', 'overTarget', 'column', 'none'] : ['actionPriority', 'overTarget', 'none'];
  return (
    <CaptionedSelect<SortChoice>
      label="Sort"
      value={choice}
      options={options.map((o) => ({ value: o, label: SORT_CHOICE_LABELS[o] }))}
      onChange={(next) => {
        if (next === 'actionPriority') onChange({ column: ACTION_PRIORITY_SORT, direction: 'desc' });
        else if (next === 'overTarget') onChange({ column: OVER_TARGET_SORT, direction: 'desc' });
        else if (next === 'none') onChange(null);
      }}
      hint={unitize(SORT_HINT, grain)}
      widthClass="min-w-[120px]"
    />
  );
};

// ---------------------------------------------------------------------------------------------------------------------
// HierarchySelect: the row groupings as the Vendor Performance view dropdown, in "With open-to-buy" and "Without
// open-to-buy" groups (a label only, no column is hidden). Vendor and product views need SKU-level data and are left out
// (not disabled) when `skuViewsAvailable` is false.

export const HIERARCHY_LABELS = Object.fromEntries(HIERARCHY_OPTIONS.map((o) => [o.key, o.label])) as Record<HierarchyView, string>;

export interface HierarchySelectProps {
  value: HierarchyView;
  onChange: (view: HierarchyView) => void;
  skuViewsAvailable: boolean;
}

export const HierarchySelect: React.FC<HierarchySelectProps> = ({ value, onChange, skuViewsAvailable }) => {
  const [isOpen, setIsOpen] = useState(false);
  const options = hierarchyOptionsFor(skuViewsAvailable);
  const groups = [
    { title: 'With open-to-buy', items: options.filter((o) => o.hasOtb) },
    { title: 'Without open-to-buy', items: options.filter((o) => !o.hasOtb) },
  ].filter((g) => g.items.length > 0);
  return (
    <Popover
      label="Hierarchy"
      icon={<Layers className="w-4 h-4 text-[#667085] shrink-0" />}
      value={<span className="text-[#101828] font-bold">{HIERARCHY_LABELS[value]}</span>}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      width="w-[300px]"
    >
      <div role="listbox" aria-label="Hierarchy" className="flex flex-col gap-1 -m-2">
        {groups.map((g) => (
          <div key={g.title} role="group" aria-label={g.title} className="flex flex-col gap-0.5">
            <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#98A2B3]">{g.title}</div>
            {g.items.map((o) => {
              const selected = o.key === value;
              return (
                <button
                  key={o.key}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  title={o.description}
                  onClick={() => { onChange(o.key); setIsOpen(false); }}
                  className={cn(
                    'w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors leading-[20px]',
                    selected ? 'bg-[#F9F5FF] text-[#6941C6]' : 'text-[#344054] hover:bg-[#F4F4F5]'
                  )}
                >
                  <span className="truncate">{o.label}</span>
                  {selected && <Check className="w-3.5 h-3.5 text-[#6941C6]" />}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </Popover>
  );
};

// ---------------------------------------------------------------------------------------------------------------------
// OverstockMultiplier: beside the WOS target; both set how a row's weeks of supply is judged, both apply in the browser.

type MultiplierKey = '1.25' | '1.5' | '2';
const MULTIPLIER_OPTIONS = MULTIPLIERS.map((m) => ({ value: String(m) as MultiplierKey, label: `${m}×` }));

export interface OverstockMultiplierProps { value: Multiplier; onChange: (multiplier: Multiplier) => void; grain?: Grain }

export const OverstockMultiplier: React.FC<OverstockMultiplierProps> = ({ value, onChange, grain }) => (
  <div className="flex flex-col gap-1">
    <Caption
      label="Overstock at"
      hint={unitize('Weeks of stock (WOS) above target × this multiplier counts as overstocked, and below target ÷ it as understocked. Status, Overstocked / Watch – high and urgency update at once; the Reorder / Hold action follows the same bands.', grain)}
    />
    <SegmentedControl<MultiplierKey> ariaLabel="Overstock multiplier" options={MULTIPLIER_OPTIONS} value={String(value) as MultiplierKey} onChange={(v) => onChange(Number(v) as Multiplier)} size="sm" />
  </div>
);

// ---------------------------------------------------------------------------------------------------------------------
// WosTargetInput: the weeks-of-supply target override. Empty or zero means "use the plan target" (resolveTarget only honours
// an override > 0); a positive number, clamped to MAX_WOS_TARGET, overrides every row's target and shows an Override chip.
// The accessible name follows `unit`: a months unit reads "Months of supply target", anything else "Weeks of supply target".

export const MAX_WOS_TARGET = 104;

export interface WosTargetInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  label?: string;
  unit?: string;
}

/** "Months" for a months unit (mos / months / MOS), else "Weeks". */
export const supplyUnitWord = (unit: string): 'Weeks' | 'Months' => (/^mo/i.test(unit.trim()) ? 'Months' : 'Weeks');

export const WosTargetInput: React.FC<WosTargetInputProps> = ({ value, onChange, label = 'Target', unit = 'wks' }) => (
  <div className="flex flex-col gap-1">
    <Caption label={label} hint="Replaces each class's plan target for Health, Action and Urgent. Blank = the plan's own target." />
    <div className="flex items-center gap-2">
      <div className="flex items-center h-[34px] bg-white border border-[#D0D5DD] rounded-[9px] shadow-sm focus-within:ring-2 focus-within:ring-[#7F56D9] focus-within:border-transparent transition-all">
        <input
          type="number"
          min="0"
          max={MAX_WOS_TARGET}
          step="1"
          inputMode="numeric"
          aria-label={`${supplyUnitWord(unit)} of supply target`}
          placeholder="Plan"
          value={value ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') return onChange(null);
            const parsed = Number(raw);
            if (!Number.isFinite(parsed) || parsed < 0) return;
            onChange(parsed > 0 ? Math.min(parsed, MAX_WOS_TARGET) : null);
          }}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          className="w-14 pl-2.5 pr-0.5 py-1 text-[13px] font-semibold text-[#344054] bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-center placeholder-[#98A2B3]"
        />
        <span className="text-[12px] font-medium text-[#667085] pr-2.5 select-none">{unit}</span>
      </div>
      {value !== null && <Pill tone="brand" onRemove={() => onChange(null)}>Override</Pill>}
    </div>
  </div>
);

// ---------------------------------------------------------------------------------------------------------------------
// TimeGrainToggle: Weekly / Monthly. Clients without daily actuals (capabilities.weeklyMode false) only get Monthly; the
// tooltip says why rather than showing a dead Weekly segment.

export interface TimeGrainToggleProps {
  value: Grain;
  onChange: (grain: Grain) => void;
  weeklyAvailable: boolean;
  /** Hover or focus on the toggle: the page warms the other grain's payload so the click is instant. */
  onPrefetch?: () => void;
}

const GRAINS: { value: Grain; label: string }[] = [{ value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }];

export const TimeGrainToggle: React.FC<TimeGrainToggleProps> = ({ value, onChange, weeklyAvailable, onPrefetch }) => (
  <div className="flex flex-col gap-1" onMouseEnter={onPrefetch} onFocus={onPrefetch}>
    <div className="flex items-center gap-1.5">
      <span className={CAPTION_CSS}>Grain</span>
      {!weeklyAvailable && <InfoTooltip text="Weekly mode needs daily data" widthClass="w-48" positionClass={HINT_POS} />}
    </div>
    <SegmentedControl<Grain>
      ariaLabel="Grain"
      options={weeklyAvailable ? GRAINS : GRAINS.filter((o) => o.value === 'monthly')}
      value={weeklyAvailable ? value : 'monthly'}
      onChange={onChange}
    />
  </div>
);

// ---------------------------------------------------------------------------------------------------------------------
// ProductGrainToggle: Product / SKU rows for views whose leaf is the product. The variants arrive with the products: no fetch.
// ProductRankToggle: Total by Product keeps the top 500 products, ranked by sales over the lookback or by inventory on hand
// (so dead stock can be reached). The choice is a request parameter (`sort=inventory`), so changing it refetches.

const PRODUCT_GRAINS: { value: ProductGrain; label: string }[] = [{ value: 'product', label: 'Product' }, { value: 'sku', label: 'SKU' }];
const PRODUCT_SORTS: { value: ProductSort; label: string }[] = [{ value: 'sales', label: 'Top by sales' }, { value: 'inventory', label: 'Top by inventory' }];

export interface ProductGrainToggleProps { value: ProductGrain; onChange: (grain: ProductGrain) => void }
export interface ProductRankToggleProps { value: ProductSort; onChange: (sort: ProductSort) => void }

export const ProductGrainToggle: React.FC<ProductGrainToggleProps> = ({ value, onChange }) => (
  <SegmentedControl<ProductGrain> ariaLabel="Product or SKU rows" options={PRODUCT_GRAINS} value={value} onChange={onChange} />
);

export const ProductRankToggle: React.FC<ProductRankToggleProps> = ({ value, onChange }) => (
  <SegmentedControl<ProductSort> ariaLabel="Rank products by" options={PRODUCT_SORTS} value={value} onChange={onChange} />
);

// ---------------------------------------------------------------------------------------------------------------------
// PctOfTotalButton: a show / hide button for the % of Total columns. Pressed = every % of Total column the report offers is
// in the table; the Columns menu and the button always agree because both read `columns.visible`.

/** What % of Total divides by, as the button's tooltip. */
export const pctOfTotalExplanation = (parentRelation: string | null | undefined): string =>
  parentRelation
    ? `% of Total divides each row by its parent (${parentRelation}); top-level rows divide by the report total.`
    : '% of Total divides each row by the report total.';

export interface PctOfTotalButtonProps {
  pressed: boolean;
  /** The parent relation of the view in force (HierarchyOption.parentRelation); in the tooltip. */
  parentRelation?: string | null;
  onPressedChange: (pressed: boolean) => void;
  grain?: Grain | null;
}

export const PctOfTotalButton: React.FC<PctOfTotalButtonProps> = ({ pressed, onPressedChange, parentRelation, grain }) => (
  <button
    type="button"
    aria-pressed={pressed}
    title={`Show or hide the % of Total columns next to Sales, ${bomInventoryLabel(grain)}, Inflows and On order. ${pctOfTotalExplanation(parentRelation)}`}
    onClick={() => onPressedChange(!pressed)}
    className={cn(
      'h-[34px] px-3 rounded-[9px] border text-[13px] font-semibold leading-[20px] transition-colors cursor-pointer whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7F56D9]',
      pressed ? 'bg-[#F4EBFF] border-[#7F56D9] text-[#6941C6]' : 'bg-white border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]',
    )}
  >
    % of Total
  </button>
);

// ---------------------------------------------------------------------------------------------------------------------
// SwitchFilter: a boolean filter in the view row (the VP switch with an optional tooltip). ShowSkusToggle: the same switch
// for "Show SKUs"; only clients with item-level daily data can (capabilities.skuLevel), otherwise it is disabled and the
// tooltip explains.

export interface SwitchFilterProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  disabled?: boolean;
  /** The small switch with its label inline (the control card's second row). */
  compact?: boolean;
}

export const SwitchFilter: React.FC<SwitchFilterProps> = ({ label, checked, onChange, hint, disabled, compact = false }) => (
  <ToggleSwitch
    size={compact ? 'sm' : 'md'}
    label={label}
    checked={checked}
    onChange={onChange}
    disabled={disabled}
    hint={hint ? <InfoTooltip text={hint} widthClass="w-56" positionClass={compact ? 'top-full right-0 mt-1.5 z-[110]' : HINT_POS} /> : undefined}
  />
);

export interface ShowSkusToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  available: boolean;
  compact?: boolean;
}

export const ShowSkusToggle: React.FC<ShowSkusToggleProps> = ({ checked, onChange, available, compact }) => (
  <SwitchFilter
    compact={compact}
    label="Show SKUs"
    checked={available && checked}
    onChange={onChange}
    disabled={!available}
    hint={available ? 'Expand each vendor-class row into its SKUs. The SKU and product search scopes need this on.' : 'SKU rows need item-level daily data (Tier A/B).'}
  />
);

// ---------------------------------------------------------------------------------------------------------------------
// AllocationControls (Vendor views): how a class plan is split across its vendors. The window shares the lookback lists
// (what VendorAllocationService allows); the basis is the share key.

export const ALLOC_BASIS_LABELS: Record<AllocBasis, string> = {
  net_sales: 'Sales % of total',
  cash_margin: 'Cash margin % of total',
  inventory: 'Inventory % of total',
  gross_margin: 'Gross margin % of total',
};

export interface AllocationControlsProps {
  grain: Grain;
  window: number;
  onWindowChange: (window: number) => void;
  basis: AllocBasis;
  onBasisChange: (basis: AllocBasis) => void;
}

export const AllocationControls: React.FC<AllocationControlsProps> = ({ grain, window, onWindowChange, basis, onBasisChange }) => (
  <div className="flex items-end gap-3">
    <CaptionedSelect<number>
      label="Allocation window"
      value={window}
      onChange={onWindowChange}
      options={LOOKBACK_OPTIONS[grain].map((n) => ({ value: n, label: lookbackLabel(grain, n) }))}
      hint="Sales history used to share each class plan across its vendors."
    />
    <CaptionedSelect<AllocBasis>
      label="Allocation basis"
      value={basis}
      onChange={onBasisChange}
      options={(Object.keys(ALLOC_BASIS_LABELS) as AllocBasis[]).map((k) => ({ value: k, label: ALLOC_BASIS_LABELS[k] }))}
      hint="Each vendor's share of the class plan follows its share of this measure over the window."
      widthClass="min-w-[140px]"
    />
  </div>
);

// FilterDropdown: the Vendor Performance checkbox filter popover (search box, checkbox list, "Exclude selected instead",
// Reset / Apply footer). By default the draft lives in usePendingFilterSelection and is committed once, when the popover
// closes (Apply, an outside click or Escape), so the report fetches once per popover session (Plan & OTB's Rows menu). With
// `applyOnSelect` (the weekly planning page's filters) every pick applies at once: no draft, no Apply button.
// FiltersPopover: Location, Category, Class, Vendor and Status in one "Filters" popover; each is its own FilterDropdown
// applying on select. The trigger reads "Filters" with nothing picked, else "Filters · Location, Status" plus a count badge,
// and its title lists each pick in full. "Clear all" empties the five filters (not the search or Weekly-planned only).

export interface FilterOption {
  code: string;
  label: string;
  count?: number;
}

export interface FilterDropdownProps {
  label: string;
  options: FilterOption[];
  selection: FilterSelection;
  onApply: (selection: FilterSelection) => void;
  searchable?: boolean;
  allowExclude?: boolean;
  /** Trigger text when nothing is selected; defaults to "All". */
  allLabel?: string;
  /** Lower-case plural for "Search …" / "No … found"; defaults to pluralOf(label). */
  pluralLabel?: string;
  /** Apply each pick (tick, exclude toggle, Reset) at once instead of on close; the Apply button goes. */
  applyOnSelect?: boolean;
}

const IRREGULAR_PLURALS: Record<string, string> = { category: 'categories', status: 'statuses', class: 'classes' };
export const pluralOf = (label: string): string => {
  const lower = label.toLowerCase();
  return IRREGULAR_PLURALS[lower] ?? `${lower}s`;
};

export const INPUT_CSS =
  'h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-sm';
export const APPLY_BUTTON_CSS = 'px-4 py-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold text-[13px] rounded-[9px] shadow-sm';

const triggerText = (selection: FilterSelection, options: FilterOption[], allLabel: string): string => {
  const n = selection.codes.length;
  if (n === 0) return allLabel;
  const prefix = selection.exclude ? 'Not ' : '';
  if (n === 1) return prefix + (options.find((o) => o.code === selection.codes[0])?.label ?? selection.codes[0]);
  return `${prefix}${n} selected`;
};

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  selection,
  onApply,
  searchable = true,
  allowExclude = false,
  allLabel = 'All',
  pluralLabel,
  applyOnSelect = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  // In select mode the draft never commits (isOpen is held false: it only follows the applied selection).
  const draft = usePendingFilterSelection(selection, applyOnSelect ? false : isOpen, onApply);
  const shown = applyOnSelect ? selection : draft.pending;
  const toggleCode = (code: string) => {
    if (!applyOnSelect) draft.toggleCode(code);
    else onApply({ ...selection, codes: selection.codes.includes(code) ? selection.codes.filter((c) => c !== code) : [...selection.codes, code] });
  };
  const setExclude = (exclude: boolean) => (applyOnSelect ? onApply({ ...selection, exclude }) : draft.setExclude(exclude));
  const clearCodes = () => (applyOnSelect ? onApply({ ...selection, codes: [] }) : draft.clearCodes());
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q) || o.code.toLowerCase().includes(q)) : options;
  }, [options, query]);
  const plural = pluralLabel ?? pluralOf(label);
  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  return (
    <Popover label={label} value={triggerText(selection, options, allLabel)} badge={selection.codes.length} isOpen={isOpen} onOpenChange={setIsOpen}>
      <div className="text-[14px] font-bold text-[#101828]">{label}</div>
      {searchable && (
        <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${plural}…`} className={INPUT_CSS} />
      )}
      <div className="flex flex-col gap-2.5 max-h-52 overflow-y-auto pr-1">
        {visible.length === 0 && <span className="text-[13px] text-[#94A3B8]">No {plural} found</span>}
        {visible.map((o) => (
          <label key={o.code} className="flex items-start gap-2.5 cursor-pointer text-[14px] font-semibold text-[#344054] leading-[21px]">
            <input
              type="checkbox"
              checked={shown.codes.includes(o.code)}
              onChange={() => toggleCode(o.code)}
              className="w-4 h-4 mt-0.5 accent-[#7F56D9] rounded border-[#D0D5DD] shrink-0"
            />
            <span className="min-w-0 flex-1 whitespace-normal break-words">{o.label}</span>
            {o.count !== undefined && <span className="text-[12px] font-medium text-[#98A2B3] tabular-nums shrink-0">{o.count}</span>}
          </label>
        ))}
      </div>
      {allowExclude && (
        <label className="border-t border-[#EAECF0] pt-2.5 flex items-center gap-2 text-[12px] font-medium text-[#344054] cursor-pointer">
          <input type="checkbox" checked={shown.exclude} onChange={(e) => setExclude(e.target.checked)} className="w-3.5 h-3.5 accent-[#7F56D9] rounded" />
          <span>Exclude selected instead</span>
        </label>
      )}
      <div className="flex items-center justify-between border-t border-[#EAECF0] pt-3">
        <button
          type="button"
          onClick={() => {
            clearCodes();
            setQuery('');
          }}
          className="text-[13px] font-semibold text-[#667085] hover:text-[#101828]"
        >
          Reset
        </button>
        {!applyOnSelect && (
          <button type="button" onClick={() => setIsOpen(false)} className={APPLY_BUTTON_CSS}>
            Apply
          </button>
        )}
      </div>
    </Popover>
  );
};

export interface FilterSpec {
  key: string;
  label: string;
  options: FilterOption[];
  selection: FilterSelection;
  onApply: (selection: FilterSelection) => void;
  allowExclude?: boolean;
  searchable?: boolean;
}

export interface FiltersPopoverProps {
  filters: FilterSpec[];
  onClearAll: () => void;
}

const pickText = (f: FilterSpec): string => {
  const names = f.selection.codes.map((c) => f.options.find((o) => o.code === c)?.label ?? c);
  const list = names.length <= 3 ? names.join(', ') : `${names.length} selected`;
  return `${f.label}: ${f.selection.exclude ? 'not ' : ''}${list}`;
};

export const FiltersPopover: React.FC<FiltersPopoverProps> = ({ filters, onClearAll }) => {
  const [isOpen, setIsOpen] = useState(false);
  const active = filters.filter((f) => f.selection.codes.length > 0);
  return (
    <Popover
      label="Filters"
      hideCaption
      icon={<Filter className="w-4 h-4 text-[#667085] shrink-0" aria-hidden="true" />}
      value={active.length === 0 ? 'Filters' : `Filters · ${active.map((f) => f.label).join(', ')}`}
      title={active.length > 0 ? active.map(pickText).join('; ') : undefined}
      badge={active.length}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      width="w-[280px]"
    >
      <div className="flex items-center justify-between">
        <span className="text-[14px] font-bold text-[#101828]">Filters</span>
        <button
          type="button"
          onClick={onClearAll}
          disabled={active.length === 0}
          className="text-[13px] font-semibold text-[#6941C6] hover:text-[#53389E] disabled:text-[#98A2B3] disabled:cursor-not-allowed"
        >
          Clear all
        </button>
      </div>
      <div className="flex flex-col gap-3">
        {filters.map((f) => (
          <FilterDropdown key={f.key} label={f.label} options={f.options} selection={f.selection} onApply={f.onApply} allowExclude={f.allowExclude} searchable={f.searchable} applyOnSelect />
        ))}
      </div>
    </Popover>
  );
};

// Open-to-buy settings in one popover: the span, "Buy window starts" (this period, or one of the next 26 weeks / 12 months:
// the span then runs from there), "Count on order" (due in the window, the rule, or all open orders) and Retail / Cost. The
// span follows the grain: Monthly types months ahead (1–6, "3 mo"; a saved 0 reads "This month"), Weekly types weeks (1–26,
// "13 wk"; presets 4 / 8 / 13 / 26); each grain keeps its own choice. The trigger summarises the settings: "13 wk · Retail",
// with a start "Jan W1 + 13 wk · Retail" (Weekly) / "Jan 2027 + 3 · Retail" (Monthly), and " · all POs" when counting every open order.

export interface OtbModuleProps {
  grain: Grain;
  /** The span in the grain's unit: months (Monthly) or weeks (Weekly). */
  span: number;
  onSpanChange: (span: number) => void;
  basis: PriceBasis;
  onBasisChange: (basis: PriceBasis) => void;
  /** The target period (the window's default start, "This week" / "This month"); the start options are the periods after it. */
  targetPeriod: number;
  /** The period in progress: the list of starts begins after the later of the target and this (the default start is the current period). */
  currentPeriod?: number;
  /** The buy window's first period, null = the target period. A value that does not apply to `targetPeriod` reads as null. */
  otbStart: number | null;
  onOtbStartChange: (otbStart: number | null) => void;
  otbOnOrder: OtbOnOrder;
  onOtbOnOrderChange: (otbOnOrder: OtbOnOrder) => void;
}

const SPAN_HINT: Record<Grain, string> = {
  weekly: 'How many weeks of planned inflows, less received and on order, make up open to buy (OTB). Weekly uses the weekly plan: a class whose plan ends sooner stops there.',
  monthly: 'How many months AFTER this one count: open to buy (OTB) is this month plus that many months of planned inflows, less received and on order (3 = this month + 3, four months in all).',
};
const START_HINT: Record<Grain, string> = {
  weekly: 'Start the open to buy at a later week to plan a future buy: the span runs from that week. The report\'s status and cover still judge the target week.',
  monthly: 'Start the open to buy at a later month to plan a future buy: the span runs from that month. The report\'s status and cover still judge the target month.',
};
export const OTB_ON_ORDER_ALL_HINT = 'Every open PO whatever its due date, overdue included: use this if your POS has no reliable due dates.';
const THIS_PERIOD: Record<Grain, string> = { weekly: 'This week', monthly: 'This month' };
const BASES: { value: PriceBasis; label: string }[] = [
  { value: 'retail', label: 'Retail' },
  { value: 'cost', label: 'Cost' },
];

/** The span as the trigger shows it: "13 wk", "3 mo", or "This month" for a Monthly span of 0 (a saved span of 0). */
export const otbSpanSummary = (grain: Grain, span: number): string => (grain === 'monthly' && span === 0 ? 'This month' : `${span} ${OTB_SPAN_UNIT[grain]}`);

export const otbSummary = (o: { grain: Grain; span: number; basis: PriceBasis; otbStart: number | null; otbOnOrder: OtbOnOrder }): string => {
  const window = o.otbStart === null ? otbSpanSummary(o.grain, o.span)
    : o.grain === 'weekly' ? `${otbStartLabel(o.otbStart, 'weekly')} + ${o.span} ${OTB_SPAN_UNIT.weekly}`
    : o.span === 0 ? otbStartLabel(o.otbStart, 'monthly') : `${otbStartLabel(o.otbStart, 'monthly')} + ${o.span}`;
  return `${window} · ${o.basis === 'retail' ? 'Retail' : 'Cost'}${o.otbOnOrder === 'all' ? ' · all POs' : ''}`;
};

const Field: React.FC<{ label: string; hint: string; children: React.ReactNode; labelId?: string }> = ({ label, hint, children, labelId }) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-center gap-1.5">
      <span id={labelId} className="text-[12px] font-medium text-[#71717A]">{label}</span>
      <InfoTooltip text={hint} widthClass="w-56" positionClass="top-full left-0 mt-1.5 z-[110]" />
    </div>
    {children}
  </div>
);

const OTB_SELECT_CSS = 'h-[34px] w-full bg-white border border-[#D0D5DD] rounded-[9px] shadow-sm px-2 text-[13px] font-semibold text-[#344054] focus:outline-none focus:ring-2 focus:ring-blue-500';
const RADIO_CSS = 'flex items-start gap-2 text-[13px] text-[#344054] cursor-pointer';

/** The buy window's start and which open orders it nets (Wendi, 2026-10-22: these two sit in the control bar's Advanced group, not the OTB popover). */
export type OtbWindowFieldsProps = Pick<OtbModuleProps, 'grain' | 'targetPeriod' | 'currentPeriod' | 'otbStart' | 'onOtbStartChange' | 'otbOnOrder' | 'onOtbOnOrderChange'>;
export const OtbWindowFields: React.FC<OtbWindowFieldsProps> = ({ grain, targetPeriod, currentPeriod, otbStart, onOtbStartChange, otbOnOrder, onOtbOnOrderChange }) => {
  const id = useId();
  const start = otbStartFor(otbStart, grain, targetPeriod);
  const startLabel = (p: number): string => (grain === 'weekly' ? formatWeekLabel(p, { withYear: true }) : formatPeriodLabel(p, 'monthly'));
  return (
    <>
      <Field label="Buy window starts" hint={START_HINT[grain]}>
        <select aria-label="Buy window starts" className={OTB_SELECT_CSS} value={start === null ? '' : String(start)} onChange={(e) => onOtbStartChange(e.target.value === '' ? null : Number(e.target.value))}>
          <option value="">{THIS_PERIOD[grain]}</option>
          {otbStartOptions(grain, Math.max(targetPeriod, currentPeriod ?? targetPeriod)).filter((p) => p > targetPeriod).map((p) => ( // never the period the default already covers
            <option key={p} value={p}>{startLabel(p)}</option>
          ))}
        </select>
      </Field>
      <Field label="Count on order" hint="Which open purchase orders the open to buy nets: those due inside the buy window (the rule), or every open order." labelId={`${id}-on-order`}>
        <div role="radiogroup" aria-labelledby={`${id}-on-order`} className="flex flex-col gap-1.5">
          <label className={RADIO_CSS}>
            <input type="radio" name={`${id}-on-order`} className="mt-0.5" checked={otbOnOrder === 'window'} onChange={() => onOtbOnOrderChange('window')} />
            <span>Due in the window</span>
          </label>
          <label className={RADIO_CSS}>
            <input type="radio" name={`${id}-on-order`} className="mt-0.5" checked={otbOnOrder === 'all'} onChange={() => onOtbOnOrderChange('all')} />
            <span>
              All open orders
              <span className="block text-[11px] font-normal text-[#71717A]">{OTB_ON_ORDER_ALL_HINT}</span>
            </span>
          </label>
        </div>
      </Field>
    </>
  );
};

/** `windowFields` (default false): also show the buy-window start and on-order fields in the popover (the control bar keeps them under Advanced). */
export const OtbModule: React.FC<OtbModuleProps & { windowFields?: boolean }> = ({ grain, span, onSpanChange, basis, onBasisChange, targetPeriod, currentPeriod, otbStart, onOtbStartChange, otbOnOrder, onOtbOnOrderChange, windowFields = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const start = otbStartFor(otbStart, grain, targetPeriod);
  const summary = otbSummary({ grain, span, basis, otbStart: start, otbOnOrder });
  return (
    <Popover label="OTB" value={summary} isOpen={isOpen} onOpenChange={setIsOpen} width="w-72">
      <div className="text-[14px] font-bold text-[#101828]">Open-to-buy</div>
      <Field label="Span" hint={SPAN_HINT[grain]}>
        <TypedNumberField
          label="OTB span"
          hideCaption
          value={span}
          min={OTB_SPAN_RANGE[grain].min}
          max={OTB_SPAN_RANGE[grain].max}
          unit={grain === 'weekly' ? 'weeks' : 'months'}
          presets={grain === 'weekly' ? OTB_SPAN_WEEK_OPTIONS : OTB_SPAN_MONTH_OPTIONS}
          onChange={onSpanChange}
        />
      </Field>
      {windowFields && <OtbWindowFields grain={grain} targetPeriod={targetPeriod} currentPeriod={currentPeriod} otbStart={otbStart} onOtbStartChange={onOtbStartChange} otbOnOrder={otbOnOrder} onOtbOnOrderChange={onOtbOnOrderChange} />}
      {!windowFields && (start !== null || otbOnOrder === 'all') && (
        <p className="text-[11px] text-[#71717A]">The buy window start and which open orders count are set under Advanced in the control bar.</p>
      )}
      <Field label="Basis" hint="Show every dollar figure at retail or at cost. Figures the data has no cost for (sales, plans other than inflows, markdowns, OTB + Dev) read —; ratios do not change.">
        <SegmentedControl<PriceBasis> ariaLabel="Price basis" options={BASES} value={basis} onChange={onBasisChange} size="sm" />
      </Field>
    </Popover>
  );
};

// The target period. Weekly: a popover listing the next 9 and last 26 epoch weeks with date ranges,
// NEWEST FIRST, the
// current one tagged "This week" and the default one (the week containing the last sales day, when it
// is not the current week) tagged "Latest sales"; ± steppers, in the VP date-popover style (VP
// L2698-2862). Monthly: a native <select> of epoch months, newest first (so no tags), labelled "Oct 2026"
// (the shared MonthSelector says "Oct, 26"). `pending` (the capabilities, and so the default period, are not in yet) holds
// the label back instead of showing the calendar period and then switching.

export const WEEKS_BACK = 26;
export const MONTHS_BACK = 24;
export const WEEKS_FORWARD = 9;
/** Monthly may look up to 12 months ahead (a future window's target), as Weekly looks 9 weeks ahead. */
export const MONTHS_FORWARD = 12;

export interface PeriodPickerProps {
  grain: Grain;
  value: number;
  onChange: (period: number) => void;
  min?: number;
  max?: number;
  /** Today's period: "This week". */
  currentPeriod: number;
  /** The period shown when none is picked (the one containing the last sales day): "Latest sales" when it differs from `currentPeriod`. */
  defaultPeriod?: number;
  /** The default period is not known yet: no label, controls disabled. */
  pending?: boolean;
}

const STEP_CSS =
  'h-[34px] w-[34px] bg-white border border-[#D0D5DD] rounded-[9px] shadow-sm flex items-center justify-center text-[#667085] hover:bg-[#F9FAFB] hover:text-[#344054] disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const ITEM_CSS = 'w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors leading-[20px]';
const TAG_CSS = 'px-1.5 py-0.5 rounded-[5px] bg-[#F9F5FF] text-[#6941C6] border border-[#E9D7FE] text-[10px] font-bold';
const PENDING_LABEL = '…';

// Always includes `value`, so a share link to an older month still shows it selected.
const MonthSelect: React.FC<{ value: number; onChange: (period: number) => void; min: number; max: number }> = ({ value, onChange, min, max }) => {
  const months: number[] = [];
  for (let m = Math.max(value, max); m >= Math.min(value, min); m -= 1) months.push(m);
  return (
    <div className="flex flex-row items-center space-x-2 bg-white justify-center">
      <Calendar1 className="ml-1 text-slate-500" />
      <select aria-label="Month" value={value} onChange={(e) => onChange(Number(e.target.value))} className="p-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
        {months.map((m) => (
          <option key={m} value={m}>
            {formatPeriodLabel(m, 'monthly')}
          </option>
        ))}
      </select>
    </div>
  );
};

export const PeriodPicker: React.FC<PeriodPickerProps> = ({ grain, value, onChange, min, max, currentPeriod, defaultPeriod, pending = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  if (grain === 'monthly') {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-[12px] font-medium text-[#71717A]">Period</span>
        <div className="h-[34px] flex items-center bg-white border border-[#D0D5DD] rounded-[9px] shadow-sm pr-1">
          {pending ? <span className="px-3 text-[14px] font-semibold text-[#98A2B3]">{PENDING_LABEL}</span> : <MonthSelect value={value} onChange={onChange} min={min ?? currentPeriod - MONTHS_BACK} max={max ?? currentPeriod + MONTHS_FORWARD} />}
        </div>
      </div>
    );
  }

  // The window always includes `value`, so a share link to an older week still shows it selected.
  const lo = Math.min(value, Math.max(currentPeriod - WEEKS_BACK, min ?? -Infinity));
  const hi = Math.max(value, Math.min(currentPeriod + WEEKS_FORWARD, max ?? Infinity));
  const weeks: number[] = [];
  for (let w = hi; w >= lo; w -= 1) weeks.push(w);
  const canPrev = !pending && (min === undefined || value - 1 >= min);
  const canNext = !pending && (max === undefined || value + 1 <= max);
  const latestSales = defaultPeriod !== undefined && defaultPeriod !== currentPeriod ? defaultPeriod : null;

  return (
    <div className="flex items-end gap-1.5">
      <Popover
        label="Period"
        icon={<Calendar className="w-4 h-4 text-[#667085] shrink-0" />}
        value={pending ? PENDING_LABEL : formatWeekLabel(value, { withYear: true })}
        isOpen={isOpen && !pending}
        onOpenChange={setIsOpen}
        width="w-64"
        disabled={pending}
      >
        <div role="listbox" aria-label="Week" className="flex flex-col gap-0.5 max-h-72 overflow-y-auto pr-1 -m-2">
          {weeks.map((w) => {
            const selected = w === value;
            return (
              <button
                key={w}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(w);
                  setIsOpen(false);
                }}
                className={cn(ITEM_CSS, selected ? 'bg-[#F9F5FF] text-[#6941C6]' : 'text-[#344054] hover:bg-[#F4F4F5]')}
              >
                <span className="tabular-nums">{formatWeekLabel(w, { withYear: true })}</span>
                {w === currentPeriod && <span className={TAG_CSS}>This week</span>}
                {w === latestSales && <span className={TAG_CSS}>Latest sales</span>}
              </button>
            );
          })}
        </div>
      </Popover>
      <button type="button" aria-label="Previous period" disabled={!canPrev} onClick={() => onChange(value - 1)} className={STEP_CSS}>
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button type="button" aria-label="Next period" disabled={!canNext} onClick={() => onChange(value + 1)} className={STEP_CSS}>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

// W3 (decision D15): the product search box. It replaces the class / SKU / product-name search (Top Sellers' scope
// dropdown): there is one search, over products, answered by the server (/product-search), so there is no scope to pick. The page
// shows it only while product or SKU rows are in the view (product hierarchies, or Show SKUs on). Typing is debounced
// (SEARCH_DEBOUNCE_MS) so one request goes out per pause, not per keystroke; the box shows what is typed at once, Clear applies at
// once, and a value that changes from outside (a chip removed, a saved view, a share link) replaces the typed text.
// The placeholder names only the fields the client's POS lets us match (`fields`, from the endpoint / capabilities); beside it, a
// status line says "N products match" (or that it is searching, or why it could not).
export const SEARCH_DEBOUNCE_MS = 300;

const FIELD_NAMES: Record<ProductSearchField, string> = { title: 'name', sku: 'SKU', barcode: 'barcode', tags: 'tags' };
const FIELD_ORDER: ProductSearchField[] = ['title', 'sku', 'barcode', 'tags'];

/** "Search products: name, SKU, barcode, tags" with only the fields given (always in that order). */
export const searchPlaceholder = (fields: ProductSearchField[]): string => {
  const names = FIELD_ORDER.filter((f) => fields.includes(f)).map((f) => FIELD_NAMES[f]);
  return `Search products: ${names.length > 0 ? names.join(', ') : FIELD_NAMES.title}`;
};

/** "N products match" (singular for 1; "N products match, showing the top M" when the server capped the list). */
export const matchSummary = (total: number, shown: number): string => {
  const base = `${total.toLocaleString('en-US')} ${total === 1 ? 'product matches' : 'products match'}`;
  return total > shown ? `${base}, showing the top ${shown.toLocaleString('en-US')}` : base;
};

export interface ProductSearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  fields: ProductSearchField[];
  /** A search is in flight. */
  loading?: boolean;
  /** The count the table is filtered to; null when no search is applied. */
  total?: number | null;
  shown?: number;
  /** The search failed (the table shows the full view). */
  error?: string | null;
  /** The text is too short to search (MIN_SEARCH_LENGTH): say so instead of nothing. */
  tooShort?: boolean;
}

export const ProductSearchBox: React.FC<ProductSearchBoxProps> = ({ value, onChange, fields, loading = false, total = null, shown = 0, error = null, tooShort = false }) => {
  const [text, setText] = useState(value);
  const sent = useRef(value); // the last text handed to onChange (or received as `value`)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(onChange);
  latest.current = onChange;
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => {
    if (value !== sent.current) {
      cancel();
      sent.current = value;
      setText(value);
    }
  }, [value]);
  useEffect(() => cancel, []);
  const emit = (next: string) => {
    cancel();
    sent.current = next;
    latest.current(next);
  };
  const status = error ? error : loading ? 'Searching…' : tooShort ? 'Type at least 2 characters' : total !== null ? matchSummary(total, shown) : null;

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex items-center">
        <Search className="w-4 h-4 text-[#667085] absolute left-3 top-2.5 pointer-events-none" />
        <input
          type="search"
          aria-label="Search products"
          placeholder={searchPlaceholder(fields)}
          value={text}
          maxLength={100}
          onChange={(e) => {
            const next = e.target.value;
            setText(next);
            cancel();
            timer.current = setTimeout(() => emit(next), SEARCH_DEBOUNCE_MS);
          }}
          className="h-[34px] pl-9 pr-7 text-[13px] font-medium bg-white border border-[#D0D5DD] rounded-[9px] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] w-72 shadow-sm text-[#344054] placeholder-[#98A2B3] [&::-webkit-search-cancel-button]:hidden"
        />
        {text && (
          <button type="button" aria-label="Clear search" onClick={() => { setText(''); emit(''); }} className="absolute right-2.5 top-2 text-[#98A2B3] hover:text-[#344054] cursor-pointer">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <span role="status" aria-live="polite" className={`text-[12px] font-medium whitespace-nowrap ${error ? 'text-[#B42318]' : 'text-[#667085]'}`}>{status}</span>
    </div>
  );
};

// The two control rows of the weekly planning page shell (Plan 4 layout points 2–3). exactly two rows at
// about 1440px. Row 1 (`filterRow`): Period, Grain, Lookback (typed, no presets), Compare, Filters (Location, Category, Class, Vendor and
// Status in one popover, FiltersPopover), Hierarchy, then the product search where it shows. Row 2 (`viewRow`): Trend, OTB span, Columns,
// % of Total, Overstock at, the report's own controls (`extraViewControls`: Weekly Action's WOS target and Sort, Plan & OTB's Mode / Rows /
// Options; then `extraFilterControls`: Inventory Health's $ Detail / Compact), the allocation controls and the product grain / rank, and
// last the compact toggles (Show SKUs, Weekly-planned only, Freshness), stacked two to a column so they take one control's height. Both rows wrap below about 1100px. Table / Dashboard is the page
// header's (`DisplayToggle`). Each top-level control carries `data-control` (a `contents` wrapper: no layout of its own).
// Tier gating leaves SKU-level controls OUT of the DOM (spec 3, hand-off 8.7): one flag, `skuAllowed`
// (= capabilityAllows(caps, 'vendor'): SKU-level data on a Tier A/B client), decides the Vendor filter,
// the vendor and product hierarchy views (HierarchySelect omits them when `skuViewsAvailable` is false), Show SKUs,
// the SKU / product search scopes and the allocation controls. Using the stricter `vendor` rule for
// Show SKUs too means a mis-set `skuLevel` on a Tier C client still shows nothing SKU-level. Product
// views show the Product / SKU grain instead of Show SKUs.
// `config.hideColumnControls` leaves out the Columns menu and "% of Total" (a report table that ignores
// the catalogue columns, e.g. Plan & OTB's matrix). The period picker shows `defaultPeriod` when none
// is picked and, until the capabilities are in, no period at all (no calendar-week label that then
// switches to the sales week); "Latest sales" tags the default, "This week" today's.
// The Trend method select (Part B) sits in the view row only when the payload has a trend
// period and the tier is A or B (`trendMethodAvailable`); Tier C is always the ORBIT projection.

export type ReportDisplay = 'table' | 'dashboard';
const DISPLAY_OPTIONS: { value: ReportDisplay; label: string }[] = [{ value: 'table', label: 'Table' }, { value: 'dashboard', label: 'Dashboard' }];
/** Table / Dashboard, in the page header beside How it works / Save view / Share link / Export CSV. */
export const DisplayToggle: React.FC<{ display: ReportDisplay; onDisplay: (d: ReportDisplay) => void }> = ({ display, onDisplay }) => (
  <SegmentedControl<ReportDisplay> ariaLabel="Display" options={DISPLAY_OPTIONS} value={display} onChange={onDisplay} />
);
const WEEKLY_PLANNED_HINT = 'Only classes with a weekly plan (V3) in the table; classes planned by the month only are left out.';
/** A top-level control of a row, named for tests and the layout (display: contents, so the row lays the control out itself). */
const Slot: React.FC<{ name: string; className?: string; children: React.ReactNode }> = ({ name, className = 'contents', children }) => (
  <div data-control={name} className={className}>{children}</div>
);
// The statuses, then the M1 §6 Deadstock flag: a filterable chip, not a status (rowTree's leafPasses ORs it with the statuses).
const STATUS_OPTIONS = [...(Object.keys(STATUS_LABELS) as Status[]).map((s) => ({ code: s as string, label: STATUS_LABELS[s] })), { code: DEADSTOCK_FILTER, label: DEADSTOCK_LABEL }];
type SelectionKey = Exclude<keyof ViewFilters, 'statuses'>;

/** Where the "Weekly-planned only" switch shows (and where the hook applies it): Weekly with V3 plans. */
export const weeklyPlannedAvailable = (m: WeeklyPlanningReportModel): boolean => m.view.state.grain === 'weekly' && !!m.capabilities?.hasV3Plans;

/** Where the Trend method select shows (and How it works explains it): a payload with a trend period on Tier A/B. */
export const trendMethodAvailable = (m: WeeklyPlanningReportModel): boolean =>
  (m.tier === 'A' || m.tier === 'B') && !!m.payload?.trend && Array.isArray(m.payload.trend.periods) && m.payload.trend.periods.length > 0;

export interface WeeklyPlanningControlBarProps {
  model: WeeklyPlanningReportModel;
  config: ReportPageConfig;
  /** The columns the table shows (freshness / config filter and label overrides applied). */
  columns: MetricDefinition[];
}

export const WeeklyPlanningControlBar: React.FC<WeeklyPlanningControlBarProps> = ({ model, config, columns }) => {
  const { view, capabilities, payload, filterOptions, columnState, reportKey, tier } = model;
  const { state, set } = view;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advancedId = useId();
  const skuAllowed = capabilityAllows(capabilities, 'vendor');
  const grain = state.grain;
  // The % of Total columns this report's catalogue offers (the button shows / hides exactly these).
  const pctIds = columns.filter((c) => c.group === 'pctOfTotal').map((c) => c.id);
  const setFilter = (key: SelectionKey) => (selection: FilterSelection) => set({ filters: { ...state.filters, [key]: selection } });
  // Product views: the Product / SKU grain replaces Show SKUs.
  const productView = skuAllowed && isProductView(state.view);

  const filters: FilterSpec[] = [
    { key: 'locations', label: 'Location', options: filterOptions.locations, selection: state.filters.locations, onApply: setFilter('locations'), allowExclude: true },
    { key: 'categories', label: 'Category', options: filterOptions.categories, selection: state.filters.categories, onApply: setFilter('categories'), allowExclude: true },
    { key: 'classes', label: 'Class', options: filterOptions.classes, selection: state.filters.classes, onApply: setFilter('classes'), allowExclude: true },
    ...(skuAllowed ? [{ key: 'vendors', label: 'Vendor', options: filterOptions.vendors, selection: state.filters.vendors, onApply: setFilter('vendors'), allowExclude: true }] : []),
    {
      key: 'statuses', label: 'Status', options: STATUS_OPTIONS, searchable: false,
      selection: { codes: state.filters.statuses, exclude: false },
      onApply: (s: FilterSelection) => set({ filters: { ...state.filters, statuses: s.codes } }),
    },
  ];
  const none = { codes: [], exclude: false };
  const clearFilters = () => set({ filters: { ...state.filters, locations: none, categories: none, classes: none, vendors: none, statuses: [] } });

  const filterRow = (
    <>
      <Slot name="period">
        <PeriodPicker
          grain={grain}
          value={state.period ?? model.defaultPeriod}
          currentPeriod={model.currentPeriod}
          defaultPeriod={model.defaultPeriod}
          pending={state.period === null && !capabilities}
          // a start that no longer follows the new target goes back to the target.
          onChange={(period) => set({ period, otbStart: otbStartFor(state.otbStart, grain, period) })}
        />
      </Slot>
      <Slot name="grain"><TimeGrainToggle value={grain} onChange={(g) => set({ grain: g })} weeklyAvailable={capabilities?.weeklyMode ?? false} onPrefetch={model.prefetchOtherGrain} /></Slot>
      <Slot name="lookback"><LookbackSelect grain={grain} value={state.lookback} onChange={(lookback) => set({ lookback })} /></Slot>
      <Slot name="compare"><CompareSelect value={state.compare} onChange={(compare) => set({ compare })} lyAvailable={payload?.lastYear != null} /></Slot>
      <Slot name="filters" className="min-w-0 max-w-[280px]"><FiltersPopover filters={filters} onClearAll={clearFilters} /></Slot>
      <Slot name="hierarchy">
        <HierarchySelect value={state.view} onChange={(v) => set(v === 'totalByProduct' ? { view: v, sort: sortForProductRank(state.sort) } : { view: v })} skuViewsAvailable={skuAllowed && config.supportsVendorView !== false} />
      </Slot>
      {model.productSearch.visible && (
        <Slot name="search">
          <ProductSearchBox
            value={state.search.text}
            onChange={(text) => set({ search: { text, scope: 'product' } })}
            fields={model.productSearch.fields}
            loading={model.productSearch.loading}
            total={model.productSearch.applied && model.productSearch.result ? model.productSearch.result.total : null}
            shown={model.productSearch.result?.truncated ? model.productSearch.result.limit : model.productSearch.result?.total ?? 0}
            error={model.productSearch.error}
            tooShort={model.productSearch.tooShort}
          />
        </Slot>
      )}
    </>
  );

  const showSkus = skuAllowed && !productView;
  const weeklyPlanned = weeklyPlannedAvailable(model);
  const freshness = !!config.showFreshnessToggle && !!capabilities?.hasReceiving;
  // The switch reads the columns themselves: on while any freshness column is shown.
  const freshnessVisible = FRESHNESS_IDS.some((id) => columnState.visible[id] === true);
  const freshnessDefaults = columnsFor(reportKey, tier).filter((c) => c.visible && FRESHNESS_IDS.includes(c.id)).map((c) => c.id);
  // Wendi (2026-10-22), item 11: fewer choices by default. Trend method, Overstock at, Distribute OTB and the OTB buy-window start /
  // on-order controls sit in a collapsed "Advanced" group (one button, a third row when open); values and saving are unchanged.
  const advancedTrend = trendMethodAvailable(model);
  const advancedSet = [state.trendMethod !== 'auto' && advancedTrend, otbStartFor(state.otbStart, grain, state.period ?? model.defaultPeriod) !== null, state.otbOnOrder === 'all', reportKey !== 'plan_otb' && state.multiplier !== 1.5, reportKey !== 'plan_otb' && state.distributeOtb].filter(Boolean).length;
  const otbWindowProps = {
    grain, targetPeriod: state.period ?? model.defaultPeriod, currentPeriod: model.currentPeriod,
    otbStart: state.otbStart, onOtbStartChange: (otbStart: number | null) => set({ otbStart }),
    otbOnOrder: state.otbOnOrder, onOtbOnOrderChange: (otbOnOrder: OtbOnOrder) => set({ otbOnOrder }),
  };
  const advancedRow = advancedOpen ? (
    <>
      {advancedTrend && (
        <Slot name="trend"><TrendMethodSelect value={state.trendMethod} onChange={(trendMethod) => set({ trendMethod })} planAvailable={!!capabilities?.hasV3Plans} /></Slot>
      )}
      <Slot name="otbWindow" className="flex flex-wrap items-start gap-x-3 gap-y-2 min-w-0"><OtbWindowFields {...otbWindowProps} /></Slot>
      {reportKey !== 'plan_otb' && <Slot name="overstock"><OverstockMultiplier grain={grain} value={state.multiplier} onChange={(multiplier) => set({ multiplier })} /></Slot>}
      {reportKey !== 'plan_otb' && (
        <Slot name="distribute">
          <SwitchFilter compact label="Distribute OTB" hint="Splits each class's OTB down to its child classes, products and SKUs (vendors already have theirs) by the allocation basis and window: sales, cash margin or inventory % of total." checked={state.distributeOtb} onChange={(distributeOtb) => set(distributeOtb ? { distributeOtb, columns: { ...state.columns, visible: { ...state.columns.visible, otb: true } } } : { distributeOtb })} />
        </Slot>
      )}
    </>
  ) : null;
  const viewRow = (
    <>
      <Slot name="otb">
        <OtbModule
          span={grain === 'weekly' ? state.otbSpanWeeks : state.otbSpanMonths}
          onSpanChange={(span) => set(grain === 'weekly' ? { otbSpanWeeks: span } : { otbSpanMonths: span })}
          basis={state.basis}
          onBasisChange={(basis) => set({ basis })}
          {...otbWindowProps}
        />
      </Slot>
      {!config.hideColumnControls && (
        <>
          <Slot name="columns">
            <ColumnsMenu
              columns={columns}
              visible={columnState.visible}
              order={columnState.order}
              pinned={columnState.pinned}
              grain={grain}
              onChange={({ visible, order }) => model.onColumnState({ ...columnState, visible, order })}
              onReset={() => set(resetColumns(state, reportKey, tier))}
            />
          </Slot>
          {pctIds.length > 0 && <Slot name="pctOfTotal">
            <PctOfTotalButton grain={grain} parentRelation={HIERARCHY_BY_KEY[state.view].parentRelation} pressed={pctIds.length > 0 && pctIds.every((id) => columnState.visible[id] === true)} onPressedChange={(on) => set(setShowPctOfTotal(state, on, pctIds))} />
          </Slot>}
        </>
      )}
      {config.extraViewControls && <Slot name="report">{config.extraViewControls(model)}</Slot>}
      {config.extraFilterControls && <Slot name="detail">{config.extraFilterControls(model)}</Slot>}
      {skuAllowed && (needsVendorRows(state.view) || (state.distributeOtb && reportKey !== 'plan_otb')) && (
        <Slot name="allocation">
          <AllocationControls grain={grain} window={state.allocWindow} onWindowChange={(allocWindow) => set({ allocWindow })} basis={state.allocBasis} onBasisChange={(allocBasis) => set({ allocBasis })} />
        </Slot>
      )}
      {productView && (
        <Slot name="product">
          <ProductGrainToggle value={state.productGrain} onChange={(productGrain) => set({ productGrain })} />
          {state.view === 'totalByProduct' && <ProductRankToggle value={state.productSort} onChange={(productSort) => set({ productSort, sort: sortForProductRank(state.sort) })} />}
        </Slot>
      )}
      {(showSkus || weeklyPlanned || freshness || reportKey !== 'plan_otb') && (
        <Slot name="toggles" className="grid grid-rows-2 grid-flow-col auto-cols-max items-center gap-x-[12px] gap-y-1">
          {showSkus && <ShowSkusToggle compact checked={state.showSkus} onChange={(v) => set({ showSkus: v })} available />}
          {weeklyPlanned && (
            <SwitchFilter compact label="Weekly-planned only" hint={WEEKLY_PLANNED_HINT} checked={state.weeklyPlannedOnly} onChange={(weeklyPlannedOnly) => set({ weeklyPlannedOnly })} />
          )}
          {/* Wendi (2026-10-22), item 2: child classes are an option (Plan & OTB offers it in its Options popover). */}
          {reportKey !== 'plan_otb' && <SwitchFilter compact label={SHOW_CHILD_CLASSES_LABEL} hint={SHOW_CHILD_CLASSES_HINT} checked={state.showChildClasses} onChange={(showChildClasses) => set({ showChildClasses })} />}
          {freshness && <SwitchFilter compact label="Freshness" hint="Shows or hides the freshness columns (Fresh %, aging). Pick them one by one under Columns." checked={freshnessVisible} onChange={(on) => set(setFreshnessShown(state, on, freshnessDefaults))} />}
        </Slot>
      )}
      <Slot name="advanced">
        <button
          type="button"
          aria-expanded={advancedOpen}
          aria-controls={advancedId}
          onClick={() => setAdvancedOpen((o) => !o)}
          title="Trend method, the OTB buy window start and which open orders count, Overstock at and Distribute OTB"
          className={cn(FILTER_BUTTON_CSS, 'h-[34px]')}
        >
          <Settings2 className="w-4 h-4 text-[#667085] shrink-0" aria-hidden="true" />
          <span className="font-semibold text-[#344054]">Advanced</span>
          {advancedSet > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4EBFF] text-[#6941C6] text-[11px] font-bold leading-none">{advancedSet}</span>}
          {advancedOpen ? <ChevronUp className="w-3.5 h-3.5 text-[#667085]" aria-hidden="true" /> : <ChevronDown className="w-3.5 h-3.5 text-[#667085]" aria-hidden="true" />}
        </button>
      </Slot>
    </>
  );

  return <ReportControlBar filterRow={filterRow} viewRow={viewRow} advancedRow={advancedRow} advancedId={advancedId} />;
};

/** Item 2: the "Show child classes" option (default on; saved in views). */
export const SHOW_CHILD_CLASSES_LABEL = 'Show child classes';
export const SHOW_CHILD_CLASSES_HINT = 'On: a parent class lists its child classes under it. Off: child classes are not shown and the parent row stands for the family (its figures already include them).';

// ===================================================================================================================
// ==== ReportPage.tsx ================================================================================================
// ===================================================================================================================
// The Vendor Performance "Save this view" modal (VP L4426-4550), rendered through a portal into
// document.body so `position: fixed` centres on the viewport whatever wraps the report (VP's
// comment explains the transform/containing-block trap), at z-[10000] above the MUI app shell.
// Differences from VP: an empty name is refused rather than saved as "Untitled view", the
// visibility pills are a radio group, and a failed save stays open with the error. `shadow-2xs`
// (Tailwind v4) becomes `shadow-sm`.

export interface SaveViewModalProps {
  open: boolean;
  onClose: () => void;
  /** Resolve (or return) when saved; reject to keep the modal open with an error. */
  onSave: (name: string, visibility: SavedViewVisibility) => void | Promise<unknown>;
}

const PILL_CSS = 'flex-1 h-11 rounded-[10px] text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors';
const PILL_ON = 'bg-[#7C3AED] text-white shadow-sm';
const PILL_OFF = 'bg-white border border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]';

export const SaveViewModal: React.FC<SaveViewModalProps> = ({ open, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<SavedViewVisibility>('only_me');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Read through a ref so an inline onClose from a parent re-render neither resets the form nor
  // re-binds the listener.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    setName('');
    setVisibility('only_me');
    setError(null);
    setSaving(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  const handleSave = async () => {
    if (saving) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Enter a name for this view.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed, visibility);
      onClose();
    } catch {
      setError('This view could not be saved. Please try again.');
      setSaving(false);
    }
  };

  const pill = (value: SavedViewVisibility, icon: React.ReactNode, label: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={visibility === value}
      onClick={() => setVisibility(value)}
      className={cn(PILL_CSS, visibility === value ? PILL_ON : PILL_OFF)}
    >
      {icon} {label}
    </button>
  );

  return createPortal(
    <div
      data-testid="save-view-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#101828]/50 backdrop-blur-[2px] animate-in fade-in duration-150"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-view-title"
        className="bg-white rounded-2xl p-6 w-[520px] max-w-[calc(100vw-32px)] shadow-2xl border border-slate-100 flex flex-col gap-3 animate-in zoom-in-95 duration-150"
      >
        <div className="flex items-center justify-between">
          <h3 id="save-view-title" className="text-[18px] font-bold text-[#101828]">Save this view</h3>
          <button type="button" aria-label="Close" onClick={onClose} className="text-[#98A2B3] hover:text-[#101828] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-[14px] text-[#667085] leading-relaxed font-normal">
          Saves your filters, view, time settings, sort and columns to your account. The period is not saved: the view opens on the current week or month.
        </p>

        <div className="flex flex-col gap-2">
          <label htmlFor="save-view-name" className="text-[14px] font-medium text-[#344054]">View name</label>
          <input
            id="save-view-name"
            type="text"
            placeholder="e.g. Classes to reorder this week"
            value={name}
            maxLength={SAVED_VIEW_NAME_MAX_LENGTH}
            aria-invalid={error !== null && !name.trim()}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
            className="h-11 w-full border border-[#D0D5DD] rounded-[10px] px-3.5 text-[14px] font-medium text-[#101828] placeholder-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[#7C3AED] shadow-sm"
          />
        </div>

        <div className="flex flex-col gap-2">
          <span id="save-view-visibility" className="text-[14px] font-medium text-[#344054]">Visibility</span>
          <div role="radiogroup" aria-labelledby="save-view-visibility" className="flex items-center gap-2.5">
            {pill('only_me', <Lock className="w-4 h-4" />, 'Only me')}
            {pill('shared', <Users className="w-4 h-4" />, 'Shared')}
          </div>
          {visibility === 'shared' && (
            <p className="text-[12px] text-[#667085]">Everyone with access to this client can see and apply this view. Only you can delete it.</p>
          )}
        </div>

        {error && (
          <p role="alert" className="text-[13px] font-medium text-[#D92D20]">{error}</p>
        )}

        <div className="flex items-center justify-end gap-3 mt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 bg-white border border-[#D0D5DD] rounded-[10px] text-[14px] font-semibold text-[#344054] hover:bg-[#F9FAFB] shadow-sm transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="h-10 px-4 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[14px] font-semibold rounded-[10px] shadow-sm transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" /> Save view
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// The Vendor Performance saved-views star menu (VP L2591-2693) on the shared useSavedViews hook,
// grouped Only me / Shared (spec 6), favourites first within each group, delete (own views, after
// a confirm). Owns the one hook instance for the report, so the optional `saveModal` renders the
// SaveViewModal here and saves through the same list; the page's "Save view" button only opens it.
// The card is opaque (inline white, no fade-in: it was see-through mid-animation and swallowed the first click) and sits above the control bar.
// Hover states are React state (not `group-hover`, see VP L1501); `shadow-2xs` becomes `shadow-sm`.
// a saved view named by the URL (`?sv=`) is `pendingViewId`; once the list is in
// (ready, or failed) the record with that id, or null (with `listFailed` when the list request failed),
// is handed to `onPendingView` once per id. A successful delete reports the id through `onDeleted`.

export const SAVED_VIEWS_STORAGE_PREFIX = 'weekly-planning';
const NO_LOCAL_VIEWS = (): never[] => [];

export interface SavedViewsMenuProps {
  reportKey: ReportKey;
  clientId: string | number | null | undefined;
  userId: string | number | null | undefined;
  onApply: (record: SavedViewRecord<SavedViewPayload>) => void;
  /** Render the Save view modal from here (one hook instance): the page controls `open`. */
  saveModal?: { open: boolean; onClose: () => void; payload: () => SavedViewPayload; onSaved?: (record: SavedViewRecord<SavedViewPayload>) => void };
  onError?: (message: string) => void;
  /** A saved view id from the URL, resolved against the loaded list (null / undefined: none). */
  pendingViewId?: number | null;
  /** Called once per `pendingViewId` with its record, or null when it is not in the list (`listFailed`: the list could not be loaded). */
  onPendingView?: (record: SavedViewRecord<SavedViewPayload> | null, opts: { listFailed: boolean }) => void;
  /** A view was deleted (after the server confirmed it). */
  onDeleted?: (id: number) => void;
  /** built-in view templates (dev-tools users only; the page passes none to anyone else). Applying one only changes the view. */
  templates?: { id: string; name: string; description: string; onApply: () => void }[];
}

const sharedCaption = (v: SavedViewRecord<SavedViewPayload>): string => (v.canEdit || !v.ownerName ? 'Shared' : `Shared by ${v.ownerName}`);
/** Item 12: the templates group at the top of the Views menu. */
export const TEMPLATES_GROUP_LABEL = 'Start from a template';
/** Item 12: the hint under the title on a first visit (no saved view applied, the columns untouched). */
export const FIRST_VISIT_HINT = 'Start from a template or pick your columns';
export const FIRST_VISIT_HINT_NO_TEMPLATES = 'Pick your columns, or open Views for a saved view';

export const SavedViewsMenu: React.FC<SavedViewsMenuProps> = ({ reportKey, clientId, userId, onApply, saveModal, onError, pendingViewId, onPendingView, onDeleted, templates = [] }) => {
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const { views, status, isLocalFallback, saveView, deleteView, isFavorite, toggleFavorite } = useSavedViews<SavedViewPayload>({
    reportKey, clientId, userId, storagePrefix: SAVED_VIEWS_STORAGE_PREFIX, readLocalViews: NO_LOCAL_VIEWS,
  });

  useEffect(() => {
    if (!isOpen) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement | null)?.closest(`[data-popover-container="${id}"]`)) setIsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, id]);

  // The URL's saved view: looked up once the list has loaded (or failed), once per id.
  const resolvedPending = useRef<number | null>(null);
  const onPendingViewRef = useRef(onPendingView);
  onPendingViewRef.current = onPendingView;
  useEffect(() => {
    if (pendingViewId === null || pendingViewId === undefined || resolvedPending.current === pendingViewId) return;
    if (status !== 'ready' && status !== 'error') return;
    resolvedPending.current = pendingViewId;
    onPendingViewRef.current?.(views.find((v) => v.id === pendingViewId) ?? null, { listFailed: status === 'error' });
  }, [pendingViewId, status, views]);

  // Favourites float to the top of each group; ties keep their save order.
  const groups = useMemo(() => {
    const byFavorite = (a: SavedViewRecord<SavedViewPayload>, b: SavedViewRecord<SavedViewPayload>) => (isFavorite(b.id) ? 1 : 0) - (isFavorite(a.id) ? 1 : 0);
    return (['only_me', 'shared'] as SavedViewVisibility[])
      .map((visibility) => ({ visibility, label: visibility === 'shared' ? 'Shared' : 'Only me', views: views.filter((v) => v.visibility === visibility).sort(byFavorite) }))
      .filter((g) => g.views.length > 0);
  }, [views, isFavorite]);

  const handleApply = (record: SavedViewRecord<SavedViewPayload>) => {
    onApply(record);
    setIsOpen(false);
  };
  const handleDelete = async (record: SavedViewRecord<SavedViewPayload>) => {
    if (!window.confirm(`Delete the saved view "${record.name}"?`)) return;
    try {
      await deleteView(record.id);
      onDeleted?.(record.id);
    } catch {
      onError?.(`Could not delete "${record.name}". Please try again.`);
    }
  };
  const handleSave = useCallback(
    async (name: string, visibility: SavedViewVisibility) => {
      if (!saveModal) return;
      const record = await saveView({ name, visibility, payload: saveModal.payload() });
      saveModal.onSaved?.(record);
    },
    [saveModal, saveView]
  );

  return (
    <div className="relative" data-popover-container={id}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Saved views"
        aria-expanded={isOpen}
        title="Saved views and templates"
        className={HEADER_BUTTON_CSS}
      >
        <Star className="w-4 h-4 text-[#667085]" /> Views
        {views.length + templates.length > 0 && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4EBFF] text-[#6941C6] text-[11px] font-bold leading-none">{views.length + templates.length}</span>
        )}
      </button>

      {isOpen && (
        <div
          style={{ backgroundColor: '#FFFFFF' }}
          className="absolute right-0 top-11 z-[70] pointer-events-auto w-72 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3 flex flex-col gap-1"
        >
          <div className="text-[13px] font-bold text-[#101828] px-1.5 pb-1">Saved views</div>
          {/* Wendi (2026-10-22), item 12: templates first, before the user's saved views. */}
          {templates.length > 0 && (
            <div role="group" aria-label={TEMPLATES_GROUP_LABEL} className="flex flex-col gap-0.5 border-b border-[#EAECF0] pb-2 mb-1">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#98A2B3] px-1.5 pt-1">{TEMPLATES_GROUP_LABEL}</div>
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { t.onApply(); setIsOpen(false); }}
                  title={`${t.description} Only admins see templates: use Save view → Shared to give this client's buyers the view.`}
                  className="flex items-start gap-2 px-1.5 py-1.5 rounded-lg hover:bg-[#F9FAFB] text-left"
                >
                  <LayoutTemplate className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#7F56D9]" aria-hidden="true" />
                  <span className="flex flex-col min-w-0">
                    <span className="text-[13px] font-semibold text-[#344054] truncate">{t.name}</span>
                    <span className="text-[11px] text-[#667085] leading-snug line-clamp-2">{t.description}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {status === 'loading' && views.length === 0 && <div className="text-[12px] text-[#94A3B8] px-1.5 py-2">Loading saved views…</div>}
          {status === 'error' && (
            <div className="text-[12px] text-[#B42318] px-1.5 py-1">
              {isLocalFallback ? 'Saved views could not be loaded; showing the ones kept in this browser.' : 'Saved views could not be loaded. Please try again later.'}
            </div>
          )}
          {status === 'ready' && views.length === 0 && (
            <div className="text-[12px] text-[#94A3B8] px-1.5 py-2">No saved views yet — use "Save view" to save your current filters.</div>
          )}
          {groups.length > 0 && (
            <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
              {groups.map((group) => (
                <div key={group.visibility} role="group" aria-label={group.label} className="flex flex-col gap-0.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-[#98A2B3] px-1.5 pt-1">{group.label}</div>
                  {group.views.map((view) => {
                    const favorite = isFavorite(view.id);
                    const hovered = hoveredId === view.id;
                    return (
                      <div
                        key={view.id}
                        onMouseEnter={() => setHoveredId(view.id)}
                        onMouseLeave={() => setHoveredId((h) => (h === view.id ? null : h))}
                        className="flex items-center justify-between gap-2 px-1.5 py-1.5 rounded-lg hover:bg-[#F9FAFB]"
                      >
                        <button
                          type="button"
                          onClick={() => toggleFavorite(view.id)}
                          aria-pressed={favorite}
                          aria-label={favorite ? `Remove ${view.name} from favorites` : `Mark ${view.name} as favorite`}
                          className="shrink-0 text-[#D0D5DD] hover:text-[#F79009] transition-colors"
                        >
                          <Star className={cn('w-3.5 h-3.5', favorite && 'fill-[#F79009] text-[#F79009]')} />
                        </button>
                        <button type="button" onClick={() => handleApply(view)} className="flex-1 min-w-0 text-left flex flex-col" title={view.name}>
                          <span className="text-[13px] font-semibold text-[#344054] truncate">{view.name}</span>
                          {view.visibility === 'shared' && (
                            <span className="text-[11px] text-[#667085] flex items-center gap-1 truncate">
                              <Users className="w-3 h-3 shrink-0" />
                              {sharedCaption(view)}
                            </span>
                          )}
                        </button>
                        {view.canEdit && (
                          <button
                            type="button"
                            onClick={() => handleDelete(view)}
                            aria-label={`Delete ${view.name}`}
                            title="Delete this saved view"
                            className={cn('text-[#98A2B3] hover:text-[#D92D20] text-xs font-semibold transition-opacity px-1', hovered ? 'opacity-100' : 'opacity-0')}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {saveModal && <SaveViewModal open={saveModal.open} onClose={saveModal.onClose} onSave={handleSave} />}
    </div>
  );
};

// Share link (spec 6 "Views"): copies the share URL (useReportViewState.shareUrl) to the
// clipboard and says "Copied" for a moment. Without the async clipboard API (http origins, old
// WebViews) or when it refuses, the link is shown in a read-only input in a small popover so it can
// still be copied by hand (never window.prompt: it throws in some embedded WebViews).

export const COPIED_RESET_MS = 2000;

/** True when the clipboard took it; false when it is missing or refused (the caller then shows ShareLinkPopover). */
export const copyShareUrl = async (url: string): Promise<boolean> => {
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (!clipboard || typeof clipboard.writeText !== 'function') return false;
  try {
    await clipboard.writeText(url);
    return true;
  } catch {
    // Permission denied or no focus.
    return false;
  }
};

export type ShareLinkCloseReason = 'escape' | 'close' | 'outside';

export interface ShareLinkPopoverProps {
  url: string;
  /** Why it closed: the Share button takes focus back for 'escape' and 'close', not for an outside click (focus goes where the user clicked). */
  onClose: (reason: ShareLinkCloseReason) => void;
  /** Position classes; the card is `absolute` so the parent must be `relative`. */
  className?: string;
}

/** The manual-copy fallback: the link selected in a read-only input. Closes on Escape, outside mousedown or Close. */
export const ShareLinkPopover: React.FC<ShareLinkPopoverProps> = ({ url, onClose, className = 'right-0 top-11' }) => {
  const card = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    input.current?.focus();
    input.current?.select();
    const onMouseDown = (e: MouseEvent) => {
      if (card.current && !card.current.contains(e.target as Node)) onCloseRef.current('outside');
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current('escape');
        return;
      }
      if (e.key !== 'Tab' || !card.current) return;
      // Trap focus: Tab / Shift+Tab wrap inside the card while it is open.
      const items = Array.from(card.current.querySelectorAll<HTMLElement>('input, button')).filter((el) => !el.hasAttribute('disabled'));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!card.current.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div
      ref={card}
      role="dialog"
      aria-label="Copy share link"
      style={{ backgroundColor: '#FFFFFF' }}
      className={`absolute z-[70] w-80 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl p-3 flex flex-col gap-2 ${className}`}
    >
      <label htmlFor="share-link-manual" className="text-[12px] font-medium text-[#667085]">
        Copy this link to share the view
      </label>
      <p className="text-[12px] text-[#667085]">The link includes your current search text.</p>
      <input
        id="share-link-manual"
        ref={input}
        type="text"
        readOnly
        value={url}
        aria-label="Share link"
        onFocus={(e) => e.currentTarget.select()}
        className="h-9 w-full px-2.5 border border-[#D0D5DD] rounded-[9px] text-[13px] text-[#344054] bg-[#F9FAFB]"
      />
      <button type="button" onClick={() => onClose('close')} className="self-end text-[13px] font-semibold text-[#344054] hover:text-[#09090B]">
        Close
      </button>
    </div>
  );
};

export interface ShareLinkButtonProps {
  /** The URL, or a function building it on click (so the state is read at that moment). */
  url: string | (() => string);
  onCopied?: (url: string) => void;
  className?: string;
}

export const ShareLinkButton: React.FC<ShareLinkButtonProps> = ({ url, onCopied, className = HEADER_BUTTON_CSS }) => {
  const [copied, setCopied] = useState(false);
  const [manualLink, setManualLink] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  // Escape and the Close button put focus back on the Share button; an outside click leaves it where the user clicked.
  const closeManual = useCallback((reason: ShareLinkCloseReason) => {
    setManualLink(null);
    if (reason !== 'outside') trigger.current?.focus();
  }, []);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const handleClick = useCallback(async () => {
    const link = typeof url === 'function' ? url() : url;
    const ok = await copyShareUrl(link);
    if (!ok) {
      setManualLink(link);
      return;
    }
    setManualLink(null);
    setCopied(true);
    onCopied?.(link);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
  }, [url, onCopied]);

  return (
    <div className="relative">
      <button ref={trigger} type="button" onClick={handleClick} className={className} title="Copy a link that opens this view">
        {copied ? <Check className="w-4 h-4 text-[#17B26A]" /> : <Link2 className="w-4 h-4 text-[#667085]" />}
        {copied ? 'Copied' : 'Share link'}
      </button>
      {manualLink !== null && <ShareLinkPopover url={manualLink} onClose={closeManual} />}
    </div>
  );
};

// Export CSV opens this dialog (it replaced the header's "Include expanded rows" checkbox). The same shell as the Save
// view modal (portal into document.body, z-[10000], Escape and the backdrop close it). Every report chooses the rows (top level only, or
// with the rows you opened); Weekly Action / Inventory Health also choose the columns (shown, or every column the report has), Plan & OTB
// the lines (as shown, or every metric with Plan, Act and LY).

export type ExportRows = 'shown' | 'top';
export type ExportColumns = 'shown' | 'all';
export interface ExportChoice { rows: ExportRows; columns: ExportColumns }

export interface ExportCsvModalProps {
  open: boolean;
  onClose: () => void;
  onExport: (choice: ExportChoice) => void;
  /** What the second choice is about: the table's columns, or Plan & OTB's matrix lines. */
  kind: 'columns' | 'lines';
}

const EXPORT_PILL_CSS = 'flex-1 min-h-11 px-3 py-2 rounded-[10px] text-[13px] font-semibold flex flex-col items-center justify-center text-center transition-colors';
const EXPORT_PILL_ON = 'bg-[#7C3AED] text-white shadow-sm';
const EXPORT_PILL_OFF = 'bg-white border border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]';

function Choice<T extends string>({ id, label, value, options, onChange }: { id: string; label: string; value: T; options: { value: T; label: string; hint: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-col gap-2">
      <span id={id} className="text-[14px] font-medium text-[#344054]">{label}</span>
      <div role="radiogroup" aria-labelledby={id} className="flex items-stretch gap-2.5">
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)} className={cn(EXPORT_PILL_CSS, value === o.value ? EXPORT_PILL_ON : EXPORT_PILL_OFF)}>
            {o.label}
            <span className={cn('text-[11px] font-medium', value === o.value ? 'text-white/80' : 'text-[#667085]')}>{o.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export const ExportCsvModal: React.FC<ExportCsvModalProps> = ({ open, onClose, onExport, kind }) => {
  const [rows, setRows] = useState<ExportRows>('shown');
  const [columns, setColumns] = useState<ExportColumns>('shown');
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  if (!open || typeof document === 'undefined') return null;
  const lines = kind === 'lines';
  return createPortal(
    <div
      data-testid="export-csv-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#101828]/50 backdrop-blur-[2px] animate-in fade-in duration-150"
    >
      <div role="dialog" aria-modal="true" aria-labelledby="export-csv-title" className="bg-white rounded-2xl p-6 w-[520px] max-w-[calc(100vw-32px)] shadow-2xl border border-slate-100 flex flex-col gap-4 animate-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between">
          <h3 id="export-csv-title" className="text-[18px] font-bold text-[#101828]">Export CSV</h3>
          <button type="button" aria-label="Close" onClick={onClose} className="text-[#98A2B3] hover:text-[#101828] transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-[14px] text-[#667085] leading-relaxed">Plain numbers for a spreadsheet, on the basis, period and filters you have on screen.</p>
        <Choice
          id="export-rows"
          label="Rows"
          value={rows}
          onChange={setRows}
          options={[
            { value: 'shown', label: 'As shown', hint: 'with the rows you opened, and a Level column' },
            { value: 'top', label: 'Top level only', hint: 'the rows you see with nothing opened' },
          ]}
        />
        <Choice
          id="export-columns"
          label={lines ? 'Lines' : 'Columns'}
          value={columns}
          onChange={setColumns}
          options={lines ? [
            { value: 'shown', label: 'As shown', hint: 'the mode, rows and options you picked' },
            { value: 'all', label: 'All lines', hint: 'every metric, Plan, Act and LY' },
          ] : [
            { value: 'shown', label: 'Columns shown', hint: 'in the table\'s order' },
            { value: 'all', label: 'All columns', hint: 'every column this report has' },
          ]}
        />
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-[10px] border border-[#D0D5DD] text-[14px] font-semibold text-[#344054] hover:bg-[#F9FAFB]">Cancel</button>
          <button type="button" onClick={() => { onExport({ rows, columns }); onClose(); }} className="h-10 px-4 rounded-[10px] bg-[#7C3AED] text-white text-[14px] font-semibold hover:bg-[#6D28D9] flex items-center gap-2">
            <Download className="w-4 h-4" aria-hidden="true" /> Export
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};


// The line under the report title (spec 6). the visible line is SHORT — tier, grain, plan source, data-through date and
// the benchmark month, e.g. "Tier A · Weekly · V3 plan v12 · data through Oct 16 · Benchmark: Sep 2026" — and the rest sits in its
// tooltip: the full line (`formatDataSourceLine`): grain, actuals source, plan source and version, V2 history, data freshness, the last
// sales day a Sales Trend counts through (only when the payload trends), the inventory note and the benchmark month, e.g. "Weekly ·
// SKU data · V3 plan v12 · data through Oct 16 · inventory estimated before Oct 18 · Benchmark: Sep 2026". The sources come from the
// payload's `capabilities` / `sources` blocks; unknown table names are shown as-is rather than hidden. The How it works panel shows the full line.

export interface DataSourceLineProps {
  grain: Grain;
  actualsSource: string | null | undefined;
  planSource: string | null | undefined;
  planVersion: number | null | undefined;
  /** D6: the client's first V3 monthly-plan epoch month when Monthly months before it use the V2 plan as it stood ("V2 plan before Aug 2026"). */
  planV2HistoryBefore?: number | null;
  dataThrough: string | null | undefined;
  /** The last sales day (payload.salesThrough), passed only when the payload has a trend block. */
  salesThrough?: string | null;
  /** days of the target period after salesThrough ("3 days to come"); null / 0 / absent: none to name. */
  salesDaysToCome?: number | null;
  inventoryNote?: string | null;
  /** the resolved benchmark label ("Benchmark: Sep 2026"), always the last part; null / absent when none. */
  benchmarkLabel?: string | null;
  /** the client's data tier ("A" / "B" / "C"), the first part of the short line; absent = left out. */
  tier?: string | null;
  /** Show the full line instead of the short one + tooltip (the How it works panel). */
  full?: boolean;
  /** the grain just switched and its payload is still loading: the line reads "Loading Monthly…" instead of the old grain's sources. */
  loading?: boolean;
}

export const SEPARATOR = ' · ';

const ACTUALS_LABELS: Record<string, string> = {
  'Staging.sku_detail_*': 'SKU data',
  'Reporting.ReportDataSource': 'Monthly data',
};

export const actualsSourceLabel = (source: string | null | undefined): string | null => {
  if (!source) return null;
  return ACTUALS_LABELS[source] ?? source;
};

export const planSourceLabel = (source: string | null | undefined, version: number | null | undefined): string | null => {
  if (!source) return null;
  if (source.startsWith('planbuilderv3.')) return version ? `V3 plan v${version}` : 'V3 plan';
  if (source === 'Reporting.ReportDataSource') return 'V2 plan';
  // D4: Weekly without weekly plans (Tier B, or a client not allowed them) is actuals only.
  if (source === 'none_weekly') return 'No weekly plan · actuals only';
  // Monthly with no plan rows (or only NULL / zero plans) for the period: no plan, no OTB.
  if (source === 'none_plan') return 'No plan loaded for this period';
  return source;
};

export const formatDataSourceLine = (p: DataSourceLineProps): string =>
  p.loading ? `Loading ${p.grain === 'weekly' ? 'Weekly' : 'Monthly'}…` :
  [
    p.grain === 'weekly' ? 'Weekly' : 'Monthly',
    actualsSourceLabel(p.actualsSource),
    planSourceLabel(p.planSource, p.planVersion),
    p.grain === 'monthly' && typeof p.planV2HistoryBefore === 'number' ? `V2 plan before ${formatPeriodLabel(p.planV2HistoryBefore, 'monthly')}` : null,
    p.dataThrough ? `data through ${fmtDate(p.dataThrough)}` : null,
    p.salesThrough ? `Sales through ${fmtDate(p.salesThrough)}${p.salesDaysToCome && p.salesDaysToCome > 0 ? ` (${p.salesDaysToCome} ${p.salesDaysToCome === 1 ? 'day' : 'days'} to come)` : ''}` : null,
    p.inventoryNote || null,
    p.benchmarkLabel || null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(SEPARATOR);

/** The short line: tier, grain, plan source, data through, benchmark month. */
export const formatShortDataSourceLine = (p: DataSourceLineProps): string =>
  p.loading ? formatDataSourceLine(p) :
  [
    p.tier ? `Tier ${p.tier}` : null,
    p.grain === 'weekly' ? 'Weekly' : 'Monthly',
    planSourceLabel(p.planSource, p.planVersion),
    p.dataThrough ? `data through ${fmtDate(p.dataThrough)}` : null,
    p.benchmarkLabel || null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(SEPARATOR);

const LINE_CSS = 'text-[12px] font-medium text-[#71717A] mt-1 leading-normal tabular-nums';

export const DataSourceLine: React.FC<DataSourceLineProps> = (props) => {
  const fullLine = formatDataSourceLine(props);
  if (props.full || props.loading) return <p className={LINE_CSS}>{fullLine}</p>;
  const short = formatShortDataSourceLine(props);
  return (
    <p className={`${LINE_CSS} flex items-center gap-1.5 min-w-0`}>
      <span className="truncate" title={short}>{short}</span>
      {fullLine !== short && <InfoTooltip text={fullLine} widthClass="w-72" positionClass="top-full left-0 mt-1.5" />}
    </p>
  );
};

// The Vendor Performance header row (VP L2591-2693): icon box, title, subtitle, then the action
// buttons. Adds the data source line, How it works, Share link and an optional Favorite star (spec 6). The
// saved-views menu is passed in as a node so this file stays free of view state.
// `shadow-2xs` from VP is a Tailwind v4 utility; v3 gets `shadow-sm`.

export const ICON_BUTTON_CSS =
  'h-9 w-9 bg-white border border-[#E4E4E7] rounded-[9px] text-xs text-[#667085] hover:text-[#09090B] hover:bg-[#F4F4F5] shadow-sm flex items-center justify-center transition-colors';

export interface ReportPageHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  dataSource: DataSourceLineProps;
  /** Gets the click event, so the caller can return focus to `currentTarget` (Safari does not focus buttons on click). */
  onHowItWorks: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onSaveView: () => void;
  onShare: () => void;
  onExport: () => void;
  exportDisabled?: boolean;
  /** Shows an "Include expanded rows" checkbox beside Export CSV; omitted -> no checkbox. */
  /**
   * Optional report-level favourite star; omitted → no star. (There is no report-level favourites
   * service yet; VP's star is the per-saved-view favourite inside SavedViewsMenu.)
   */
  favorite?: { isFavorite: boolean; onToggle: () => void };
  savedViewsMenu: React.ReactNode;
  /** the Table / Dashboard switch, first in the action buttons; omitted → none. */
  displayToggle?: React.ReactNode;
  /** Item 12: the first-visit hint under the title ("Start from a template or pick your columns"); omitted → none. */
  hint?: string | null;
}

export const ReportPageHeader: React.FC<ReportPageHeaderProps> = ({
  icon,
  title,
  subtitle,
  dataSource,
  onHowItWorks,
  onSaveView,
  onShare,
  onExport,
  exportDisabled = false,
  favorite,
  savedViewsMenu,
  displayToggle,
  hint,
}) => {
  const favoriteLabel = favorite?.isFavorite ? 'Remove from favorites' : 'Mark as favorite';
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mt-0.5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#F5F0FF] border border-[#E9D7FE] flex items-center justify-center text-[#7C3AED] shadow-sm shrink-0 [&>svg]:w-5 [&>svg]:h-5">
          {icon}
        </div>
        <div>
          <h1 className="text-[22px] font-bold text-[#09090B] tracking-tight leading-tight">{title}</h1>
          {subtitle && <p className="text-[13px] text-[#71717A] mt-1 max-w-xl leading-normal">{subtitle}</p>}
          {hint && <p data-testid="first-visit-hint" className="text-[12px] font-medium text-[#6941C6] mt-1 leading-normal">{hint}</p>}
          <DataSourceLine {...dataSource} />
        </div>
      </div>

      <div data-header-actions className="flex flex-wrap items-center gap-2">
        {displayToggle}
        <button type="button" onClick={onHowItWorks} className={HEADER_BUTTON_CSS}>
          <HelpCircle className="w-4 h-4 text-[#667085]" /> How it works
        </button>
        {/* the saved views list sits beside Save view, so a view you save is where you look for it. */}
        {savedViewsMenu}
        <button type="button" onClick={onSaveView} className={HEADER_BUTTON_CSS}>
          <Bookmark className="w-4 h-4 text-[#667085]" /> Save view
        </button>
        <button type="button" onClick={onShare} className={HEADER_BUTTON_CSS}>
          <Link2 className="w-4 h-4 text-[#667085]" /> Share link
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onExport} disabled={exportDisabled} className={HEADER_BUTTON_CSS}>
            <Download className="w-4 h-4 text-[#667085]" /> Export CSV
          </button>
        </div>
        {favorite && (
          <button
            type="button"
            aria-label={favoriteLabel}
            aria-pressed={favorite.isFavorite}
            title={favoriteLabel}
            onClick={favorite.onToggle}
            className={ICON_BUTTON_CSS}
          >
            <Star className={cn('w-4 h-4', favorite.isFavorite ? 'fill-[#F79009] text-[#F79009]' : 'text-[#667085]')} />
          </button>
        )}
      </div>
    </div>
  );
};

// The Vendor Performance filter bar card (VP L2695) holding two rows (spec 6): the filter row
// (period, grain, the FilterDropdowns) and the view-control row (hierarchy, Show SKUs, lookback,
// compare, search, columns). Each row is a flex-wrap so controls fold on narrow screens. tighter gaps so the
// weekly planning rows fit one line each at about 1440px, and `min-w-0` so a row never pushes the card wider than the page.

export interface ReportControlBarProps {
  filterRow: React.ReactNode;
  viewRow?: React.ReactNode;
  /** Item 11: the Advanced group's controls, a third row shown only while the Advanced button is open (null / undefined: no row). */
  advancedRow?: React.ReactNode;
  advancedId?: string;
}

export const CONTROL_BAR_CSS = 'flex flex-col gap-3 bg-white p-4 min-w-0 border border-[#E4E4E7] rounded-[16px] shadow-sm mt-1 relative z-40';
export const CONTROL_ROW_CSS = 'flex flex-wrap items-end gap-x-2 gap-y-3 min-w-0';

export const ReportControlBar: React.FC<ReportControlBarProps> = ({ filterRow, viewRow, advancedRow, advancedId }) => (
  <div className={CONTROL_BAR_CSS}>
    <div data-row="filters" className={CONTROL_ROW_CSS}>
      {filterRow}
    </div>
    {viewRow && (
      <div data-row="view" className={`${CONTROL_ROW_CSS} border-t border-[#EAECF0] pt-3`}>
        {viewRow}
      </div>
    )}
    {advancedRow && (
      <div data-row="advanced" id={advancedId} role="group" aria-label="Advanced" className={`${CONTROL_ROW_CSS} items-start border-t border-[#EAECF0] pt-3`}>
        {advancedRow}
      </div>
    )}
  </div>
);

// The applied filters as removable chips under the control bar, with "Clear all". Renders nothing
// when no filter is active so the KPI row sits straight under the bar.

export interface ActiveFilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

export interface ActiveFilterChipsProps {
  chips: ActiveFilterChip[];
  onClearAll: () => void;
}

export const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({ chips, onClearAll }) => {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((chip) => (
        <Pill key={chip.key} tone="brand" onRemove={chip.onRemove} removeLabel={chip.label}>
          {chip.label}
        </Pill>
      ))}
      <button type="button" onClick={onClearAll} className="text-[12px] font-semibold text-[#667085] hover:text-[#101828] px-1">
        Clear all
      </button>
    </div>
  );
};

// The row count under the toolbar (VP L3705): "12 of 48 classes" while filtered, "48 classes"
// otherwise. `noun` is the plural display word ("classes", "vendors", "SKUs").

export interface CountCaptionProps {
  shown: number;
  total: number;
  noun: string;
  /** Round 2: a bracketed note after the count, e.g. "(87 location-classes)". */
  note?: string | null;
}

const n = (v: number): string => v.toLocaleString('en-US');

export const formatCountCaption = (shown: number, total: number, noun: string): string =>
  shown === total ? `${n(total)} ${noun}` : `${n(shown)} of ${n(total)} ${noun}`;

export const CountCaption: React.FC<CountCaptionProps> = ({ shown, total, noun, note }) => (
  <div className="text-[12px] font-medium text-[#A1A1AA] tracking-tight mt-0.5 tabular-nums">{formatCountCaption(shown, total, noun)}{note ? ` ${note}` : ''}</div>
);

// the page notices (view-state fallbacks, payload notes, part errors) collapse into ONE line, "3 notes", that opens on click.
// Error and suspect-data notices stay visible on their own while there are 3 or fewer of them (they are the ones to act on); more than 3
// of those fold into the line with the rest. A lone plain note reads as itself, not as "1 note". Each notice can still be dismissed.

/** Notices a buyer must see: a load failure ("Could not load…", a part error) or the backend's suspect-data flag. Plain data-gap notes ("…not available for this client") are notes. */
const PRIORITY = /^suspect data|could not|couldn't|failed|failure|error/i;
export const isPriorityNotice = (n: string): boolean => PRIORITY.test(n);
export const MAX_VISIBLE_PRIORITY = 3;

export interface NoticeListProps {
  notices: string[];
  onDismiss: (notice: string) => void;
}

const NoticeItem: React.FC<{ text: string; onDismiss: (n: string) => void }> = ({ text, onDismiss }) => (
  <li className="flex items-start gap-2 text-[12px] font-medium text-[#667085]">
    <span className="flex-1">{text}</span>
    <button type="button" aria-label={`Dismiss notice: ${text}`} onClick={() => onDismiss(text)} className="text-[#98A2B3] hover:text-[#344054]">
      <X className="w-3.5 h-3.5" aria-hidden="true" />
    </button>
  </li>
);

export const NoticeList: React.FC<NoticeListProps> = ({ notices, onDismiss }) => {
  const [open, setOpen] = useState(false);
  const priority = notices.filter(isPriorityNotice);
  const pinned = priority.length <= MAX_VISIBLE_PRIORITY ? priority : [];
  const rest = notices.filter((n) => !pinned.includes(n));
  const inlineOnly = rest.length === 1; // one plain note: show it as itself, not as "1 note"
  return (
    <ul role="status" aria-live="polite" className="flex flex-col gap-1 empty:hidden">
      {pinned.map((n) => <NoticeItem key={n} text={n} onDismiss={onDismiss} />)}
      {inlineOnly && <NoticeItem key={rest[0]} text={rest[0]} onDismiss={onDismiss} />}
      {rest.length > 0 && !inlineOnly && (
        <li className="flex flex-col gap-1">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="self-start inline-flex items-center gap-1 text-[12px] font-medium text-[#667085] hover:text-[#344054] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] rounded"
          >
            {open ? <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" /> : <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />}
            {rest.length} {rest.length === 1 ? 'note' : 'notes'}
          </button>
          {open && (
            <ul className="flex flex-col gap-1 pl-4">
              {rest.map((n) => <NoticeItem key={n} text={n} onDismiss={onDismiss} />)}
            </ul>
          )}
        </li>
      )}
    </ul>
  );
};

// The "How it works" slide-over (Plan 4 ): what the report shows, where its data comes from,
// what each visible column means (catalogue label for the grain + description) and what the client's
// tier supports (spec section 3). Rendered through a portal into document.body so `position: fixed`
// anchors to the viewport whatever wraps the report, and stacked above `ReportModal` (z-[9999], the
// full-screen wrapper the reports also open in) at backdrop z-[10000] / panel z-[10001], as
// SaveViewModal is. Escape, the close button and a click on the backdrop call `onClose`. While open,
// body scroll is locked (restored on close / unmount), Tab and Shift+Tab wrap within the panel, and
// Escape / Tab are handled on a document capture listener that stops propagation, so an outer
// Escape handler (a modal the report sits in) does not also fire. Focus moves to the close button
// on open; returning focus to the opener is the caller's job. a "Benchmarks" section
// (BENCHMARK_HOW_IT_WORKS, plus the Pro sentence at the location level) sits after the columns list
// whenever `benchmarkLevel` is not 'none'; its first line is the resolved "Benchmark: <month>" label.

/** A column described in How it works when a report supplies its own (`items`). */
export interface HowItWorksItem { label: string; description: string }

export interface HowItWorksPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  intro: string;
  dataSource: DataSourceLineProps;
  columns: MetricDefinition[];
  /** Replaces `columns` when given (a report whose table is not the catalogue's, e.g. Plan & OTB). */
  items?: HowItWorksItem[];
  grain: Grain;
  tier: Tier | null;
  /** Add the "Trended sales method" section (Part B: where the Trend select shows). */
  trendMethods?: boolean;
  /** the client's benchmark level (capabilities); the Benchmarks section shows unless 'none' (the default). */
  benchmarkLevel?: BenchmarkLevel;
  /** the resolved "Benchmark: <month>" label, shown as the section's first line when present. */
  benchmarkLabel?: string | null;
  /** W2: add the "Allocating to stores" section (Weekly Action, where the allocation columns are). */
  allocation?: boolean;
}

/** W2 (D12): "Allocating to stores", one paragraph each (kit/logic/allocation.ts has the rules). */
export const ALLOCATION_HOW_IT_WORKS: readonly string[] = [
  'Pick Class › Location (or Class › Location › Vendor) to see each class across the chain, then its stores, then its non-selling locations (warehouse, receiving, HQ, storage). The class row is the same row as in Total by Class: its totals and OTB are the chain\'s, with the non-selling locations\' inflows and on order netted against the OTB once.',
  'Need (wk) is what a store needs of the class this week to reach its target: target weeks of stock × the week\'s plan sales (the store\'s own average sales when it has no weekly plan) − (BOM inventory + on order due this week), never below 0. Monthly does the same per month: Need (mo). Available is the stock on hand at a non-selling location (on order due there is not counted). Both are retail: plans have no sales at cost.',
  'On the class row, Need is the stores\' total and Available the non-selling locations\' total. Shortfall / surplus is need − available: a shortfall is what you still need to buy or chase, a surplus what to hold in the warehouse. Stores are served largest need first from the location with the most stock; a store that gets stock reads "Allocate $X from <location>" in Action, and the row detail lists every store ranked by need.',
  'A class with sub-classes is judged at the parent (the plan\'s level): its store rows sit under the parent, each the family\'s total at that store, with the sub-classes below it. A store with no sales, stock, plan or on order in the window (a closed or NOTB store) is left out.',
  'Ship / hold (Class › Location › Vendor, and Class by Vendor): a vendor\'s on order due this week is compared with the location\'s need for the class before on order (target weeks × plan sales − BOM), less what the other vendors of the class have due this week. Above it reads Hold PO; within it, Take PO, so an order that exactly fills the gap is a Take PO. Every vendor of a class at a location gets the same hint: Hold PO when their orders together are more than the need. It is a hint only, shown where the order has a due date, and changes no status. Allocation moves stock you already own; it is not open to buy.',
];

/** Buyer language: the short glossary under Columns (every report shows it, whatever its columns). */
export const HOW_IT_WORKS_GLOSSARY: readonly HowItWorksItem[] = [
  { label: 'BOM', description: 'Beginning of month (or week): the inventory you have on hand at the start of the period.' },
  { label: 'WOS / MOS', description: 'Weeks (Weekly) or months (Monthly) of stock: BOM inventory ÷ average sales per week or month, so how long your stock lasts at the recent selling rate.' },
  { label: 'OTB', description: 'Open to buy: what you can still buy over the span, which is planned inflows − received to date − on order.' },
  { label: 'OTB + Dev', description: 'OTB plus the gap between planned and actual BOM inventory ("Dev" is deviation): lower when you start the period over plan, higher when under.' },
  { label: 'OTB buy window', description: "Start the open-to-buy at a later week or month to plan a future buy; the report's status and cover still judge the target period. Count on order: due in the window (the rule) or all open orders." },
  { label: 'On order timing', description: ON_ORDER_TIMING_CAVEAT },
  { label: 'Trended sales', description: "Where the period's sales are heading: sales so far ÷ % of period elapsed. Shown only while the period is still in progress." },
  { label: '% of period elapsed', description: "How much of the period's sales you would expect to have happened by now: from the plan's daily pattern, else last year's daily sales, else simply the days elapsed." },
  { label: 'Non-selling location', description: 'A location you own but do not sell from: a warehouse, distribution center, receiving, headquarters, storage, stockroom, "to be split" or events location, going by its name, that has sold nothing in the report\'s periods (no sales dollars and no units) nor in the last 52 weeks. Its inventory, inflows and on order count in the chain and class totals and net against the OTB, but its rows get no status, action, target or suggested reorder, and a location that did sell is a store whatever its name.' },
];

/** the Benchmarks section, one paragraph each (shown at the location and class levels). */
export const BENCHMARK_HOW_IT_WORKS: readonly string[] = [
  'Benchmarks compare you with peer retailers in the same vertical from the Retail ORBIT benchmark pool, for one complete month: in Monthly the month you picked (or the latest complete month while it is still running), in Weekly the latest complete month. The month is shown as "Benchmark: <month>".',
  'Class rows compare the class (with its child classes) with peers in the same vertical and master class. Location rows compare the store, summed from its classes, with the vertical. The KPIs and "You vs peers" compare the whole business with the vertical. Vendor, product and SKU rows show no benchmark: no brand-level peer group has enough members.',
  'A peer value is shown only when at least 4 peer companies report that group in that month over a 24-month history; otherwise "not enough peers". Sell-through and cash margin also need comparable inflows. Every ratio is worked out from summed dollars, never averaged.',
  'Definitions (one month): sell-through = sales ÷ (beginning inventory + inflows); weeks of stock = month-end inventory ÷ sales per week; turnover = sales ÷ month-end inventory (× 12 ≈ annual); stock-to-sales = beginning inventory ÷ sales; markdown % = markdowns ÷ sales; gross margin over the sales that carry a cost; cash margin = (sales − inflows at cost) ÷ sales; sales vs LY = the month against the same month last year. Index = you ÷ peers (1.00 = on the benchmark); sales vs LY shows the difference in points.',
  'Caveats: clients whose data has no cost show no gross margin of their own while peers do; a large peer can weigh heavily in a pooled value; these definitions are the benchmark\'s, not the report\'s own WOS or Fresh %.',
];
/** added at the location level only (Basic Member: location and client benchmarks; class level is Pro). */
export const BENCHMARK_PRO_SENTENCE = 'Your plan includes location-level benchmarks; class-level benchmarks come with Indie Insights Pro.';

/** One sentence per tier (spec 3); an unknown tier (`null`, degraded capabilities) reads as Tier C. */
export const TIER_SENTENCES: Record<Tier, string> = {
  A: 'Your data supports everything: Weekly and Monthly from daily sales, V3 weekly and monthly plans, and the Vendor and SKU views.',
  B: 'Your data supports Weekly and Monthly from daily sales, with V2 monthly plans: Weekly shows actuals only ("No weekly plan": weekly plans are V3), Monthly shows the plan when one is loaded ("No plan loaded for this period" otherwise: no OTB), and the Vendor and SKU views are available.',
  C: 'Your data supports Monthly only, at class level (Category, Class, Location): SKU-level controls and columns such as the vendor and product views, Show SKUs, SKU search and aging are hidden.',
};

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SECTION_TITLE = 'text-[12px] font-semibold uppercase tracking-wide text-[#71717A] mb-2';

export const HowItWorksPanel: React.FC<HowItWorksPanelProps> = ({ open, onClose, title, intro, dataSource, columns, items, grain, tier, trendMethods = false, benchmarkLevel = 'none', benchmarkLabel = null, allocation = false }) => {
  const titleId = useId();
  const benchmarksId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Read through a ref so an inline onClose from a parent re-render does not re-bind the listener.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const inside = active instanceof Node && panelRef.current.contains(active);
      if (e.shiftKey ? !inside || active === first : !inside || active === last) {
        e.preventDefault();
        e.stopPropagation();
        (e.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div data-testid="how-it-works-backdrop" className="fixed inset-0 bg-[#09090B]/20 z-[10000]" onClick={() => onClose()} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed inset-y-0 right-0 w-[420px] max-w-full bg-white border-l border-[#E4E4E7] shadow-xl z-[10001] flex flex-col text-[#09090B] font-sans antialiased"
      >
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[#E4E4E7]">
          <h2 id={titleId} className="text-[16px] font-semibold leading-snug">{title}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={() => onClose()}
            aria-label="Close"
            className="h-8 w-8 flex items-center justify-center rounded-md text-[#71717A] hover:bg-[#F4F4F5] hover:text-[#09090B] transition-colors"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-6">
          <section>
            <p className="text-[14px] leading-relaxed text-[#3F3F46]">{unitize(intro, grain)}</p>
            <DataSourceLine {...dataSource} full />
          </section>
          <section>
            <h3 className={SECTION_TITLE}>Columns</h3>
            <dl className="flex flex-col gap-3">
              {(items ? items.map((d) => ({ label: unitize(d.label, grain), description: unitize(d.description, grain) })) : columns.map((d) => ({ label: columnLabel(d, grain), description: unitize(d.description, grain) }))).map((d, i) => (
                <div key={`${i}:${d.label}`}>
                  <dt className="text-[13px] font-semibold">{d.label}</dt>
                  <dd className="text-[13px] leading-relaxed text-[#52525B]">{d.description}</dd>
                </div>
              ))}
            </dl>
          </section>
          <section>
            <h3 className={SECTION_TITLE}>Glossary</h3>
            <dl className="flex flex-col gap-3">
              {HOW_IT_WORKS_GLOSSARY.map((d) => (
                <div key={d.label}>
                  <dt className="text-[13px] font-semibold">{unitize(d.label, grain)}</dt>
                  <dd className="text-[13px] leading-relaxed text-[#52525B]">{unitize(d.description, grain)}</dd>
                </div>
              ))}
            </dl>
          </section>
          {benchmarkLevel !== 'none' && (
            <section aria-labelledby={benchmarksId}>
              <h3 id={benchmarksId} className={SECTION_TITLE}>Benchmarks</h3>
              <div className="flex flex-col gap-2">
                {benchmarkLabel && <p className="text-[13px] font-semibold tabular-nums">{benchmarkLabel}</p>}
                {BENCHMARK_HOW_IT_WORKS.map((text) => (
                  <p key={text} className="text-[13px] leading-relaxed text-[#52525B]">{unitize(text, grain)}</p>
                ))}
                {benchmarkLevel === 'location' && <p className="text-[13px] leading-relaxed text-[#52525B]">{BENCHMARK_PRO_SENTENCE}</p>}
              </div>
            </section>
          )}
          {allocation && (
            <section>
              <h3 className={SECTION_TITLE}>Allocating to stores</h3>
              <div className="flex flex-col gap-2">
                {ALLOCATION_HOW_IT_WORKS.map((text) => (
                  <p key={text} className="text-[13px] leading-relaxed text-[#52525B]">{unitize(text, grain)}</p>
                ))}
              </div>
            </section>
          )}
          {trendMethods && (
            <section>
              <h3 className={SECTION_TITLE}>Trended sales method</h3>
              <p className="text-[13px] leading-relaxed text-[#52525B]">{TREND_METHODS_HELP}</p>
            </section>
          )}
          <section>
            <h3 className={SECTION_TITLE}>What your data supports</h3>
            <p className="text-[13px] leading-relaxed text-[#52525B]">{TIER_SENTENCES[tier ?? 'C']}</p>
          </section>
        </div>
      </div>
    </>,
    document.body,
  );
};

// What one weekly planning report supplies to the shared page shell (Plan 4 ): its title and
// icon, its KPIs and dashboard cards, the extra controls it adds to the two control rows, and the
// optional overrides (its own table, row detail, column filtering / labels, its own CSV). Everything
// else (capabilities, view state, fetching, tier gating, header actions) is the shell's.
//
// Configs must be module-level constants: the shell memoises on the config's identity (KPIs,
// columns), so a config (or its functions) rebuilt on every render defeats the memos.

export interface ReportPageConfig {
  reportKey: ReportKey;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  howItWorksIntro: string;
  kpis: (m: WeeklyPlanningReportModel) => KpiItem[];
  dashboardCards: (m: WeeklyPlanningReportModel) => React.ReactNode[];
  /** e.g. Inventory Health's "$ Detail / Compact"; shown in the second row, after `extraViewControls`. */
  extraFilterControls?: (m: WeeklyPlanningReportModel) => React.ReactNode;
  /** e.g. Weekly Action's WOS target input and Sort, Plan & OTB's Mode / Rows / Options: the second row, after Overstock at. */
  extraViewControls?: (m: WeeklyPlanningReportModel) => React.ReactNode;
  /** Plan & OTB's matrix; default = ReportTable. */
  renderTable?: (m: WeeklyPlanningReportModel) => React.ReactNode;
  /** Expanded-row panel (IH, WA). */
  renderRowDetail?: (m: WeeklyPlanningReportModel, flatRow: FlatRow) => React.ReactNode | null;
  /** e.g. IH Compact / freshness off: the column ids the table may show, applied after the shell's own freshness filter. */
  columnsFilter?: (m: WeeklyPlanningReportModel, ids: string[]) => string[];
  /** e.g. IH "Actual Inv (start of period)". */
  columnLabelOverrides?: Record<string, string>;
  /** Plan & OTB exports its matrix; `includeExpanded` is the header's "Include expanded rows" checkbox (true: expanded rows with a Level column; false: top-level rows only). */
  exportCsv?: (m: WeeklyPlanningReportModel, options?: { includeExpanded: boolean; allLines?: boolean }) => void;
  /** Default true. */
  supportsVendorView?: boolean;
  /**
   * The report's own table ignores the catalogue columns (Plan & OTB's matrix): leave out the Columns
   * menu and the "% of Total" switch. An explicit flag, not inferred from `renderTable`.
   */
  hideColumnControls?: boolean;
  /** When given, what How it works lists under "Columns" instead of the catalogue's visible columns (computed only while the panel is open). */
  howItWorksColumns?: (m: WeeklyPlanningReportModel) => HowItWorksItem[];
  /**
   * Offer the "Freshness" switch (shown when the client has receiving data). Without it the
   * freshness columns follow the receiving capability only and `freshnessShown` is ignored, so a
   * hidden switch never hides columns. Inventory Health sets it.
   */
  showFreshnessToggle?: boolean;
}

// Pure helpers of the weekly planning page shell (WeeklyPlanningReportPage): the data source line,
// the active filter chips and the row count. Kept here so the page stays a layout.

/** The freshness group of the catalogue (hidden together by the Freshness switch / no receiving data). */
export const FRESHNESS_COLUMN_IDS: readonly string[] = METRIC_DEFINITIONS.filter((d) => d.group === 'freshness').map((d) => d.id);

const periodStartIso = (grain: Grain, period: number): string =>
  (grain === 'weekly' ? epochWeekStart(period) : new Date(Date.UTC(1970 + Math.floor(period / 12), period % 12, 1))).toISOString();

/** The header's data source line: payload sources first, the capabilities' before the first payload. */
export const dataSourceOf = (payload: ClassReportPayload | null, capabilities: Capabilities | null, grain: Grain, currentPeriod: number, loading = false): DataSourceLineProps => {
  // after a grain switch the old grain's payload stays until the new one arrives; the line then says only that it is loading.
  if (loading && payload !== null && payload.grain !== grain) return { grain, actualsSource: null, planSource: null, planVersion: null, dataThrough: null, loading: true };
  const g = payload?.grain ?? grain;
  const estimated = payload?.rows.some((r) => Object.values(r.periods).some((c) => c?.isEstimated)) ?? false;
  return {
    grain: g,
    tier: capabilities?.tier ?? null,
    actualsSource: payload?.sources.actuals ?? capabilities?.actualsSource,
    planSource: payload?.sources.plans ?? capabilities?.planSource,
    planVersion: payload?.sources.planVersion,
    planV2HistoryBefore: payload?.sources.planV2HistoryBefore ?? null,
    dataThrough: payload?.dataThrough,
    // wherever a Sales Trend shows, the date it counts through shows too.
    salesThrough: payload?.trend ? payload.salesThrough ?? null : null,
    // a trend period whose sales lag its end names the days still to come ("sales through Oct 14 (3 days to come)"); QA fix 10: only the
    // period that contains the last sales day (a target before or after it has no days to come to name).
    salesDaysToCome: payload?.trend && payload.trend.periods.includes(payload.targetPeriod) && payload.targetPeriod < currentPeriod && periodContainsSalesDay(payload.targetPeriod, g, payload.salesThrough) ? daysToCome(payload.targetPeriod, g, payload.salesThrough) : null,
    inventoryNote: estimated ? `inventory estimated before ${fmtDate(periodStartIso(g, currentPeriod))}` : null,
  };
};

type FilterOptions = { locations: FilterOption[]; categories: FilterOption[]; classes: FilterOption[]; vendors: FilterOption[] };
const SELECTION_FILTERS: { key: Exclude<keyof ViewFilters, 'statuses'>; label: string }[] = [
  { key: 'locations', label: 'Location' },
  { key: 'categories', label: 'Category' },
  { key: 'classes', label: 'Class' },
  { key: 'vendors', label: 'Vendor' },
];
const NONE = { codes: [] as string[], exclude: false };

/** The chips for every active filter, the search text and (only where it applies) Weekly-planned only. */
export const filterChips = (state: ReportViewState, options: FilterOptions, set: (p: Partial<ReportViewState>) => void, weeklyPlannedApplies: boolean): ActiveFilterChip[] => {
  const chips: ActiveFilterChip[] = SELECTION_FILTERS.filter((f) => state.filters[f.key].codes.length > 0).map((f) => {
    const sel = state.filters[f.key];
    const names = sel.codes.map((code) => options[f.key].find((o) => o.code === code)?.label ?? code);
    return {
      key: f.key,
      label: `${f.label}: ${sel.exclude ? 'not ' : ''}${names.length > 2 ? `${names.length} selected` : names.join(', ')}`,
      onRemove: () => set({ filters: { ...state.filters, [f.key]: NONE } }),
    };
  });
  if (state.filters.statuses.length > 0) {
    const names = state.filters.statuses.map((s) => (s === DEADSTOCK_FILTER ? DEADSTOCK_LABEL : STATUS_LABELS[s as keyof typeof STATUS_LABELS] ?? s));
    chips.push({ key: 'statuses', label: `Status: ${names.join(', ')}`, onRemove: () => set({ filters: { ...state.filters, statuses: [] } }) });
  }
  if (weeklyPlannedApplies && state.weeklyPlannedOnly) chips.push({ key: 'weeklyPlannedOnly', label: 'Weekly-planned only', onRemove: () => set({ weeklyPlannedOnly: false }) });
  if (state.search.text) chips.push({ key: 'search', label: `Search: ${state.search.text}`, onRemove: () => set({ search: { ...state.search, text: '' } }) });
  return chips;
};

/** The "Clear all" patch: every filter, the search text and Weekly-planned only. */
export const clearedFilters = (state: ReportViewState): Partial<ReportViewState> => ({
  filters: { locations: NONE, categories: NONE, classes: NONE, vendors: NONE, statuses: [] },
  search: { ...state.search, text: '' },
  weeklyPlannedOnly: false,
});

const COUNTED_KINDS: FlatRow['kind'][] = ['group', 'leaf', 'unclassified'];

/** Round 2: Location views list a class once per store, so their caption counts classes and gives the location-classes in brackets. */
export interface RowCounts {
  shown: number; total: number; locations?: { shown: number; total: number };
  /** item 8: chain class views count the top-level classes; the sub-classes below them are named apart. */
  subClasses?: number;
  /** Class › Location (› Vendor): the location rows on screen (the view's own rows, not the totals tree). */
  locationRows?: number;
}

/** A class row's chain identity at the top of its family: no parent class in the payload (its parent id is not a class row there). */
const topLevelCodes = (rows: ClassRow[]): { top: Set<string>; sub: Set<string> } => {
  const ids = new Set(rows.filter((r) => !r.isUnallocated && r.key !== 'unclassified').map((r) => r.locationClassId));
  const top = new Set<string>();
  const sub = new Set<string>();
  rows.forEach((r) => {
    if (r.isUnallocated || r.key === 'unclassified') return;
    const code = chainCodeOf(r) ?? r.key;
    if (r.parentLocationClassId !== null && r.parentLocationClassId !== undefined && r.parentLocationClassId !== r.locationClassId && ids.has(r.parentLocationClassId)) sub.add(code);
    else top.add(code);
  });
  sub.forEach((c) => { if (top.has(c)) sub.delete(c); });
  return { top, sub };
};

/**
 * Rows shown (group / leaf / unclassified rows of the totals tree, no SKU rows) of the payload's rows (no Unallocated; chain views: classes).
 * Round 2: the caption counts CLASSES (chain classes after the merge). In a Location view, where the tree lists each class once per store,
 * `shown` / `total` are still the classes (one per class code) and `locations` carries the location-class rows.
 */
export const rowCounts = (totalsRows: FlatRow[], payload: ClassReportPayload | null, vendorPayload: VendorRowsPayload | null, view: HierarchyView, viewRows?: readonly FlatRow[]): RowCounts => {
  const tv = totalsViewOf(view);
  const source: { isUnallocated?: boolean }[] = needsVendorRows(tv) && vendorPayload ? vendorPayload.rows.filter((r) => tv !== 'totalByVendor' || r.level === 'vendor') : payload?.rows ?? [];
  // Total / Category by Class of a multi-store client count classes (one chain row each), not location classes.
  // Vendor by Class counts its vendors plus one vendor × class row per vendor and class.
  const chain = !!payload && chainApplies(tv, payload.rows);
  const total = !chain || !payload ? source.filter((r) => !r.isUnallocated).length
    : tv === 'vendorByClass' && vendorPayload ? vendorPayload.rows.filter((r) => r.level === 'vendor').length + chainVendorClassCount(vendorPayload.rows)
      : needsVendorRows(tv) ? source.filter((r) => !r.isUnallocated).length
        : chainClassCount(payload.rows) + payload.rows.filter((r) => r.key === 'unclassified').length;
  const counted = totalsRows.filter((f) => COUNTED_KINDS.includes(f.kind) && !(isVendorRow(f.row) && f.row.level === 'sku'));
  if (tv === 'locationCategoryClass' && payload) {
    const classKeys = (rows: { isUnallocated?: boolean; key: string; classCode?: string | null }[]): number =>
      new Set(rows.filter((r) => !r.isUnallocated).map((r) => (r.key === 'unclassified' ? r.key : chainCodeOf(r as never) ?? r.key))).size;
    return {
      shown: classKeys(counted.map((f) => f.row)),
      total: classKeys(payload.rows),
      locations: { shown: counted.length, total: payload.rows.filter((r) => !r.isUnallocated).length },
    };
  }
  const locationRows = isLocationAllocationView(view) && viewRows ? { locationRows: viewRows.filter((f) => !isVendorRow(f.row) && f.row.labelAs === 'location').length } : {};
  // item 8: a chain class view counts the classes at the top of their families (3027: 10, not the 41 codes of every
  // parent and sub-class), shown and total alike; the sub-classes are named apart.
  if (chain && payload && !needsVendorRows(tv) && tv !== 'vendorByClass') {
    const { top, sub } = topLevelCodes(payload.rows);
    const ids = new Set(counted.map((f) => f.id));
    const shownTop = counted.filter((f) => { const i = f.id.lastIndexOf('/'); return i < 0 || !ids.has(f.id.slice(0, i)); }).length;
    return { total: top.size + payload.rows.filter((r) => r.key === 'unclassified').length, shown: shownTop, ...(sub.size > 0 ? { subClasses: sub.size } : {}), ...locationRows };
  }
  return { total, shown: counted.length, ...locationRows };
};

/** "(83 of 87 location-classes)" beside a Location view's class count; nothing elsewhere. */
export const locationClassNote = (c: RowCounts): string | null => {
  if (c.locations) return `(${c.locations.shown === c.locations.total ? '' : `${c.locations.shown.toLocaleString('en-US')} of `}${c.locations.total.toLocaleString('en-US')} location-classes)`;
  const parts = [
    ...(c.subClasses ? [`+${c.subClasses.toLocaleString('en-US')} sub-${c.subClasses === 1 ? 'class' : 'classes'}`] : []),
    ...(c.locationRows ? [`${c.locationRows.toLocaleString('en-US')} location ${c.locationRows === 1 ? 'row' : 'rows'} shown`] : []),
  ];
  return parts.length ? `(${parts.join(' · ')})` : null;
};

// The shared page shell of the three weekly planning reports (Plan 4 ): header (saved views,
// share, export, How it works, Table / Dashboard; no favourite), the two control rows (WeeklyPlanningControlBar), active
// filter chips and the row count, dismissible notices, KPIs, then the table (or the report's own) or
// the dashboard. Data and view state come from useWeeklyPlanningReport; the outer component mounts
// the inner one with `key={String(clientId)}` so a client switch starts from a clean slate.
// Columns: the freshness group is dropped without receiving data, or when the config offers the
// Freshness switch and it is off; then `config.columnsFilter` narrows the ids and
// `columnLabelOverrides` relabel them. The table, the Columns menu, How it works and the CSV all use
// that one list. The attention list's `onSelect` returns to the table, expands the item's ancestors
// (the totals rows whose id prefixes the item's FlatRow id) and scrolls to `[data-row-id]` inside
// this page (one attempt). the label column takes the view's first-column name and description,
// placeholders call `loadChildRows` (products in a product view, SKUs otherwise), and with % of Total on
// the % of Total button's tooltip says what each row divides by (`parentRelation`). A `?sv=` saved view
// is resolved by the SavedViewsMenu (it owns the list) once the model is `viewReady`, so the hook's own
// initialisation (reset to the real defaults, the Monthly seed) can never undo it. the resolved
// benchmark label ends the data-source line, the benchmark peer-value / index columns carry the benchmark
// month in their label and tooltip (`withBenchmarkMonth`), and How it works gets the Benchmarks section
// for the client's `benchmarkLevel`. Derived values are memoised on their inputs; the config's identity is one
// of them (configs are module-level constants, see reportPageConfig.ts).

export interface WeeklyPlanningReportPageProps {
  config: ReportPageConfig;
  clientId: string | number;
  epochMonth: number | null;
  setEpochMonth?: (m: number) => void;
}

const PAGE_CSS = 'flex flex-col gap-4 p-6 bg-[#FAFAFC] min-h-screen text-[#09090B] font-sans antialiased relative';
/** A KPI with every string in the grain's wording (`unitize`). */
export const unitizeKpi = (k: KpiItem, grain: Grain | null | undefined): KpiItem => ({
  ...k,
  label: unitize(k.label, grain),
  ...(k.sub !== undefined ? { sub: unitize(k.sub, grain) } : {}),
  ...(k.tooltip !== undefined ? { tooltip: unitize(k.tooltip, grain) } : {}),
  ...(k.note !== undefined ? { note: unitize(k.note, grain) } : {}),
  ...(k.benchmark ? { benchmark: { ...k.benchmark, text: unitize(k.benchmark.text, grain), ...(k.benchmark.tooltip !== undefined ? { tooltip: unitize(k.benchmark.tooltip, grain) } : {}) } } : {}),
});
const CARD_CSS = 'bg-white border border-[#E4E4E7] rounded-2xl shadow-sm overflow-hidden';

const WeeklyPlanningReportPageInner: React.FC<WeeklyPlanningReportPageProps> = ({ config, clientId, epochMonth, setEpochMonth }) => {
  const model = useWeeklyPlanningReport({ reportKey: config.reportKey, clientId, epochMonth, setEpochMonth });
  const { view, capabilities, payload, vendorPayload, ctx, status, columnState, totalsRows, flatRows, filterOptions, currentPeriod } = model;
  const { state, set } = view;
  // built-in view templates (e.g. Pet stores) for dev-tools users only; an admin shares one with Save view → Shared.
  const auth = AuthProvider.useAuth() as { checkPermission?: (p: string) => boolean } | null;
  const isAdmin = Boolean(auth?.checkPermission?.('dev-tools'));
  const templates = isAdmin
    ? (VIEW_TEMPLATES[config.reportKey] ?? []).map((t) => ({ id: t.id, name: t.name, description: unitize(t.description, state.grain), onApply: () => set(t.build(state, model.tier)) }))
    : [];
  const hierarchy = HIERARCHY_BY_KEY[state.view];
  const [display, setDisplay] = useState<ReportDisplay>('table');
  const [howOpen, setHowOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [manualLink, setManualLink] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});
  const [scrollTo, setScrollTo] = useState<string | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);
  useEffect(() => setDismissed({}), [payload]);

  // ---- columns ---------------------------------------------------------------------------------
  // the freshness columns follow the receiving capability only; the Freshness switch shows / hides them (setFreshnessShown).
  const freshnessOn = capabilities?.hasReceiving !== false;
  const allIds = model.columns.map((c) => c.id).filter((id) => freshnessOn || !FRESHNESS_COLUMN_IDS.includes(id));
  // Wendi (2026-10-22), item 10: non-admins get the client column list (plus any expert column a saved / shared view shows or pins).
  const baseIds = isAdmin ? allIds : clientColumnIds(config.reportKey, allIds, state);
  const ids = config.columnsFilter ? config.columnsFilter(model, baseIds) : baseIds;
  // Item 12: the first-visit hint, while no saved view is applied and the columns are the report's defaults.
  const columnsUntouched = useMemo(() => {
    const d = defaultViewState(config.reportKey, state.grain).columns;
    return JSON.stringify([state.columns.order, state.columns.visible, state.columns.pinned]) === JSON.stringify([d.order, d.visible, d.pinned]);
  }, [config.reportKey, state.grain, state.columns]);
  const hint = view.savedViewId === null && columnsUntouched && !config.hideColumnControls ? (templates.length > 0 ? FIRST_VISIT_HINT : FIRST_VISIT_HINT_NO_TEMPLATES) : null;
  const idsKey = ids.join('|');
  const overrides = config.columnLabelOverrides;
  const benchmarkLabel = ctx?.benchmarks?.label ?? null;
  const benchMonth = benchmarkMonth(ctx?.benchmarks);
  const otbPeriods = payload?.otbPeriods;
  const planWeights = useMemo(() => !!ctx && model.trendPeriod && usesPlanWeights(totalRows(totalsRows).map((f) => f.row), ctx.targetPeriod, ctx.trendMethod), [ctx, model.trendPeriod, totalsRows]);
  const tableColumns = useMemo<MetricDefinition[]>(() => {
    const keep = new Set(idsKey.split('|'));
    return model.columns
      .filter((c) => keep.has(c.id))
      .map((c) => withGrainUnits(((): MetricDefinition => {
        if (overrides?.[c.id]) return { ...c, label: overrides[c.id], shortLabel: overrides[c.id] };
        // Buyer language: "Sales WTD" / "Sales MTD" in a period still in progress; the vs columns follow the Compare setting.
        const contextual = withContextLabels(c, { compare: state.compare, grain: state.grain, trendPeriod: model.trendPeriod, planWeights });
        if (contextual !== c) return contextual;
        // / the optional per-period OTB columns are named after the span's periods ("OTB wk of Oct 18" / "OTB Nov").
        const slot = otbPeriodSlot(c.id);
        if (slot !== null) { const label = otbPeriodLabel(slot, otbPeriods, state.grain); return { ...c, label, shortLabel: label }; }
        // The first column names the view's levels and explains them (hierarchyViews).
        if (c.id === 'label') return { ...c, label: hierarchy.firstColumn, shortLabel: hierarchy.firstColumn, description: hierarchy.description };
        return withBenchmarkBasis(withBenchmarkMonth(c, benchMonth), state.basis);
      })(), state.grain));
  }, [model.columns, idsKey, overrides, hierarchy, benchMonth, otbPeriods, state.compare, state.grain, state.basis, model.trendPeriod, planWeights]);
  const visibleColumns = useMemo(() => orderedVisibleColumns(tableColumns, columnState), [tableColumns, columnState]);

  // ---- derived (memoised on their inputs) ------------------------------------------------------
  const dataSource = useMemo(() => ({ ...dataSourceOf(payload, capabilities, state.grain, currentPeriod, status === 'loading'), benchmarkLabel }), [payload, capabilities, state.grain, currentPeriod, benchmarkLabel, status]);
  const weeklyPlannedApplies = weeklyPlannedAvailable(model);
  const chips = useMemo(() => filterChips(state, filterOptions, set, weeklyPlannedApplies), [state, filterOptions, set, weeklyPlannedApplies]);
  const counts = useMemo(() => rowCounts(totalsRows, payload, vendorPayload, state.view, flatRows), [totalsRows, payload, vendorPayload, state.view, flatRows]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- config.kpis(model) reads the whole model; its parts are listed, the model object itself is rebuilt every render
  // Grain-correct wording (Wendi, 2026-10-22): every KPI string reads BOW / WOS / weeks in Weekly and BOM / MOS / months in Monthly.
  const shownGrain = ctx?.grain ?? state.grain; // the grain of the data on screen (a monthly payload under a weekly state during a switch)
  const kpis = useMemo(() => (status === 'ready' ? config.kpis(model).map((k) => unitizeKpi(k, shownGrain)) : []), [config, status, totalsRows, flatRows, ctx, payload, vendorPayload, state, shownGrain]);

  // ---- header actions --------------------------------------------------------------------------
  const onShare = useCallback(async () => {
    const link = view.shareUrl();
    if (!(await copyShareUrl(link))) {
      setManualLink(link);
      return;
    }
    setManualLink(null);
    setCopied(true);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
  }, [view]);
  // Export CSV opens a dialog (ExportCsvModal): rows as shown (with the rows opened, and a Level column) or top level only;
  // columns shown or all of the report's (Plan & OTB: lines as shown or all, with LY).
  const [exportOpen, setExportOpen] = useState(false);
  const onExport = () => setExportOpen(true);
  const runExport = ({ rows, columns }: ExportChoice) => {
    const includeExpanded = rows === 'shown';
    if (config.exportCsv) config.exportCsv(model, { includeExpanded, allLines: columns === 'all' });
    else if (ctx) exportReportCsv(config.reportKey, state, flatRows, columns === 'all' ? orderedVisibleColumns(tableColumns, { ...columnState, visible: {} }) : visibleColumns, ctx, overrides, { includeExpanded });
  };
  const openHow = (e: React.MouseEvent<HTMLButtonElement>) => {
    opener.current = e.currentTarget; // not document.activeElement: Safari does not focus buttons on click
    setHowOpen(true);
  };
  const closeHow = () => {
    setHowOpen(false);
    if (opener.current?.isConnected) opener.current.focus();
  };

  // ---- attention → table row -------------------------------------------------------------------
  const onSelect = (item: AttentionItem) => {
    setDisplay('table');
    totalsRows.filter((f) => item.key.startsWith(`${f.id}/`) && !model.expanded[f.expandKey ?? f.row.key]).forEach((f) => model.toggleGroup(f.expandKey ?? f.row.key));
    setScrollTo(item.key);
  };
  useEffect(() => {
    if (!scrollTo || display !== 'table') return;
    setScrollTo(null); // one attempt: a row that is not there (filtered, another table) is not chased
    const rows = pageRef.current ? Array.from(pageRef.current.querySelectorAll<HTMLElement>('[data-row-id]')) : [];
    rows.find((n) => n.getAttribute('data-row-id') === scrollTo)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [scrollTo, display, flatRows]);

  // ---- body ------------------------------------------------------------------------------------
  const cols = visibleColumns.length + 1;
  const notices = model.notices.map((n) => unitize(n, shownGrain)).filter((n) => !dismissed[n]);
  let body: React.ReactNode;
  if (status === 'loading') body = <div className={CARD_CSS}><TableStates state="loading" columns={cols} /></div>;
  else if (status === 'error') body = <div className={CARD_CSS}><TableStates state="error" message={model.error?.message} onRetry={model.retry} columns={cols} /></div>;
  else if (status === 'unsupported') {
    body = (
      <div className={`${CARD_CSS} py-14 text-center`}>
        <p className="text-[14px] font-semibold text-[#344054]">This report is not available for this client.</p>
      </div>
    );
  } else if (display === 'dashboard') {
    body = <DashboardView cards={config.dashboardCards(model)} attention={model.attention} onSelect={onSelect} grain={state.grain} />;
  } else if (config.renderTable) {
    body = config.renderTable(model);
  } else if (flatRows.length === 0 || !ctx) {
    body = <div className={CARD_CSS}><TableStates state="empty" columns={cols} /></div>;
  } else {
    const renderRowDetail = config.renderRowDetail;
    body = (
      <ReportTable
        flatRows={flatRows}
        columns={tableColumns}
        columnState={columnState}
        onColumnState={model.onColumnState}
        sort={state.sort}
        onSort={(sort) => set({ sort })}
        ctx={ctx}
        onToggleGroup={model.toggleGroup}
        onExpandSkus={model.loadChildRows}
        renderRowDetail={renderRowDetail ? (fr) => renderRowDetail(model, fr) : undefined}
      />
    );
  }

  return (
    <div ref={pageRef} className={PAGE_CSS}>
      <ReportPageHeader
        icon={config.icon}
        title={config.title}
        subtitle={unitize(config.subtitle, shownGrain)}
        dataSource={dataSource}
        onHowItWorks={openHow}
        onSaveView={() => setSaveOpen(true)}
        onShare={onShare}
        onExport={onExport}
        exportDisabled={status !== 'ready'}
        hint={hint}
        displayToggle={<DisplayToggle display={display} onDisplay={setDisplay} />}
        savedViewsMenu={
          <SavedViewsMenu
            reportKey={config.reportKey}
            clientId={clientId}
            userId={model.userId}
            onApply={view.applySaved}
            pendingViewId={model.viewReady ? view.pendingSavedViewId : null}
            onPendingView={view.resolveSavedView}
            onDeleted={view.clearSavedView}
            templates={templates}
            saveModal={{ open: saveOpen, onClose: () => setSaveOpen(false), payload: () => toSavedViewPayload(state) }}
          />
        }
      />
      <ExportCsvModal open={exportOpen} onClose={() => setExportOpen(false)} onExport={runExport} kind={config.exportCsv ? 'lines' : 'columns'} />
      {manualLink !== null && (
        <div className="relative h-0 self-end">
          <ShareLinkPopover url={manualLink} onClose={() => setManualLink(null)} className="right-0 -top-2" />
        </div>
      )}
      {copied && <p role="status" className="text-[12px] font-medium text-[#027A48] -mt-2">Link copied</p>}
      <WeeklyPlanningControlBar model={model} config={config} columns={tableColumns} />
      <NoticeList notices={notices} onDismiss={(n) => setDismissed((d) => ({ ...d, [n]: true }))} />
      <ActiveFilterChips chips={chips} onClearAll={() => set(clearedFilters(state))} />
      {status === 'ready' && <CountCaption shown={counts.shown} total={counts.total} noun={totalsNoun(state.view, counts.total)} note={locationClassNote(counts)} />}
      {status === 'ready' && <KpiRow items={kpis} />}
      {body}
      <HowItWorksPanel
        open={howOpen}
        onClose={closeHow}
        title={`How ${config.title} works`}
        intro={config.howItWorksIntro}
        dataSource={dataSource}
        columns={visibleColumns}
        items={howOpen && config.howItWorksColumns ? config.howItWorksColumns(model) : undefined}
        grain={dataSource.grain}
        tier={model.tier}
        trendMethods={trendMethodAvailable(model)}
        benchmarkLevel={model.benchmarkLevel}
        benchmarkLabel={benchmarkLabel}
        allocation={config.reportKey === 'weekly_action'}
      />
    </div>
  );
};

/** Mounts the page per client (`key={String(clientId)}`), so nothing of one client's report leaks into the next. */
export const WeeklyPlanningReportPage: React.FC<WeeklyPlanningReportPageProps> = (props) => <WeeklyPlanningReportPageInner key={String(props.clientId)} {...props} />;
